import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { rdProjectsApi, roleRatesApi } from '../services/rdProjectsApi';
import { divisionsApi, objectivesApi, programmesApi, kpisApi, clustersApi, researchThemesApi, motivationTopicsApi, grantsApi, risksApi } from '../services/api';
import {
  FlaskConical, Plus, Edit3, Trash2, Filter, Target, GitBranch, DollarSign, Calendar,
  User, TrendingUp, ChevronDown, ChevronUp, ChevronRight, CheckCircle2, Circle, Clock,
  AlertTriangle, FileSpreadsheet, BarChart3, Layers, Beaker, Briefcase, FileCheck,
  Upload, FileText, Eye, Download, Send, RotateCcw, XCircle, Search, ArrowRight,
  Microscope, Building2, ScrollText, Shield, BookOpen, Users, Zap, Globe,
  ClipboardList, Award, Flag, ExternalLink, Info, AlertCircle, Hash, X, Lock, GaugeCircle,
  Link2
} from 'lucide-react';
import LoadingOverlay from '../components/LoadingOverlay';

// ══════════════════════════════════════════════════════
// CONSTANTS & HELPERS
// ══════════════════════════════════════════════════════

const fmtR = (v) => v != null ? (v >= 1000000 ? `R${(v/1000000).toFixed(1)}M` : v >= 1000 ? `R${(v/1000).toFixed(0)}K` : `R${v}`) : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const isoDate = (d) => d ? new Date(d).toISOString().split('T')[0] : '';
const pc = (p) => p >= 70 ? '#10b981' : p >= 40 ? '#f59e0b' : '#ef4444';
const wordCount = (text) => (text || '').trim().split(/\s+/).filter(Boolean).length;

// Extract error message from axios error
const getErrorMsg = (e) => {
  if (e?.response?.data) {
    const d = e.response.data;
    if (typeof d === 'string') return d;
    if (d.title) return d.title;
    if (d.errors) return Object.values(d.errors).flat().join(', ');
    if (d.message) return d.message;
    return `Server returned ${e.response.status}`;
  }
  if (e?.response?.status) return `Server error ${e.response.status}`;
  if (e?.message === 'Network Error') return 'Network Error — check if the backend is running and CORS is configured for localhost:3000';
  if (e?.message) return e.message;
  return 'An unexpected error occurred';
};

const STATUSES = ['Draft','Submitted','UnderInternalReview','RevisionsRequired','InternalApproved','UnderExternalReview','Declined','Active','YearPendingRelease','Suspended','Completed'];
const STATUS_LABELS = { Draft:'Draft', Submitted:'Submitted', UnderInternalReview:'Under Internal Review', RevisionsRequired:'Revisions Required', InternalApproved:'Internal Approved', UnderExternalReview:'Under External Review', Declined:'Declined', Active:'Active', Approved:'Approved', YearPendingRelease:'Year Pending Release', Suspended:'Suspended', Completed:'Completed', InProgress:'In Progress', OnHold:'On Hold', Cancelled:'Cancelled' };
const STATUS_COLORS = { Draft:'bg-slate-100 text-slate-600', Submitted:'bg-blue-100 text-blue-700', UnderInternalReview:'bg-indigo-100 text-indigo-700', RevisionsRequired:'bg-amber-100 text-amber-700', InternalApproved:'bg-teal-100 text-teal-700', UnderExternalReview:'bg-violet-100 text-violet-700', Declined:'bg-red-100 text-red-700', Active:'bg-emerald-100 text-emerald-700', Approved:'bg-emerald-100 text-emerald-700', YearPendingRelease:'bg-orange-100 text-orange-700', Suspended:'bg-gray-100 text-gray-600', Completed:'bg-emerald-100 text-emerald-700', InProgress:'bg-blue-100 text-blue-700', OnHold:'bg-amber-100 text-amber-700', Cancelled:'bg-red-100 text-red-700' };

const TYPES = ['Research', 'Commercial', 'Contract'];

// V3.5 — Funding sources are now a fixed enumerated list (was free-text) so
// that the filter and Group By on the project list work cleanly. If you need
// to extend the list, just add a value below — no DB or backend change needed
// since the column is still NVARCHAR.
const FUNDING_SOURCES = [
  'State Grant (DSI/NRF)',
  'Commercial Income',
  'Contract Research',
  'International Grant',
  'Internal Funding',
  'Industry Partner',
  'Other'
];
const TYPE_ICONS = { Research: Beaker, Commercial: Briefcase, Contract: ScrollText };
const TYPE_COLORS = { Research: 'bg-purple-100 text-purple-700', Commercial: 'bg-cyan-100 text-cyan-700', Contract: 'bg-indigo-100 text-indigo-700' };
const TYPE_RING = { Research: 'ring-purple-400', Commercial: 'ring-cyan-400', Contract: 'ring-indigo-400' };

const TRL_LEVELS = [1,2,3,4,5,6,7,8,9];
const TRL_NAMES = { 1:'Basic Principles Observed', 2:'Technology Concept Formulated', 3:'Experimental Proof of Concept', 4:'Technology Validated in Lab', 5:'Technology Validated in Relevant Environment', 6:'Technology Demonstrated', 7:'System Prototype Demonstrated', 8:'System Complete and Qualified', 9:'Actual System Proven' };
const trlColor = (t) => t <= 3 ? 'bg-violet-100 text-violet-700 border-violet-200' : t <= 6 ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200';

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];
// ── Phase B: Running cost categories. 'Personnel' removed — labour now lives
// in RdProjectResource (Phase A). 'Other' unlocks a free-text CustomCategory.
const BUDGET_CATEGORIES = ['Equipment & Instrumentation', 'Consumables & Materials', 'Travel & Subsistence', 'Subcontracting / Consulting', 'Publication & Dissemination', 'Overheads', 'Contingency', 'Other'];
const FUNDING_CATEGORIES = ['New Research Initiative', 'Continuation of Existing Project', 'Pilot / Feasibility Study', 'Collaborative Research', 'Technology Demonstration'];
// ── V4.1: Project risk assessment = SELECTION from the risk register (tier: Project).
// Selecting a risk shows its register attributes (inherent score etc., read-only)
// and its controls; the user ticks the controls that APPLY IN THIS PROJECT'S
// CONTEXT, and the project-level residual is calculated from the selected
// controls (combined effect = strongest mitigation per dimension). No controls
// selected = the risk is uncontrolled in this context, so residual = inherent.
// Stored rows keep the legacy shape plus riskId + controlIds for round-tripping.
const scaleWord = (n) => (n <= 2 ? 'Low' : n === 3 ? 'Medium' : 'High');
const scoreBadge = (rs) => `px-1.5 py-0.5 rounded-full text-[9px] font-bold ${rs >= 17 ? 'bg-red-800 text-white' : rs >= 13 ? 'bg-red-500 text-white' : rs >= 9 ? 'bg-orange-400 text-white' : rs >= 5 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`;
function contextResidual(risk, controlIds) {
  const applied = (risk.controls || []).filter(c => (controlIds || []).includes(c.id) && c.residualLikelihood != null && c.residualImpact != null);
  if (applied.length === 0) return { l: risk.inherentLikelihood, i: risk.inherentImpact, calculated: false };
  return { l: Math.min(...applied.map(c => c.residualLikelihood)), i: Math.min(...applied.map(c => c.residualImpact)), calculated: true };
}
function ProjectRiskPicker({ rows, onChange }) {
  const [registerRisks, setRegisterRisks] = useState(null);
  useEffect(() => { let live = true;
    risksApi.getAll().then(({ data }) => { if (live) setRegisterRisks((data || []).filter(r => r.tier === 'Project' && r.status !== 'Closed')); })
      .catch(() => { if (live) setRegisterRisks([]); });
    return () => { live = false; };
  }, []);
  const rowFor = (riskId) => (rows || []).find(x => x.riskId === riskId);
  const legacyRows = (rows || []).filter(r => !r.riskId && r.description);

  const buildRow = (r, controlIds) => {
    const res = contextResidual(r, controlIds);
    const applied = (r.controls || []).filter(c => controlIds.includes(c.id));
    return {
      riskId: r.id,
      controlIds,
      description: `${r.reference || ''} ${r.title}`.trim(),
      likelihood: scaleWord(res.l),
      impact: scaleWord(res.i),
      mitigation: applied.length ? applied.map(c => c.title).join('; ') : 'No controls applied in this project context',
    };
  };
  const toggleRisk = (r) => {
    if (rowFor(r.id)) { onChange((rows || []).filter(x => x.riskId !== r.id)); return; }
    // Default: all controls with lifecycle values pre-selected — user unticks what doesn't apply here
    const defaults = (r.controls || []).filter(c => c.status !== 'Inactive' && c.residualLikelihood != null).map(c => c.id);
    onChange([...(rows || []), buildRow(r, defaults)]);
  };
  const toggleControl = (r, controlId) => {
    const row = rowFor(r.id); if (!row) return;
    const ids = (row.controlIds || []).includes(controlId)
      ? (row.controlIds || []).filter(x => x !== controlId)
      : [...(row.controlIds || []), controlId];
    onChange((rows || []).map(x => (x.riskId === r.id ? buildRow(r, ids) : x)));
  };

  if (registerRisks === null) return <p className="text-xs text-slate-400">Loading project risk register…</p>;
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Select applicable risks from the <span className="font-semibold">risk register</span> (Project tier), then tick the controls operating in this project's context — the project-level residual is calculated from your selection. Risk attributes and controls are configured in the Risk module.</p>
      {registerRisks.length === 0 && <p className="text-xs text-amber-600">No Project-tier risks exist in the register yet — register them in the Risk module first.</p>}
      <div className="max-h-96 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
        {registerRisks.map(r => {
          const row = rowFor(r.id);
          const selected = !!row;
          const inh = r.inherentLikelihood * r.inherentImpact;
          const res = selected ? contextResidual(r, row.controlIds || []) : null;
          const resScore = res ? res.l * res.i : null;
          const lifecycleControls = (r.controls || []).filter(c => c.status !== 'Inactive');
          return (
            <div key={r.id} className={selected ? 'bg-red-50/40' : ''}>
              <label className={`flex items-center gap-3 px-3 py-2 text-xs cursor-pointer ${selected ? '' : 'hover:bg-slate-50'}`}>
                <input type="checkbox" checked={selected} onChange={() => toggleRisk(r)} className="accent-[#c8102e]" />
                <span className="font-mono text-[9px] text-[#c8102e] w-16 shrink-0">{r.reference}</span>
                <span className="flex-1 text-slate-700">{r.title}</span>
                <span className="text-[10px] text-slate-400">Inherent</span>
                <span className={scoreBadge(inh)}>{inh}</span>
              </label>
              {selected && (
                <div className="mx-3 mb-2 rounded-lg border border-slate-200 bg-white p-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-500">
                    <span>Category: <span className="font-semibold text-slate-700">{r.category}</span></span>
                    <span>Owner: <span className="font-semibold text-slate-700">{r.riskOwner || '—'}</span></span>
                    <span>Inherent: <span className="font-semibold text-slate-700">L{r.inherentLikelihood} × I{r.inherentImpact}</span> <span className={scoreBadge(inh)}>{inh}</span></span>
                    {r.appetiteThreshold && <span>Appetite: <span className="font-semibold text-slate-700">{r.appetiteThreshold}</span></span>}
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wide mb-1">Controls applied in this project's context</p>
                    {lifecycleControls.length === 0 && <p className="text-[10px] text-amber-600">This risk has no controls in the register — residual stays at inherent until controls are configured in the Risk module.</p>}
                    {lifecycleControls.map(c => (
                      <label key={c.id} className="flex items-center gap-2 py-0.5 text-[11px] text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={(row.controlIds || []).includes(c.id)} onChange={() => toggleControl(r, c.id)} className="accent-[#c8102e]" disabled={c.residualLikelihood == null} />
                        <span className="flex-1">{c.title} <span className="text-slate-400">· {c.type}</span></span>
                        {c.residualLikelihood != null
                          ? <span className="text-[9px] text-slate-500">→ L{c.residualLikelihood} × I{c.residualImpact}</span>
                          : <span className="text-[9px] text-slate-400 italic">no lifecycle values</span>}
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 pt-1 border-t border-slate-100 text-[11px]">
                    <span className="text-slate-500">Project-context residual:</span>
                    <span className="font-semibold text-slate-800">L{res.l} × I{res.i}</span>
                    <span className={scoreBadge(resScore)}>{resScore}</span>
                    <span className="text-[9px] text-slate-400">{res.calculated ? `calculated from ${(row.controlIds || []).length} selected control(s)` : 'no applicable controls — equals inherent'}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {legacyRows.length > 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 p-2">
          <p className="text-[10px] text-slate-400 mb-1">Legacy free-text risks (from before register linking) — remove and reselect from the register:</p>
          {legacyRows.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-600 py-0.5">
              <span className="flex-1 truncate">{r.description}</span>
              <button type="button" className="text-red-500 text-[10px] font-semibold" onClick={() => onChange((rows || []).filter(x => x !== r))}>Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const LIKELIHOOD_OPTIONS = ['Low', 'Medium', 'High'];
const OUTPUT_TYPES = ['Peer-Reviewed Publication', 'Patent / IP Filing', 'Research Dataset', 'Prototype / Physical Deliverable', 'Technology Transfer / Licence', 'Student / Postdoc Trained', 'Report / Technical Note', 'Industry Demonstration / Pilot', 'Policy Brief / Advisory', 'Other'];
const GOVT_PRIORITIES = ['Economic transformation & industrialisation', 'Job creation & skills development', 'Beneficiation of mineral resources', 'Environmental sustainability & green economy', 'Science, Technology & Innovation (STI) strategy', 'BBBEE & transformation imperatives', 'Safety, health and mine rehabilitation'];
const CONFIDENTIALITY = ['Public', 'Internal', 'Confidential', 'Strictly Confidential'];

const RESEARCH_STAGES = [
  { key: 'Draft', label: 'Draft', icon: Edit3 },
  { key: 'Submitted', label: 'Submitted', icon: Send },
  { key: 'InternalReview', label: 'Internal Review', icon: Users },
  { key: 'ExternalReview', label: 'External Review', icon: Globe },
  { key: 'Active', label: 'Active Project', icon: Zap },
  { key: 'Completed', label: 'Completed', icon: Award },
];
const COMMERCIAL_STAGES = [
  { key: 'Draft', label: 'Draft', icon: Edit3 },
  { key: 'Submitted', label: 'Submitted', icon: Send },
  { key: 'DMReview', label: 'DM Review', icon: Users },
  { key: 'AdditionalReview', label: 'Additional Review', icon: Shield },
  { key: 'Active', label: 'Active', icon: Zap },
  { key: 'Completed', label: 'Completed', icon: Award },
];

function getStageIndex(status, type) {
  const map = type === 'Commercial' ? {
    Draft: 0, Submitted: 1, UnderInternalReview: 2, UnderDMReview: 2, RevisionsRequired: 1,
    UnderExternalReview: 3, UnderAdditionalReview: 3, InternalApproved: 3, Approved: 4, Active: 4, Completed: 5, Declined: -1, Suspended: 4, InProgress: 4
  } : {
    Draft: 0, Submitted: 1, UnderInternalReview: 2, RevisionsRequired: 1,
    InternalApproved: 3, UnderExternalReview: 3, Approved: 4, Active: 4, Completed: 5, Declined: -1, Suspended: 4, InProgress: 4
  };
  return map[status] ?? 0;
}

const input = 'w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#c8102e]/20 focus:border-[#c8102e] bg-white';
const btnP = 'px-4 py-2 bg-[#c8102e] text-white rounded-xl text-xs font-semibold hover:bg-[#a00d24] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed';
const btnS = 'px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-200 transition-colors inline-flex items-center gap-1.5';
const btnG = 'px-4 py-2 bg-emerald-500 text-white rounded-xl text-xs font-semibold hover:bg-emerald-600 transition-colors inline-flex items-center gap-1.5';
const btnD = 'px-4 py-2 bg-red-50 text-red-600 rounded-xl text-xs font-semibold hover:bg-red-100 transition-colors inline-flex items-center gap-1.5';
const label = 'block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1';

// ══════════════════════════════════════════════════════
// DRAFT PERSISTENCE — sessionStorage
// ══════════════════════════════════════════════════════

const DRAFT_KEY = 'silogate_rd_project_draft';
const saveDraft = (form) => { try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch {} };
const loadDraft = () => { try { const d = sessionStorage.getItem(DRAFT_KEY); return d ? JSON.parse(d) : null; } catch { return null; } };
const clearDraft = () => { try { sessionStorage.removeItem(DRAFT_KEY); } catch {} };

// ══════════════════════════════════════════════════════
// REUSABLE SUB-COMPONENTS
// ══════════════════════════════════════════════════════

function Toast({ message, type = 'error', onClose }) {
  useEffect(() => { const t = setTimeout(onClose, type === 'error' ? 8000 : 4000); return () => clearTimeout(t); }, [onClose, type]);
  const bg = type === 'error' ? 'bg-red-600' : type === 'success' ? 'bg-emerald-600' : 'bg-amber-500';
  return (
    <div className={`fixed bottom-6 right-6 z-[100] ${bg} text-white px-5 py-3 rounded-xl shadow-2xl max-w-md flex items-start gap-3 animate-slide-in`}>
      {type === 'error' ? <AlertCircle size={18} className="flex-shrink-0 mt-0.5" /> : type === 'success' ? <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" /> : <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />}
      <p className="text-sm flex-1">{message}</p>
      <button onClick={onClose} className="flex-shrink-0 hover:opacity-70"><X size={16} /></button>
    </div>
  );
}

function Modal({ open, onClose, title, wide, extraWide, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[5vh]" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className={`relative bg-white rounded-2xl shadow-2xl ${extraWide ? 'max-w-6xl' : wide ? 'max-w-4xl' : 'max-w-lg'} w-full max-h-[90vh] overflow-y-auto animate-slide-in`} onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white rounded-t-2xl border-b px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-bold text-lg text-slate-800">{title}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 text-lg">×</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function MultiSelect({ label: lbl, options, value = [], onChange, renderOption, placeholder }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <label className={label}>{lbl}</label>
      <button type="button" onClick={() => setOpen(!open)} className={input + ' text-left flex items-center justify-between'}>
        <span className="truncate">{value.length ? `${value.length} selected` : placeholder || '— Select —'}</span>
        <ChevronDown size={14} className="text-slate-400" />
      </button>
      {open && <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto p-2">
        {options.map(opt => { const id = opt.id || opt; const checked = value.includes(id); return (
          <label key={id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-slate-50 ${checked ? 'bg-[#c8102e]/5' : ''}`}>
            <input type="checkbox" checked={checked} onChange={() => onChange(checked ? value.filter(v => v !== id) : [...value, id])} className="rounded" />
            <span className="text-xs text-slate-700">{renderOption ? renderOption(opt) : opt.title || opt.name || opt}</span>
          </label>); })}
        {options.length === 0 && <p className="text-xs text-slate-400 p-2">None available</p>}
      </div>}
      {open && <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />}
    </div>
  );
}

function FormSection({ number, title, icon: Icon, children, complete, required, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className={`rd-motivation-card ${open ? 'is-open' : ''} ${complete ? 'is-complete' : ''} ${required ? 'is-required' : ''}`}>
      <button type="button" onClick={() => setOpen(!open)} className="rd-motivation-card-head">
        <span className="rd-motivation-number">{number}</span>
        {Icon && <span className="rd-motivation-icon"><Icon size={18} /></span>}
        <span className="rd-motivation-title">{title}</span>
        {required && !complete && <span className="rd-required-chip">Required</span>}
        <span className="rd-motivation-chevron">{open ? <ChevronUp size={26} /> : <ChevronDown size={26} />}</span>
      </button>
      {open && <div className="rd-motivation-body">{children}</div>}
    </div>
  );
}

function StatusTimeline({ status, projectType }) {
  const stages = projectType === 'Commercial' ? COMMERCIAL_STAGES : RESEARCH_STAGES;
  const currentIdx = getStageIndex(status, projectType);
  const isDeclined = status === 'Declined';
  return (
    <div className="rd-stage-track" data-stage={Math.max(currentIdx,0)}>
      {stages.map((stage, idx) => {
        const Icon = stage.icon;
        const isPast = idx < currentIdx;
        const isCurrent = idx === currentIdx && !isDeclined;
        return (
          <React.Fragment key={stage.key}>
            <div className={`rd-stage-node ${isPast ? 'is-past' : ''} ${isCurrent ? 'is-current' : ''}`}>
              <span className="rd-stage-dot">{isPast ? <CheckCircle2 size={17} /> : <Icon size={18} />}</span>
              <span className="rd-stage-label">{stage.label}</span>
            </div>
            {idx < stages.length - 1 && <span className={`rd-stage-line ${idx < currentIdx ? 'is-filled' : ''}`} />}
          </React.Fragment>
        );
      })}
      {isDeclined && <div className="rd-stage-node is-declined"><span className="rd-stage-dot"><XCircle size={18} /></span><span className="rd-stage-label">Declined</span></div>}
    </div>
  );
}

function StatCard({ label: lbl, value, icon: Icon, accent }) {
  return (
    <div className={`rd-stat-card ${accent || ''}`}>
      <div>
        <p className="rd-stat-label">{lbl}</p>
        <p className="rd-stat-value">{value}</p>
      </div>
      <Icon size={20} className="rd-stat-icon" />
    </div>
  );
}

function TrlBadge({ level, size = 'sm' }) {
  if (!level) return null;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold border ${trlColor(level)} ${size === 'sm' ? 'text-[9px]' : 'text-xs'}`} title={TRL_NAMES[level]}>TRL-{level}</span>;
}

// ══════════════════════════════════════════════════════
// EMPTY FORM STATES
// ══════════════════════════════════════════════════════

const emptyProject = {
  title: '', description: '', projectType: 'Research', status: 'Draft', priority: 'Medium',
  trlStage: '', trlTarget: '', divisionId: '', clusterId: '', principalInvestigator: '', projectManager: '',
  researchTheme: '', researchThemeId: '', startDate: '', endDate: '', totalBudget: '', fundingSource: '',
  totalYears: 1, financialYear: 2026, confidentialityLevel: 'Internal',
  // ── Phase C+: per-year planning entered at creation time ──
  yearPlans: [], // [{yearNumber, financialYear, expectedOutcome, plannedBudget}, ...]
  fundingCategory: '', piEmail: '', piPhone: '', piEmployeeNumber: '', divisionalManager: '',
  coInvestigators: '', executiveSummary: '', problemStatement: '', mandateAlignment: '',
  innovationNovelty: '', methodology: '', socioeconomicImpact: '', commercialisationPotential: '',
  existingInfrastructure: '', additionalResources: '', keyReferences: '',
  govtPriorities: [], selectedSOs: [], selectedProgrammes: [], selectedKpis: [],
  clientName: '', clientContactName: '', clientContactEmail: '', clientContactPhone: '',
  scopeDescription: '', costCentreCode: '',
  impactScore: '', feasibilityScore: '', alignmentScore: '',
  risks: [
    { description: '', likelihood: 'Medium', impact: 'Medium', mitigation: '' },
    { description: '', likelihood: 'Medium', impact: 'Medium', mitigation: '' },
    { description: '', likelihood: 'Medium', impact: 'Medium', mitigation: '' },
  ],
  complianceAnswers: {},  // { 0: 'Yes', 1: 'No', ... } keyed by question index
  mintekTheme: '', dstPriority: '', mprdaMandate: '', ndpPriority: '',  // Strategy tags
};

const emptyMilestone = { title: '', description: '', startDate: '', dueDate: '', responsiblePerson: '', weightPercentage: 0, progressPercentage: 0, status: 'NotStarted', yearNumber: 1 };
const emptyBudgetLine = { category: 'Consumables & Materials', customCategory: '', description: '', budgetAmount: 0, actualAmount: 0, yearNumber: 1, incurredDate: '', reference: '' };
const emptyOutput = { outputType: 'Peer-Reviewed Publication', description: '', targetDate: '', isPlanned: true, yearNumber: 1, status: 'Planned' };

// ══════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════

export default function RdProjectsPage() {
  // ── Core State ──
  const [projects, setProjects] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('overview');
  const [mainView, setMainView] = useState('list');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyProject);
  const [editingId, setEditingId] = useState(null);
  const [formStep, setFormStep] = useState(0);
  const [activeYear, setActiveYear] = useState(1);

  // ── Toast Notifications ──
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'error') => setToast({ message, type });

  // ── Reference Data ──
  const [divisions, setDivisions] = useState([]);
  const [clusters, setClusters] = useState([]);              // V3.1
  const [researchThemes, setResearchThemes] = useState([]);  // V3.1
  const [motivationTopics, setMotivationTopics] = useState([]); // V3.1 — replaces hardcoded GOVT_PRIORITIES
  const [objectives, setObjectives] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  // ── Phase A ──
  const [kpis, setKpis] = useState([]);
  const [roleRates, setRoleRates] = useState([]);
  const [resourceYear, setResourceYear] = useState(null);
  const [resourceForm, setResourceForm] = useState({
    id: null, yearNumber: 1, roleRateId: '', assigneeName: '', assigneeUserId: '',
    plannedHours: 0, actualHours: 0, notes: ''
  });
  // ── Phase B: Project Costs sub-tab + budget edit mode + year filter ──
  const [costsSubTab, setCostsSubTab] = useState('labour'); // 'labour' | 'running'
  const [costsYear, setCostsYear] = useState(null);
  const [editingBudgetId, setEditingBudgetId] = useState(null);

  // ── Phase C: Attachments sub-tab + forms for documents, deliverables, extensions, billing ──
  const [attachmentsSubTab, setAttachmentsSubTab] = useState('documents'); // 'documents' | 'deliverables'
  const [documentForm, setDocumentForm] = useState({
    id: null, documentType: 'Other', description: '', version: 1,
    fileUrl: '', fileBase64: '', fileName: '', isConfidential: false
  });
  const [deliverableForm, setDeliverableForm] = useState({
    id: null, milestoneId: '', deliverableType: 'Report', title: '',
    description: '', version: 1, isForClient: true,
    fileUrl: '', fileBase64: '', fileName: ''
  });
  const [extensionForm, setExtensionForm] = useState({
    id: null, reason: 'ClientRequest', reasonOther: '', description: '',
    additionalBudget: 0, revisedEndDate: '', impactOnExistingWork: ''
  });
  const [billingForm, setBillingForm] = useState({
    id: null, sequenceNumber: 0, title: '', description: '',
    milestoneId: '', percentage: '', amount: 0, targetDate: '', notes: ''
  });
  const [invoiceForm, setInvoiceForm] = useState({ id: null, invoiceReference: '', invoicedDate: '' });
  const [paidForm, setPaidForm] = useState({ id: null, paidDate: '' });

  // ── Filters ──
  const [filters, setFilters] = useState({
    status: '', type: '', search: '', division: '', fy: 2026, trl: '',
    // V3.3 — new tester-requested filter dimensions
    clusterId: '', researchThemeId: '', fundingSource: '',
    groupBy: ''                       // '' | 'cluster' | 'researchTheme' | 'fundingSource'
  });

  // V3.3 — grants linked to the currently-selected project (Grants tab)
  const [projectGrants, setProjectGrants] = useState([]);
  const [grantLinkModal, setGrantLinkModal] = useState(false);
  const [grantLinkForm, setGrantLinkForm] = useState({ grantId: '', allocatedAmount: '', allocatedPercentage: '', notes: '' });
  const [allGrants, setAllGrants] = useState([]);

  // ── Sub-entity Forms ──
  const [msForm, setMsForm] = useState(emptyMilestone);
  const [budgetForm, setBudgetForm] = useState(emptyBudgetLine);
  const [outputForm, setOutputForm] = useState(emptyOutput);
  const [reviewComment, setReviewComment] = useState('');

  // ── Auto-save draft when form changes ──
  useEffect(() => { if (modal === 'project' && form.title) saveDraft(form); }, [form, modal]);

  // ── Auto-calculate endDate when startDate or totalYears changes ──
  useEffect(() => {
    if (modal === 'project' && form.startDate && form.totalYears) {
      const start = new Date(form.startDate);
      if (!isNaN(start.getTime())) {
        const end = new Date(start);
        end.setFullYear(end.getFullYear() + parseInt(form.totalYears));
        const autoEnd = end.toISOString().split('T')[0];
        // Only auto-set if endDate is empty or was previously auto-calculated
        if (!form.endDate || form._autoEndDate) {
          setForm(prev => ({ ...prev, endDate: autoEnd, _autoEndDate: true }));
        }
      }
    }
  }, [form.startDate, form.totalYears, modal]);

  // ── Phase C+: keep yearPlans array in sync with totalYears and financialYear ──
  // Preserves any already-typed outcomes / budgets when the user increments duration.
  useEffect(() => {
    if (modal !== 'project') return;
    const n = parseInt(form.totalYears) || 1;
    const baseFy = parseInt(form.financialYear) || 2026;
    setForm(prev => {
      const existing = Array.isArray(prev.yearPlans) ? prev.yearPlans : [];
      const next = [];
      for (let i = 1; i <= n; i++) {
        const prior = existing.find(p => p.yearNumber === i);
        next.push({
          yearNumber: i,
          financialYear: baseFy + (i - 1),
          expectedOutcome: prior?.expectedOutcome || '',
          plannedBudget: prior?.plannedBudget ?? ''
        });
      }
      // Only update state if something actually changed (avoids render loop)
      const same = existing.length === next.length && next.every((x, idx) =>
        existing[idx]
        && existing[idx].yearNumber === x.yearNumber
        && existing[idx].financialYear === x.financialYear
        && (existing[idx].expectedOutcome || '') === (x.expectedOutcome || '')
        && (existing[idx].plannedBudget ?? '') === (x.plannedBudget ?? '')
      );
      return same ? prev : { ...prev, yearPlans: next };
    });
  }, [form.totalYears, form.financialYear, modal]);

  // ══════════════════════════════════════════════════════
  // DATA LOADING — with graceful error handling
  // ══════════════════════════════════════════════════════

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // rdProjectsApi.getAll already maps response fields via mapProjectListFromApi
      const r = await rdProjectsApi.getAll({ financialYear: filters.fy });
      console.log('[RdProjectsPage] load result:', r.data?.length, 'projects');
      setProjects(r.data || []);
    } catch (e) {
      console.error('[RdProjectsPage] load failed:', e);
      console.error('[RdProjectsPage] response data:', e?.response?.data);
      console.error('[RdProjectsPage] response status:', e?.response?.status);
      setProjects([]);
    }
    setLoading(false);
  }, [filters.fy]);

  useEffect(() => {
    load();
    // Load reference data — each wrapped separately so one failure doesn't block others
    divisionsApi.getAll().then(r => setDivisions(r.data || [])).catch(() => {});
    clustersApi.getAll().then(r => setClusters(r.data || [])).catch(() => setClusters([]));            // V3.1
    researchThemesApi.getAll(true).then(r => setResearchThemes(r.data || [])).catch(() => setResearchThemes([])); // V3.1 — active only
    motivationTopicsApi.getAll(true).then(r => setMotivationTopics(r.data || [])).catch(() => setMotivationTopics([])); // V3.1 — active topics, replaces hardcoded GOVT_PRIORITIES
    objectivesApi.getAll(filters.fy).then(r => setObjectives(r.data || [])).catch(() => {});
    programmesApi.getAll({}).then(r => setProgrammes(r.data || [])).catch(() => {});
    // Phase A: KPI catalogue for project form + active rate card for resources
    kpisApi.getAll({ financialYear: filters.fy }).then(r => setKpis(r.data || [])).catch(() => setKpis([]));
    roleRatesApi.getAll({ includeInactive: false }).then(r => setRoleRates(r.data || r || [])).catch(() => setRoleRates([]));
  }, [load, filters.fy]);

  // ── Phase C++: auto-open the first project when the list loads and nothing is selected ──
  // Uses a ref as an in-flight guard so React's rapid re-renders don't trigger
  // multiple concurrent loadDetail calls (which show as two `(pending)` XHRs
  // in DevTools).
  const autoOpenFiredRef = React.useRef(false);
  useEffect(() => {
    if (autoOpenFiredRef.current) return;
    if (!detail && !loading && projects.length > 0) {
      autoOpenFiredRef.current = true;
      loadDetail(projects[0].id);
    }
  }, [projects, loading, detail]);

  // resetView=true for fresh project switch (go to Overview, Year 1).
  // resetView=false for in-place refresh after a save — preserve the tab the user is on.
  const loadDetail = async (id, resetView = true) => {
    // Only show the spinner on full project switches; in-place refreshes after a
    // save shouldn't flash the overlay over the user's tab.
    if (resetView) setDetailLoading(true);
    try {
      // GET {id} already includes sections, milestones, budgetLines, approvalEvents, etc.
      // mapProjectFromApi extracts _sectionData from sections automatically
      const r = await rdProjectsApi.get(id);
      setDetail(r.data);
      // V3.3 — load linked grants in parallel for the Grants tab
      loadProjectGrants(id);
      if (resetView) {
        setTab('overview');
        setActiveYear(1);
      }
    } catch (e) {
      showToast('Failed to load project details: ' + getErrorMsg(e));
    } finally {
      if (resetView) setDetailLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════
  // SAVE PROJECT — NEVER loses form data on error
  // ══════════════════════════════════════════════════════
  // rdProjectsApi.create / .update handle field mapping:
  //   form.principalInvestigator → payload.piName
  //   form.trlStage              → payload.trlCurrent
  //   form.researchTheme         → payload.researchGroup
  //   (removes priority, impactScore, feasibilityScore, alignmentScore — not on RdProject)
  // ══════════════════════════════════════════════════════

  const saveProject = async () => {
    // Validate required fields
    if (!form.title?.trim()) { showToast('Project title is required', 'warning'); return; }
    if (!form.principalInvestigator?.trim()) { showToast('Principal Investigator is required', 'warning'); return; }

    setSaving(true);
    try {
      const soIds = form.selectedSOs || [];
      const progIds = form.selectedProgrammes || [];
      const kpiIds = form.selectedKpis || [];

      let projectId;
      if (editingId) {
        // rdProjectsApi.update maps form fields → RdProject model fields
        await rdProjectsApi.update(editingId, form);
        projectId = editingId;
      } else {
        // rdProjectsApi.create maps form fields → RdProject model fields
        const r = await rdProjectsApi.create(form);
        projectId = r.data?.id || r.data;
      }

      // Save form section data into the auto-created RdProjectSection records.
      // Controller auto-creates sections on Create. For new projects, we need to
      // fetch them first to get their IDs. For edits, we already have detail.sections.
      try {
        let sections = editingId ? (detail?.sections || []) : [];
        if (!editingId && projectId) {
          // Fetch the newly created project to get the auto-created section IDs
          const freshProject = await rdProjectsApi.get(projectId);
          sections = freshProject.data?.sections || [];
        }
        if (sections.length > 0) {
          await rdProjectsApi.saveSections(projectId, form, sections);
        }
      } catch (e) { console.warn('Section save:', e.message); }

      // Save SO and Programme linkages — wrapped individually
      if (soIds.length > 0) { try { await rdProjectsApi.setProjectSOs(projectId, soIds); } catch (e) { console.warn('SO linkage failed:', e.message); } }
      if (progIds.length > 0) { try { await rdProjectsApi.setProjectProgrammes(projectId, progIds); } catch (e) { console.warn('Programme linkage failed:', e.message); } }
      // Phase A: always call (including empty list — this lets the user clear KPIs by deselecting all)
      try { await rdProjectsApi.setKpis(projectId, kpiIds); } catch (e) { console.warn('KPI linkage failed:', e.message); }

      // Save strategy tags (RdStrategyTag) — required for submission
      try {
        const tags = [];
        if (form.mintekTheme?.trim()) tags.push({ category: 'MintekTheme', tagValue: form.mintekTheme.trim() });
        if (form.dstPriority?.trim()) tags.push({ category: 'DSTNVPriority', tagValue: form.dstPriority.trim() });
        if (form.mprdaMandate?.trim()) tags.push({ category: 'MPRDAMandate', tagValue: form.mprdaMandate.trim() });
        if (form.ndpPriority?.trim()) tags.push({ category: 'NDPGovernment', tagValue: form.ndpPriority.trim() });
        // Also include government priorities from section 4 as tags
        for (const gp of (form.govtPriorities || [])) {
          if (!tags.some(t => t.category === 'NDPGovernment' && t.tagValue === gp)) {
            tags.push({ category: 'NDPGovernment', tagValue: gp });
          }
        }
        if (tags.length > 0) { await rdProjectsApi.setStrategyTags(projectId, tags); }
      } catch (e) { console.warn('Strategy tags save failed:', e.message); }

      // SUCCESS — now clear everything
      clearDraft();
      setModal(null);
      setEditingId(null);
      setForm(emptyProject);
      setFormStep(0);
      showToast(editingId ? 'Project updated successfully' : 'Project created successfully', 'success');
      load();
      if (editingId && detail?.id === editingId) loadDetail(editingId);

    } catch (e) {
      // FAILURE — do NOT close modal, do NOT clear form
      showToast('Save failed: ' + getErrorMsg(e));
    }
    setSaving(false);
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    try {
      await rdProjectsApi.delete(id);
      if (detail?.id === id) setDetail(null);
      showToast('Project deleted', 'success');
      load();
    } catch (e) { showToast('Delete failed: ' + getErrorMsg(e)); }
  };

  const openEdit = (p) => {
    // V3.5 — warn before editing a post-approval project; changes may need re-approval
    if (p && !['Draft', 'RevisionsRequired'].includes(p.status)) {
      const proceed = window.confirm(
        `This project is in '${STATUS_LABELS[p.status] || p.status}' status. ` +
        `Editing it after approval may require the project to go through re-approval. ` +
        `Are you sure you want to continue?`
      );
      if (!proceed) return;
    }
    // p is already mapped by mapProjectFromApi, so aliased fields are available:
    //   p.principalInvestigator (alias of piName)
    //   p.trlStage (alias of trlCurrent)
    //   p.researchTheme (alias of researchGroup)
    //   p.actualSpend (alias of totalActualSpend)
    // Also merge any section data
    const secData = p._sectionData || {};
    setForm({
      ...emptyProject,
      title: p.title || '',
      description: secData.objectives || secData.description || p.description || '',
      projectType: p.projectType || 'Research',
      status: p.status || 'Draft',
      trlStage: p.trlStage?.toString() || '',
      trlTarget: p.trlTarget?.toString() || '',
      divisionId: p.divisionId || '',
      principalInvestigator: p.principalInvestigator || '',
      piEmail: p.piEmail || '',
      piPhone: p.piPhone || '',
      piEmployeeNumber: p.piEmployeeNumber || '',
      researchTheme: p.researchTheme || '',
      researchThemeId: p.researchThemeId || '',  // V3.1
      clusterId: p.clusterId || '',              // V3.1
      priorGrant: p.priorGrantReference || '',   // V3.1: reuse PriorGrantReference for Yes/No
      coInvestigators: p.coInvestigators || '',
      startDate: isoDate(p.startDate),
      endDate: isoDate(p.endDate),
      totalBudget: p.totalBudget?.toString() || '',
      fundingSource: p.fundingSource || '',
      totalYears: p.totalYears || 1,
      financialYear: p.financialYear || 2026,
      // ── Phase C+: Rehydrate per-year plans from p.years + existing yearly-outcome outputs ──
      // This lets editors see and adjust the planned budget & outcome for each year. The
      // useEffect below also keeps this array in sync if totalYears / financialYear change.
      yearPlans: (() => {
        const years = Array.isArray(p.years) ? [...p.years].sort((a, b) => a.yearNumber - b.yearNumber) : [];
        const outputs = Array.isArray(p.outputs) ? p.outputs : [];
        const yearlyOutcomes = {};
        outputs
          .filter(o => o.outputType === 'YearlyOutcome' && o.yearNumber && !o.isDeleted)
          .forEach(o => { if (!yearlyOutcomes[o.yearNumber]) yearlyOutcomes[o.yearNumber] = o.description || ''; });
        const total = p.totalYears || Math.max(1, years.length);
        const baseFy = p.financialYear || 2026;
        return Array.from({ length: total }, (_, i) => {
          const yNum = i + 1;
          const rec = years.find(y => y.yearNumber === yNum);
          return {
            yearNumber: yNum,
            financialYear: rec?.financialYear || (baseFy + i),
            expectedOutcome: yearlyOutcomes[yNum] || '',
            plannedBudget: rec?.plannedBudget != null ? rec.plannedBudget.toString() : '',
          };
        });
      })(),
      confidentialityLevel: p.confidentialityLevel || 'Internal',
      fundingCategory: p.fundingCategory || '',
      // Section data
      executiveSummary: secData.executiveSummary || '',
      problemStatement: secData.problemStatement || '',
      mandateAlignment: secData.mandateAlignment || '',
      innovationNovelty: secData.innovationNovelty || '',
      methodology: secData.methodology || '',
      socioeconomicImpact: secData.socioeconomicImpact || '',
      commercialisationPotential: secData.commercialisationPotential || '',
      existingInfrastructure: secData.existingInfrastructure || '',
      additionalResources: secData.additionalResources || '',
      keyReferences: secData.keyReferences || '',
      scopeDescription: secData.scopeDescription || '',
      govtPriorities: secData.govtPriorities || [],
      divisionalManager: secData.divisionalManager || '',
      // Scoring (from section data — not on RdProject model directly)
      impactScore: secData.impactScore || '',
      feasibilityScore: secData.feasibilityScore || '',
      alignmentScore: secData.alignmentScore || '',
      // Risks & compliance from section data
      risks: secData.risks || [
        { description: '', likelihood: 'Medium', impact: 'Medium', mitigation: '' },
        { description: '', likelihood: 'Medium', impact: 'Medium', mitigation: '' },
        { description: '', likelihood: 'Medium', impact: 'Medium', mitigation: '' },
      ],
      complianceAnswers: secData.complianceAnswers || {},
      // Commercial fields
      clientName: p.clientName || '',
      clientContactName: p.clientContactName || '',
      clientContactEmail: p.clientContactEmail || '',
      clientContactPhone: p.clientContactPhone || '',
      costCentreCode: p.costCentreCode || '',
      // Strategy linkage
      selectedSOs: (p.strategicObjectives || []).map(x => x.strategicObjectiveId),
      selectedProgrammes: (p.programmes || []).map(x => x.initiativeId),
      selectedKpis: (p.kpiLinks || []).map(x => x.kpiDefinitionId),
      // Strategy tags (RdStrategyTag)
      mintekTheme: (p.strategyTags || []).find(t => t.category === 'MintekTheme')?.tagValue || '',
      dstPriority: (p.strategyTags || []).find(t => t.category === 'DSTNVPriority')?.tagValue || '',
      mprdaMandate: (p.strategyTags || []).find(t => t.category === 'MPRDAMandate')?.tagValue || '',
      ndpPriority: (p.strategyTags || []).find(t => t.category === 'NDPGovernment')?.tagValue || '',
    });
    setEditingId(p.id);
    setFormStep(1);
    setModal('project');
  };

  const openNewWithDraftRestore = () => {
    const draft = loadDraft();
    if (draft && draft.title) {
      if (window.confirm(`You have an unsaved draft: "${draft.title}". Restore it?`)) {
        setForm(draft);
        setFormStep(1);
      } else {
        clearDraft();
        setForm({ ...emptyProject, financialYear: filters.fy });
        setFormStep(0);
      }
    } else {
      setForm({ ...emptyProject, financialYear: filters.fy });
      setFormStep(0);
    }
    setEditingId(null);
    setModal('project');
  };

  // ── Workflow Actions — controller uses POST {id}/decide with RdApprovalDecisionDto ──
  const submitProject = async () => {
    if (!window.confirm('Submit for review? The form will be locked.')) return;
    try { await rdProjectsApi.submit(detail.id); showToast('Project submitted for review', 'success'); loadDetail(detail.id, false); load(); }
    catch (e) { showToast('Submit failed: ' + getErrorMsg(e)); }
  };
  const approveProject = async () => {
    try { await rdProjectsApi.approve(detail.id, reviewComment); setReviewComment(''); showToast('Project approved', 'success'); loadDetail(detail.id, false); load(); }
    catch (e) { showToast('Approval failed: ' + getErrorMsg(e)); }
  };
  const returnProject = async () => {
    if (!reviewComment.trim()) { showToast('Comments are required when returning for revisions', 'warning'); return; }
    try { await rdProjectsApi.returnForRevisions(detail.id, reviewComment); setReviewComment(''); showToast('Returned for revisions', 'success'); loadDetail(detail.id, false); load(); }
    catch (e) { showToast('Failed: ' + getErrorMsg(e)); }
  };
  const declineProject = async () => {
    if (!reviewComment.trim()) { showToast('Comments are required when declining', 'warning'); return; }
    if (!window.confirm('Decline this project? This is final.')) return;
    try { await rdProjectsApi.decline(detail.id, reviewComment); setReviewComment(''); showToast('Project declined', 'success'); loadDetail(detail.id, false); load(); }
    catch (e) { showToast('Decline failed: ' + getErrorMsg(e)); }
  };

  // V3.5.1 — Helper: compute project progress from its milestones using the
  // same weighted-average formula the backend uses. Used by the optimistic
  // update path so the project list card progress reflects milestone edits
  // immediately (without waiting for the backend's recalculation to round-trip).
  const computeProjectProgress = (milestones) => {
    const ms = (milestones || []).filter(m => !m.isDeleted);
    if (ms.length === 0) return 0;
    const totalWeight = ms.reduce((s, m) => s + (Number(m.weightPercentage) || 0), 0);
    let p;
    if (totalWeight > 0) {
      p = ms.reduce((s, m) => s + (Number(m.weightPercentage) || 0) * (Number(m.progressPercentage) || 0), 0) / totalWeight;
    } else {
      p = ms.reduce((s, m) => s + (Number(m.progressPercentage) || 0), 0) / ms.length;
    }
    return Math.round(Math.max(0, Math.min(100, p * 10)) / 10 * 10) / 10;
  };

  // ── Sub-entity Saves ──
  // rdProjectsApi.addMilestone maps: responsiblePerson (not assignedTo), yearNumber (not financialYear)
  // V3.3 — saveMilestone now handles BOTH create and update. When msForm.id is
  // set we PUT (update); otherwise POST (add). The backend enforces auto-status
  // rules around progressPercentage (0=NotStarted hint, 100=Completed).
  //
  // V3.5.1 — Performance + correctness fixes:
  //   • No more awaited loadDetail / load() after save — the optimistic merge
  //     covers the UX path. The previous version added two sequential awaited
  //     fetches that made the modal feel sluggish (~5s on Azure).
  //   • Compute the parent project's new ProgressPercentage locally and patch
  //     both the open detail AND the matching card in the projects list, so
  //     the list card progress bar updates immediately.
  //   • Backend still authoritative — a background loadDetail (fire-and-forget)
  //     reconciles any auto-status rules the server applied.
  const saveMilestone = async () => {
    if (!msForm.title?.trim()) { showToast('Milestone title is required', 'warning'); return; }
    setSaving(true);
    try {
      let savedMs;
      if (msForm.id) {
        const r = await rdProjectsApi.updateMilestone(msForm.id, msForm);
        savedMs = r?.data;
        showToast('Milestone updated', 'success');
      } else {
        const r = await rdProjectsApi.addMilestone(detail.id, msForm);
        savedMs = r?.data;
        showToast('Milestone added', 'success');
      }
      const editedId = msForm.id;
      setModal(null);
      setMsForm(emptyMilestone);

      // ── Optimistic merge: update milestone in detail, plus recalculate progress ──
      setDetail(d => {
        if (!d) return d;
        let nextMilestones;
        if (editedId) {
          nextMilestones = (d.milestones || []).map(m =>
            m.id === editedId
              ? { ...m, ...(savedMs || {}), assignedTo: (savedMs?.responsiblePerson) || m.assignedTo || '' }
              : m
          );
        } else if (savedMs) {
          nextMilestones = [...(d.milestones || []), { ...savedMs, assignedTo: savedMs.responsiblePerson || '' }];
        } else {
          nextMilestones = d.milestones;
        }
        return {
          ...d,
          milestones: nextMilestones,
          progressPercentage: computeProjectProgress(nextMilestones)
        };
      });

      // Patch the matching card in the projects list so the progress bar updates
      setProjects(list => list.map(p =>
        p.id === detail.id
          ? { ...p, progressPercentage: computeProjectProgress(
              (() => {
                if (editedId) {
                  return (detail.milestones || []).map(m =>
                    m.id === editedId
                      ? { ...m, ...(savedMs || {}) }
                      : m
                  );
                } else if (savedMs) {
                  return [...(detail.milestones || []), savedMs];
                }
                return detail.milestones || [];
              })()
            ) }
          : p
      ));

      // Fire-and-forget canonical refresh — picks up backend auto-rules without
      // blocking the UX. If it fails silently, the optimistic state is still correct.
      loadDetail(detail.id, false).catch(() => {});
    } catch (e) { showToast('Failed to save milestone: ' + getErrorMsg(e)); }
    setSaving(false);
  };

  // V3.3 — Open the milestone modal in edit mode, pre-populated from the row
  const openMilestoneEdit = (ms) => {
    setMsForm({
      id: ms.id,
      title: ms.title || '',
      description: ms.description || '',
      responsiblePerson: ms.responsiblePerson || ms.assignedTo || '',
      startDate: isoDate(ms.startDate),
      dueDate: isoDate(ms.dueDate),
      completedDate: isoDate(ms.completedDate),
      status: ms.status || 'NotStarted',
      weightPercentage: ms.weightPercentage ?? 0,
      progressPercentage: ms.progressPercentage ?? 0,
      yearNumber: ms.yearNumber || 1
    });
    setModal('milestone');
  };

  // V3.3 — Quick "Mark Complete" or progress bump from the milestone row
  // (skips the modal). Backend auto-sets Status + CompletedDate.
  // V3.5.1 — Same fast-path as saveMilestone: optimistic update + fire-and-forget refresh.
  const updateMilestoneProgress = async (ms, newProgress) => {
    const clamped = Math.max(0, Math.min(100, Number(newProgress) || 0));
    try {
      const r = await rdProjectsApi.updateMilestone(ms.id, {
        title: ms.title,
        description: ms.description,
        responsiblePerson: ms.responsiblePerson || ms.assignedTo,
        startDate: ms.startDate,
        dueDate: ms.dueDate,
        completedDate: ms.completedDate,
        status: ms.status,
        weightPercentage: ms.weightPercentage,
        progressPercentage: clamped,
        yearNumber: ms.yearNumber
      });
      const savedMs = r?.data;

      // Optimistic merge into detail + project progress recalc
      setDetail(d => {
        if (!d) return d;
        const nextMilestones = (d.milestones || []).map(m =>
          m.id === ms.id
            ? { ...m, ...(savedMs || { progressPercentage: clamped }), assignedTo: (savedMs?.responsiblePerson) || m.assignedTo || '' }
            : m
        );
        return { ...d, milestones: nextMilestones, progressPercentage: computeProjectProgress(nextMilestones) };
      });

      // Patch the corresponding card in the list
      setProjects(list => list.map(p =>
        p.id === detail.id
          ? { ...p, progressPercentage: computeProjectProgress(
              (detail.milestones || []).map(m =>
                m.id === ms.id ? { ...m, progressPercentage: clamped } : m
              )
            ) }
          : p
      ));

      // Fire-and-forget canonical refresh
      loadDetail(detail.id, false).catch(() => {});

      if (clamped === 100) showToast('Milestone marked complete', 'success');
    } catch (e) { showToast('Update failed: ' + getErrorMsg(e)); }
  };

  const deleteMilestone = async (id) => {
    if (!window.confirm('Delete milestone?')) return;
    try { await rdProjectsApi.deleteMilestone(id); loadDetail(detail.id, false); load(); } catch (e) { showToast('Delete failed: ' + getErrorMsg(e)); }
  };

  // V3.3 — Grants linked to the selected project (Grants tab)
  const loadProjectGrants = useCallback(async (projectId) => {
    if (!projectId) { setProjectGrants([]); return; }
    try {
      const r = await grantsApi.getByProject(projectId);
      setProjectGrants(r.data || []);
    } catch (e) {
      console.error('Project grants load failed:', e);
      setProjectGrants([]);
    }
  }, []);

  const openGrantLink = async () => {
    try {
      const r = await grantsApi.getAll();
      setAllGrants(r.data || []);
    } catch { setAllGrants([]); }
    setGrantLinkForm({ grantId: '', allocatedAmount: '', allocatedPercentage: '', notes: '' });
    setGrantLinkModal(true);
  };

  const linkGrantToProject = async () => {
    if (!grantLinkForm.grantId) return;
    try {
      await grantsApi.linkProject(grantLinkForm.grantId, detail.id, {
        allocatedAmount: grantLinkForm.allocatedAmount ? parseFloat(grantLinkForm.allocatedAmount) : null,
        allocatedPercentage: grantLinkForm.allocatedPercentage ? parseFloat(grantLinkForm.allocatedPercentage) : null,
        notes: grantLinkForm.notes || null
      });
      setGrantLinkModal(false);
      loadProjectGrants(detail.id);
      showToast('Grant linked', 'success');
    } catch (e) {
      showToast('Link failed: ' + getErrorMsg(e));
    }
  };

  const unlinkGrantFromProject = async (grantId) => {
    if (!window.confirm('Unlink this grant from the project?')) return;
    try {
      await grantsApi.unlinkProject(grantId, detail.id);
      loadProjectGrants(detail.id);
    } catch (e) { showToast('Unlink failed: ' + getErrorMsg(e)); }
  };
  // rdProjectsApi.addBudgetLine / updateBudgetLine — handles both create and edit
  const saveBudgetLine = async () => {
    // Phase B: Other category requires CustomCategory
    if (budgetForm.category === 'Other' && !budgetForm.customCategory?.trim()) {
      showToast('Please specify a custom category name when Other is selected', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (editingBudgetId) {
        await rdProjectsApi.updateBudgetLine(editingBudgetId, budgetForm);
        showToast('Running cost updated', 'success');
      } else {
        await rdProjectsApi.addBudgetLine(detail.id, budgetForm);
        showToast('Running cost added', 'success');
      }
      setModal(null);
      setBudgetForm(emptyBudgetLine);
      setEditingBudgetId(null);
      loadDetail(detail.id, false);
    } catch (e) { showToast('Failed: ' + getErrorMsg(e)); }
    setSaving(false);
  };
  // ── Phase B: Running cost edit + delete handlers ──
  const openRunningEdit = (bl) => {
    setBudgetForm({
      id: bl.id,
      category: bl.category || 'Consumables & Materials',
      customCategory: bl.customCategory || '',
      description: bl.description || '',
      budgetAmount: bl.plannedAmount ?? bl.budgetAmount ?? 0,
      actualAmount: bl.actualAmount ?? 0,
      yearNumber: bl.yearNumber || 1,
      incurredDate: bl.incurredDate ? bl.incurredDate.split('T')[0] : '',
      reference: bl.reference || '',
      varianceNote: bl.varianceNote || '',
    });
    setEditingBudgetId(bl.id);
    setModal('budget');
  };
  const openRunningAdd = (yearNumber = activeYear || 1) => {
    setBudgetForm({ ...emptyBudgetLine, yearNumber });
    setEditingBudgetId(null);
    setModal('budget');
  };
  const deleteRunningLine = async (id) => {
    if (!window.confirm('Delete this running cost line?')) return;
    try {
      await rdProjectsApi.deleteBudgetLine(id);
      showToast('Running cost deleted', 'success');
      loadDetail(detail.id, false);
    } catch (e) { showToast('Delete failed: ' + getErrorMsg(e)); }
  };
  // ── Phase B: resolved category label (Custom for Other) ──
  const rcLabel = (bl) => (bl.category === 'Other' && bl.customCategory) ? bl.customCategory : (bl.category || '—');

  // ── Phase C hotfix: trigger a browser download from a base64 blob ──
  const downloadBase64AsFile = (base64, fileName) => {
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { showToast('Download failed: ' + (e.message || 'unknown'), 'warning'); }
  };
  const downloadDocument = async (d) => {
    if (d.fileUrl) { window.open(d.fileUrl, '_blank', 'noopener,noreferrer'); return; }
    try {
      const r = await rdProjectsApi.getDocumentFile(d.id);
      if (r.data?.fileBase64) downloadBase64AsFile(r.data.fileBase64, r.data.fileName || d.fileName || 'document');
      else if (r.data?.fileUrl) window.open(r.data.fileUrl, '_blank', 'noopener,noreferrer');
      else showToast('No file attached to this document', 'warning');
    } catch (e) { showToast('Download failed: ' + getErrorMsg(e)); }
  };
  const downloadDeliverable = async (d) => {
    if (d.fileUrl) { window.open(d.fileUrl, '_blank', 'noopener,noreferrer'); return; }
    try {
      const r = await rdProjectsApi.getDeliverableFile(d.id);
      if (r.data?.fileBase64) downloadBase64AsFile(r.data.fileBase64, r.data.fileName || d.title || 'deliverable');
      else if (r.data?.fileUrl) window.open(r.data.fileUrl, '_blank', 'noopener,noreferrer');
      else showToast('No file attached to this deliverable', 'warning');
    } catch (e) { showToast('Download failed: ' + getErrorMsg(e)); }
  };

  // ═══════════════════════════════════════════════════════════
  // ── Phase C handlers ──
  // ═══════════════════════════════════════════════════════════

  // Helper: read a File as base64 (strip the data URL prefix)
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

  // ── Documents ──
  const openDocumentAdd = () => {
    setDocumentForm({ id: null, documentType: 'Other', description: '', version: 1, fileUrl: '', fileBase64: '', fileName: '', isConfidential: false });
    setModal('document');
  };
  const openDocumentEdit = (d) => {
    setDocumentForm({
      id: d.id, documentType: d.documentType || 'Other', description: d.description || '',
      version: d.version || 1, fileUrl: d.fileUrl || '', fileBase64: '', fileName: d.fileName || '',
      isConfidential: !!d.isConfidential
    });
    setModal('document');
  };
  const onDocumentFileChange = async (file) => {
    if (!file) return;
    try {
      const b64 = await fileToBase64(file);
      setDocumentForm(f => ({ ...f, fileBase64: b64, fileName: file.name }));
    } catch { showToast('Failed to read file', 'warning'); }
  };
  const saveDocument = async () => {
    if (!documentForm.id && !documentForm.fileBase64 && !documentForm.fileUrl) {
      showToast('Upload a file or provide a URL', 'warning'); return;
    }
    setSaving(true);
    try {
      const payload = {
        documentType: documentForm.documentType,
        description: documentForm.description || '',
        version: parseInt(documentForm.version) || 1,
        isConfidential: documentForm.isConfidential,
        fileUrl: documentForm.fileUrl || null,
        fileBase64: documentForm.fileBase64 || null,
        fileName: documentForm.fileName || null,
      };
      if (documentForm.id) {
        await rdProjectsApi.updateDocument(documentForm.id, payload);
        showToast('Document updated', 'success');
      } else {
        await rdProjectsApi.addDocument(detail.id, payload);
        showToast('Document uploaded', 'success');
      }
      setModal(null); loadDetail(detail.id, false);
    } catch (e) { showToast('Failed: ' + getErrorMsg(e)); }
    setSaving(false);
  };
  const deleteDocument = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try { await rdProjectsApi.deleteDocument(id); showToast('Document deleted', 'success'); loadDetail(detail.id, false); }
    catch (e) { showToast('Delete failed: ' + getErrorMsg(e)); }
  };

  // ── Deliverables ──
  const openDeliverableAdd = () => {
    setDeliverableForm({ id: null, milestoneId: '', deliverableType: 'Report', title: '', description: '', version: 1, isForClient: true, fileUrl: '', fileBase64: '', fileName: '' });
    setModal('deliverable');
  };
  const openDeliverableEdit = (d) => {
    setDeliverableForm({
      id: d.id, milestoneId: d.milestoneId || '', deliverableType: d.deliverableType || 'Report',
      title: d.title || '', description: d.description || '', version: d.version || 1,
      isForClient: d.isForClient !== false, fileUrl: d.fileUrl || '', fileBase64: '', fileName: d.fileName || ''
    });
    setModal('deliverable');
  };
  const onDeliverableFileChange = async (file) => {
    if (!file) return;
    try { const b64 = await fileToBase64(file); setDeliverableForm(f => ({ ...f, fileBase64: b64, fileName: file.name })); }
    catch { showToast('Failed to read file', 'warning'); }
  };
  const saveDeliverable = async () => {
    if (!deliverableForm.title?.trim()) { showToast('Deliverable title is required', 'warning'); return; }
    setSaving(true);
    try {
      const payload = {
        milestoneId: deliverableForm.milestoneId || null,
        deliverableType: deliverableForm.deliverableType,
        title: deliverableForm.title,
        description: deliverableForm.description || null,
        version: parseInt(deliverableForm.version) || 1,
        isForClient: deliverableForm.isForClient,
        fileUrl: deliverableForm.fileUrl || null,
        fileBase64: deliverableForm.fileBase64 || null,
        fileName: deliverableForm.fileName || null,
      };
      if (deliverableForm.id) {
        await rdProjectsApi.updateDeliverable(deliverableForm.id, payload);
        showToast('Deliverable updated', 'success');
      } else {
        await rdProjectsApi.addDeliverable(detail.id, payload);
        showToast('Deliverable added', 'success');
      }
      setModal(null); loadDetail(detail.id, false);
    } catch (e) { showToast('Failed: ' + getErrorMsg(e)); }
    setSaving(false);
  };
  const updateDeliverableStatus = async (d, newStatus) => {
    try {
      await rdProjectsApi.updateDeliverable(d.id, { ...d, status: newStatus });
      showToast(`Status → ${newStatus}`, 'success');
      loadDetail(detail.id, false);
    } catch (e) { showToast('Status change failed: ' + getErrorMsg(e)); }
  };

  // ── Scope Extensions ──
  const openExtensionAdd = () => {
    setExtensionForm({ id: null, reason: 'ClientRequest', reasonOther: '', description: '', additionalBudget: 0, revisedEndDate: '', impactOnExistingWork: '' });
    setModal('extension');
  };
  const openExtensionEdit = (ext) => {
    if (ext.status !== 'Draft') { showToast('Only Draft extensions can be edited', 'warning'); return; }
    setExtensionForm({
      id: ext.id, reason: ext.reason || 'ClientRequest', reasonOther: ext.reasonOther || '',
      description: ext.description || '', additionalBudget: ext.additionalBudget || 0,
      revisedEndDate: ext.revisedEndDate ? ext.revisedEndDate.split('T')[0] : '',
      impactOnExistingWork: ext.impactOnExistingWork || ''
    });
    setModal('extension');
  };
  const saveExtension = async () => {
    if (!extensionForm.description?.trim()) { showToast('Description is required', 'warning'); return; }
    if (extensionForm.reason === 'Other' && !extensionForm.reasonOther?.trim()) {
      showToast('Specify the reason when selecting Other', 'warning'); return;
    }
    setSaving(true);
    try {
      const payload = {
        reason: extensionForm.reason,
        reasonOther: extensionForm.reason === 'Other' ? extensionForm.reasonOther : null,
        description: extensionForm.description,
        additionalBudget: parseFloat(extensionForm.additionalBudget) || 0,
        revisedEndDate: extensionForm.revisedEndDate || null,
        impactOnExistingWork: extensionForm.impactOnExistingWork || null,
      };
      if (extensionForm.id) {
        await rdProjectsApi.updateScopeExtension(extensionForm.id, payload);
        showToast('Extension updated', 'success');
      } else {
        await rdProjectsApi.createScopeExtension(detail.id, payload);
        showToast('Extension drafted', 'success');
      }
      setModal(null); loadDetail(detail.id, false);
    } catch (e) { showToast('Failed: ' + getErrorMsg(e)); }
    setSaving(false);
  };
  const submitExtension = async (id) => {
    if (!window.confirm('Submit this extension for approval? Once submitted, it cannot be edited.')) return;
    try { await rdProjectsApi.submitScopeExtension(id); showToast('Extension submitted', 'success'); loadDetail(detail.id, false); }
    catch (e) { showToast('Submit failed: ' + getErrorMsg(e)); }
  };
  const deleteExtension = async (id) => {
    if (!window.confirm('Delete this draft extension?')) return;
    try { await rdProjectsApi.deleteScopeExtension(id); showToast('Extension deleted', 'success'); loadDetail(detail.id, false); }
    catch (e) { showToast('Delete failed: ' + getErrorMsg(e)); }
  };
  const extStatusBadge = (s) => {
    const map = { Draft: 'bg-slate-100 text-slate-700', Submitted: 'bg-amber-100 text-amber-700', UnderReview: 'bg-blue-100 text-blue-700', Approved: 'bg-emerald-100 text-emerald-700', Declined: 'bg-red-100 text-red-700' };
    return map[s] || 'bg-slate-100 text-slate-700';
  };

  // ── Billing Milestones ──
  const openBillingAdd = () => {
    const nextSeq = ((detail?.billingMilestones || []).length) + 1;
    setBillingForm({ id: null, sequenceNumber: nextSeq, title: '', description: '', milestoneId: '', percentage: '', amount: 0, targetDate: '', notes: '' });
    setModal('billing');
  };
  const openBillingEdit = (b) => {
    setBillingForm({
      id: b.id, sequenceNumber: b.sequenceNumber || 1, title: b.title || '', description: b.description || '',
      milestoneId: b.milestoneId || '', percentage: b.percentage ?? '', amount: b.amount || 0,
      targetDate: b.targetDate ? b.targetDate.split('T')[0] : '', notes: b.notes || ''
    });
    setModal('billing');
  };
  const saveBilling = async () => {
    if (!billingForm.title?.trim()) { showToast('Title is required', 'warning'); return; }
    setSaving(true);
    try {
      const payload = {
        sequenceNumber: parseInt(billingForm.sequenceNumber) || 1,
        title: billingForm.title,
        description: billingForm.description || null,
        milestoneId: billingForm.milestoneId || null,
        percentage: billingForm.percentage === '' || billingForm.percentage == null ? null : parseFloat(billingForm.percentage),
        amount: parseFloat(billingForm.amount) || 0,
        targetDate: billingForm.targetDate || null,
        notes: billingForm.notes || null,
      };
      if (billingForm.id) {
        await rdProjectsApi.updateBillingMilestone(billingForm.id, payload);
        showToast('Billing milestone updated', 'success');
      } else {
        await rdProjectsApi.createBillingMilestone(detail.id, payload);
        showToast('Billing milestone added', 'success');
      }
      setModal(null); loadDetail(detail.id, false);
    } catch (e) { showToast('Failed: ' + getErrorMsg(e)); }
    setSaving(false);
  };
  const deleteBilling = async (id) => {
    if (!window.confirm('Delete this billing milestone?')) return;
    try { await rdProjectsApi.deleteBillingMilestone(id); showToast('Deleted', 'success'); loadDetail(detail.id, false); }
    catch (e) { showToast('Delete failed: ' + getErrorMsg(e)); }
  };
  const openMarkInvoiced = (b) => {
    setInvoiceForm({ id: b.id, invoiceReference: '', invoicedDate: new Date().toISOString().split('T')[0] });
    setModal('invoice');
  };
  const submitMarkInvoiced = async () => {
    if (!invoiceForm.invoiceReference?.trim()) { showToast('Invoice reference is required', 'warning'); return; }
    setSaving(true);
    try {
      await rdProjectsApi.markBillingInvoiced(invoiceForm.id, {
        invoiceReference: invoiceForm.invoiceReference,
        invoicedDate: invoiceForm.invoicedDate || null
      });
      showToast('Marked as Invoiced', 'success');
      setModal(null); loadDetail(detail.id, false);
    } catch (e) { showToast('Failed: ' + getErrorMsg(e)); }
    setSaving(false);
  };
  const openMarkPaid = (b) => {
    setPaidForm({ id: b.id, paidDate: new Date().toISOString().split('T')[0] });
    setModal('paid');
  };
  const submitMarkPaid = async () => {
    setSaving(true);
    try {
      await rdProjectsApi.markBillingPaid(paidForm.id, { paidDate: paidForm.paidDate || null });
      showToast('Marked as Paid', 'success');
      setModal(null); loadDetail(detail.id, false);
    } catch (e) { showToast('Failed: ' + getErrorMsg(e)); }
    setSaving(false);
  };
  const writeOffBilling = async (id, asCancelled = false) => {
    const label = asCancelled ? 'cancelled' : 'written off';
    const reason = window.prompt(`Reason for being ${label}? (optional)`);
    if (reason === null) return;
    try {
      await rdProjectsApi.writeOffBilling(id, { cancelled: asCancelled, reason });
      showToast(`Marked ${label}`, 'success');
      loadDetail(detail.id, false);
    } catch (e) { showToast('Failed: ' + getErrorMsg(e)); }
  };
  const billingStatusBadge = (s) => {
    const map = {
      Draft: 'bg-slate-100 text-slate-700',
      Invoiced: 'bg-amber-100 text-amber-700',
      Paid: 'bg-emerald-100 text-emerald-700',
      WrittenOff: 'bg-red-100 text-red-700',
      Cancelled: 'bg-slate-200 text-slate-600'
    };
    return map[s] || 'bg-slate-100 text-slate-700';
  };
  const saveOutput = async () => {
    if (!outputForm.description?.trim()) { showToast('Output description is required', 'warning'); return; }
    setSaving(true);
    try {
      await rdProjectsApi.addOutput(detail.id, { ...outputForm, yearNumber: parseInt(outputForm.yearNumber) || activeYear });
      setModal(null); setOutputForm(emptyOutput); showToast('Output added', 'success'); loadDetail(detail.id, false);
    } catch (e) { showToast('Failed to add output: ' + getErrorMsg(e)); }
    setSaving(false);
  };

  // ── Phase A: Resource handlers ──
  const openResourceAdd = (yearNumber = activeYear) => {
    setResourceForm({
      id: null, yearNumber, roleRateId: '', assigneeName: '', assigneeUserId: '',
      plannedHours: 0, actualHours: 0, notes: ''
    });
    setModal('resource');
  };
  const openResourceEdit = (r) => {
    setResourceForm({
      id: r.id, yearNumber: r.yearNumber, roleRateId: r.roleRateId, assigneeName: r.assigneeName || '',
      assigneeUserId: r.assigneeUserId || '', plannedHours: r.plannedHours || 0,
      actualHours: r.actualHours || 0, notes: r.notes || ''
    });
    setModal('resource');
  };
  const saveResource = async () => {
    if (!resourceForm.roleRateId) { showToast('Select a role', 'warning'); return; }
    if (!resourceForm.assigneeName?.trim()) { showToast('Assignee name is required', 'warning'); return; }
    setSaving(true);
    try {
      const payload = {
        yearNumber: parseInt(resourceForm.yearNumber) || 1,
        roleRateId: resourceForm.roleRateId,
        assigneeName: resourceForm.assigneeName,
        assigneeUserId: resourceForm.assigneeUserId || null,
        plannedHours: parseFloat(resourceForm.plannedHours) || 0,
        actualHours: parseFloat(resourceForm.actualHours) || 0,
        notes: resourceForm.notes || null,
      };
      if (resourceForm.id) await rdProjectsApi.updateResource(resourceForm.id, payload);
      else await rdProjectsApi.createResource(detail.id, payload);
      setModal(null);
      showToast(resourceForm.id ? 'Resource updated' : 'Resource added', 'success');
      loadDetail(detail.id, false);
    } catch (e) { showToast('Failed: ' + getErrorMsg(e)); }
    setSaving(false);
  };
  const deleteResource = async (id) => {
    if (!window.confirm('Delete this resource?')) return;
    try {
      await rdProjectsApi.deleteResource(id);
      loadDetail(detail.id, false);
      showToast('Resource deleted', 'success');
    } catch (e) { showToast('Delete failed: ' + getErrorMsg(e)); }
  };

  // ── Phase A: helper — is a given year locked (Active/Closed)? ──
  const isYearLocked = (yearNumber) => {
    const y = (detail?.years || []).find(y => y.yearNumber === yearNumber);
    return y && (y.status === 'Active' || y.status === 'Closed' || y.status === 'ClosedEarly');
  };
  const yearStatusBadge = (status) => {
    const map = {
      Pending: 'bg-slate-100 text-slate-700',
      Active: 'bg-emerald-100 text-emerald-700',
      Closed: 'bg-violet-100 text-violet-700',
      ClosedEarly: 'bg-amber-100 text-amber-700',
    };
    return map[status] || 'bg-slate-100 text-slate-700';
  };

  // ══════════════════════════════════════════════════════
  // COMPUTED VALUES
  // ══════════════════════════════════════════════════════
  // Note: p.reference, p.principalInvestigator, p.trlStage, p.actualSpend
  // are all aliased by mapProjectFromApi in the API layer

  const filtered = useMemo(() => projects.filter(p => {
    if (filters.status && p.status !== filters.status) return false;
    if (filters.type && p.projectType !== filters.type) return false;
    if (filters.division && p.divisionId !== filters.division) return false;
    if (filters.trl && p.trlStage !== parseInt(filters.trl)) return false;
    // V3.3 — new dimensions
    if (filters.clusterId && p.clusterId !== filters.clusterId) return false;
    if (filters.researchThemeId && p.researchThemeId !== filters.researchThemeId) return false;
    if (filters.fundingSource && (p.fundingSource || '') !== filters.fundingSource) return false;
    if (filters.search) { const s = filters.search.toLowerCase(); if (!p.title?.toLowerCase().includes(s) && !(p.reference || '').toLowerCase().includes(s) && !(p.principalInvestigator || '').toLowerCase().includes(s)) return false; }
    return true;
  }), [projects, filters]);

  // V3.3 — Unique funding sources derived from loaded projects (FundingSource is
  // a free-text field, so we can't enumerate it up front). Empty string ignored.
  const fundingSources = useMemo(() => {
    const s = new Set();
    projects.forEach(p => { if (p.fundingSource && p.fundingSource.trim()) s.add(p.fundingSource.trim()); });
    return [...s].sort();
  }, [projects]);

  // V3.3 — Group the filtered list when filters.groupBy is set. Returns either
  // a flat array (no grouping) or an array of { key, label, items } sections.
  const grouped = useMemo(() => {
    if (!filters.groupBy) return null;
    const keyFns = {
      cluster:        (p) => ({ key: p.clusterId || '__none', label: p.cluster?.name || '— No cluster —' }),
      researchTheme:  (p) => ({ key: p.researchThemeId || '__none', label: p.researchTheme?.name || p.researchTheme || '— No theme —' }),
      fundingSource:  (p) => ({ key: p.fundingSource || '__none', label: p.fundingSource || '— No funding source —' })
    };
    const fn = keyFns[filters.groupBy];
    if (!fn) return null;
    const map = new Map();
    filtered.forEach(p => {
      const { key, label } = fn(p);
      if (!map.has(key)) map.set(key, { key, label, items: [] });
      map.get(key).items.push(p);
    });
    return [...map.values()].sort((a, b) => {
      if (a.key === '__none') return 1;
      if (b.key === '__none') return -1;
      return a.label.localeCompare(b.label);
    });
  }, [filtered, filters.groupBy]);

  const stats = useMemo(() => ({
    total: projects.length,
    active: projects.filter(p => ['Active', 'Approved', 'InProgress'].includes(p.status)).length,
    draft: projects.filter(p => p.status === 'Draft').length,
    underReview: projects.filter(p => ['Submitted', 'UnderInternalReview', 'UnderExternalReview', 'UnderDMReview', 'UnderAdditionalReview'].includes(p.status)).length,
    totalBudget: projects.reduce((s, p) => s + (p.totalBudget || 0), 0),
    totalSpend: projects.reduce((s, p) => s + (p.actualSpend || 0), 0),
  }), [projects]);

  // V3.5 — Edit availability loosened. Previously only Draft/RevisionsRequired
  // could be edited, which blocked legitimate corrections on Internal-Approved
  // or Active projects. Now editable for all non-terminal statuses; the openEdit
  // helper warns the user when editing a project that has been approved.
  const TERMINAL_STATUSES = ['Completed', 'Cancelled', 'Declined'];
  const isEditable = detail && !TERMINAL_STATUSES.includes(detail.status);
  const isPostApproval = detail && !['Draft', 'RevisionsRequired'].includes(detail.status) && !TERMINAL_STATUSES.includes(detail.status);
  const isReviewable = detail && ['Submitted', 'UnderInternalReview', 'UnderExternalReview', 'UnderDMReview', 'UnderAdditionalReview'].includes(detail.status);
  const canSubmit = detail && (detail.status === 'Draft' || detail.status === 'RevisionsRequired');

  const DETAIL_TABS = useMemo(() => {
    if (!detail) return [];
    const tabs = [
      { key: 'overview', label: 'Overview', icon: Eye },
      { key: 'motivation', label: detail.projectType === 'Commercial' ? 'Project Form' : 'Motivation', icon: ClipboardList },
      { key: 'approval', label: 'Approval History', icon: Shield },
      { key: 'milestones', label: 'Milestones', icon: Flag, count: (detail.milestones || []).length },
      { key: 'resources', label: 'Resources', icon: Users, count: (detail.resources || []).length },
      { key: 'costs', label: 'Project Costs', icon: DollarSign },
      // ── Phase C: attachments + extensions + billing ──
      { key: 'attachments', label: 'Attachments', icon: FileText, count: ((detail.documents || []).length + (detail.deliverables || []).length) },
      { key: 'extensions', label: 'Extensions', icon: ArrowRight, count: (detail.scopeExtensions || []).length },
      { key: 'linkage', label: 'Strategy', icon: Target, count: (detail.strategicObjectives || []).length },
      // ── V3.3: Grants tab ──
      { key: 'grants', label: 'Grants', icon: Award, count: projectGrants.length },
    ];
    // Billing only for Commercial / Contract
    if (detail.projectType === 'Commercial' || detail.projectType === 'Contract') {
      // Insert Billing after Attachments
      const attIdx = tabs.findIndex(t => t.key === 'attachments');
      tabs.splice(attIdx + 1, 0, { key: 'billing', label: 'Billing', icon: Award, count: (detail.billingMilestones || []).length });
    }
    if (['Active', 'Approved', 'InProgress', 'Completed'].includes(detail.status)) tabs.splice(3, 0, { key: 'execution', label: 'Execution', icon: Zap });
    return tabs;
  }, [detail, projectGrants]);

  const isResearch = form.projectType === 'Research';
  const isCommercial = form.projectType === 'Commercial' || form.projectType === 'Contract';

  // ══════════════════════════════════════════════════════
  // FORM SECTION RENDERER
  // ══════════════════════════════════════════════════════

  const u = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const renderFormSection = (num) => {
    switch (num) {
      case 1: return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><label className={label}>Project Title *</label><input className={input} value={form.title} onChange={u('title')} placeholder="Enter project title (max 200 characters)" maxLength={200} /></div>
          <div><label className={label}>Principal Investigator (PI) *</label><input className={input} value={form.principalInvestigator} onChange={u('principalInvestigator')} placeholder="Full name" /></div>
          <div><label className={label}>PI Email</label><input className={input} type="email" value={form.piEmail} onChange={u('piEmail')} placeholder="email@mintek.co.za" /></div>
          {/* V3.5 — Cluster moved from "Funding Request Summary" into Project Identification.
                     Selecting a cluster filters the Divisions dropdown to only show divisions
                     belonging to that cluster. Each Division entity carries a ClusterId. */}
          <div>
            <label className={label}>Cluster</label>
            <select className={input} value={form.clusterId || ''}
                    onChange={(e) => {
                      const newClusterId = e.target.value;
                      setForm(prev => {
                        // If the currently selected division doesn't belong to the new
                        // cluster, clear it so the user must reselect.
                        const currentDiv = divisions.find(d => d.id === prev.divisionId);
                        const divStillValid = !newClusterId || (currentDiv && currentDiv.clusterId === newClusterId);
                        return { ...prev, clusterId: newClusterId, divisionId: divStillValid ? prev.divisionId : '' };
                      });
                    }}>
              <option value="">— Select cluster —</option>
              {clusters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Division / Business Unit *{form.clusterId && <span className="text-slate-400 normal-case font-normal ml-1">(filtered by cluster)</span>}</label>
            <select className={input} value={form.divisionId} onChange={u('divisionId')}>
              <option value="">— Select Division —</option>
              {divisions
                .filter(d => !form.clusterId || d.clusterId === form.clusterId)
                .map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {form.clusterId && divisions.filter(d => d.clusterId === form.clusterId).length === 0 && (
              <p className="text-[10px] text-amber-600 mt-1">No divisions are mapped to this cluster yet — ask admin to assign divisions in Admin Setup.</p>
            )}
          </div>
          <div><label className={label}>Divisional Manager</label><input className={input} value={form.divisionalManager} onChange={u('divisionalManager')} /></div>
          <div><label className={label}>Financial Year *</label><select className={input} value={form.financialYear} onChange={u('financialYear')}>{[2025,2026,2027,2028].map(y=><option key={y} value={y}>FY {y-1}/{y%100}</option>)}</select></div>
          <div><label className={label}>Co-Investigators</label><textarea className={input + ' h-16'} value={form.coInvestigators} onChange={u('coInvestigators')} placeholder="Names and institutions" /></div>
          {isCommercial && <>
            <div><label className={label}>Client Name *</label><input className={input} value={form.clientName} onChange={u('clientName')} placeholder="External client" /></div>
            <div><label className={label}>Client Contact Email</label><input className={input} type="email" value={form.clientContactEmail} onChange={u('clientContactEmail')} /></div>
            <div><label className={label}>Confidentiality Level</label><select className={input} value={form.confidentialityLevel} onChange={u('confidentialityLevel')}>{CONFIDENTIALITY.map(c=><option key={c}>{c}</option>)}</select></div>
          </>}
        </div>
      );
      case 2: return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(() => {
            const multiYear = parseInt(form.totalYears) > 1;
            const perYearBudgets = (form.yearPlans || []).filter(p => p.plannedBudget !== '' && p.plannedBudget != null);
            const usingYearBudget = multiYear && perYearBudgets.length > 0;
            const autoSum = perYearBudgets.reduce((s, p) => s + (parseFloat(p.plannedBudget) || 0), 0);
            return usingYearBudget ? (
              <div>
                <label className={label}>{isResearch ? 'Total Amount Requested (ZAR)' : 'Total Project Value (excl. VAT)'} <span className="text-[10px] text-slate-400 font-normal">auto-calculated from year plans</span></label>
                <input className={input+' bg-slate-100 cursor-not-allowed'} type="number" value={autoSum} readOnly disabled />
              </div>
            ) : (
              <div>
                <label className={label}>{isResearch ? 'Total Amount Requested (ZAR) *' : 'Total Project Value (excl. VAT) *'}</label>
                <input className={input} type="number" value={form.totalBudget} onChange={u('totalBudget')} placeholder="0.00" />
              </div>
            );
          })()}
          {isResearch && <div><label className={label}>Funding Category *</label><select className={input} value={form.fundingCategory} onChange={u('fundingCategory')}><option value="">— Select —</option>{FUNDING_CATEGORIES.map(c=><option key={c}>{c}</option>)}</select></div>}
          {isCommercial && <div><label className={label}>Cost Centre Code</label><input className={input} value={form.costCentreCode} onChange={u('costCentreCode')} /></div>}
          <div><label className={label}>Project Duration (Years) *</label><input className={input} type="number" min="1" max="10" value={form.totalYears} onChange={u('totalYears')} /></div>
          <div><label className={label}>Priority</label><select className={input} value={form.priority} onChange={u('priority')}>{PRIORITIES.map(p=><option key={p}>{p}</option>)}</select></div>
          <div><label className={label}>Project Start Date *</label><input className={input} type="date" value={form.startDate} onChange={u('startDate')} /></div>
          <div><label className={label}>Project End Date {form.startDate && form.totalYears ? '(auto-calculated)' : '*'}</label><input className={input} type="date" value={form.endDate} onChange={e => setForm(prev => ({ ...prev, endDate: e.target.value, _autoEndDate: false }))} /></div>
          {parseInt(form.totalYears) > 1 && form.financialYear && (
            <div className="md:col-span-2">
              <label className={label}>Year-by-Year Plan</label>
              <p className="text-[10px] text-slate-500 mb-2">Capture the expected outcome and planned budget for each year. Resources and running cost lines are added per year on the detail tabs after creation.</p>
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left font-bold text-[10px] text-slate-500 uppercase w-14">Year</th>
                      <th className="px-3 py-2 text-left font-bold text-[10px] text-slate-500 uppercase w-28">FY</th>
                      <th className="px-3 py-2 text-left font-bold text-[10px] text-slate-500 uppercase">Expected Outcome</th>
                      <th className="px-3 py-2 text-right font-bold text-[10px] text-slate-500 uppercase w-40">Planned Budget (R)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.yearPlans || []).map((yp, idx) => (
                      <tr key={yp.yearNumber} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-mono text-[11px] font-bold text-slate-700">{yp.yearNumber}</td>
                        <td className="px-3 py-2 text-slate-600 text-[11px]">FY {yp.financialYear - 1}/{yp.financialYear % 100}</td>
                        <td className="px-3 py-2">
                          <textarea
                            className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:border-[#c8102e] focus:outline-none resize-none"
                            rows={2}
                            value={yp.expectedOutcome}
                            placeholder="What this year will deliver"
                            onChange={e => {
                              const v = e.target.value;
                              setForm(prev => ({
                                ...prev,
                                yearPlans: (prev.yearPlans || []).map((p, i) => i === idx ? { ...p, expectedOutcome: v } : p)
                              }));
                            }}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-full px-2 py-1.5 text-xs text-right font-mono border border-slate-200 rounded-md focus:border-[#c8102e] focus:outline-none"
                            value={yp.plannedBudget}
                            placeholder="0.00"
                            onChange={e => {
                              const v = e.target.value;
                              setForm(prev => ({
                                ...prev,
                                yearPlans: (prev.yearPlans || []).map((p, i) => i === idx ? { ...p, plannedBudget: v } : p)
                              }));
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {(form.yearPlans || []).some(p => p.plannedBudget !== '' && p.plannedBudget != null) && (
                    <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                      <tr>
                        <td colSpan={3} className="px-3 py-2 text-right text-[10px] font-bold text-slate-500 uppercase">Total (auto-sum):</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-sm text-slate-800">
                          {fmtR((form.yearPlans || []).reduce((s, p) => s + (parseFloat(p.plannedBudget) || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={label}>Research Theme {researchThemes.length === 0 && <span className="text-amber-500 text-[10px] ml-1">(ask admin to set up themes)</span>}</label>
              <select
                className={input}
                value={form.researchThemeId || ''}
                onChange={(e) => {
                  const id = e.target.value;
                  const theme = researchThemes.find(t => t.id === id);
                  setForm(prev => ({ ...prev, researchThemeId: id, researchTheme: theme?.name || '' }));
                }}
              >
                <option value="">— Select theme —</option>
                {researchThemes.map(t => (
                  <option key={t.id} value={t.id}>{t.code ? `${t.code} · ${t.name}` : t.name}</option>
                ))}
              </select>
            </div>
            {/* V3.5 — Cluster removed from this section; it lives in Project Identification (Step 1) now */}
          </div>
          {isResearch && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={label}>Funding Source</label>
                <select className={input} value={form.fundingSource || ''} onChange={u('fundingSource')}>
                  <option value="">— Select source —</option>
                  {FUNDING_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><label className={label}>Prior Mintek Grant?</label><select className={input} value={form.priorGrant || ''} onChange={u('priorGrant')}><option value="">—</option><option>Yes</option><option>No</option></select></div>
            </div>
          )}
        </div>
      );
      case 3: return isResearch ? (
        <div>
          <label className={label}>Executive Summary * <span className="normal-case font-normal">(max 300 words)</span></label>
          <textarea className={input + ' h-40'} value={form.executiveSummary} onChange={u('executiveSummary')} placeholder="Provide a concise summary of the project..." />
          <p className={`text-[10px] mt-1 ${wordCount(form.executiveSummary) > 300 ? 'text-red-500 font-bold' : 'text-slate-400'}`}>{wordCount(form.executiveSummary)} / 300 words</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div><label className={label}>Project Summary / Scope *</label><textarea className={input+' h-24'} value={form.scopeDescription} onChange={u('scopeDescription')} placeholder="Scope description (max 500 words)" /><p className={`text-[10px] mt-1 ${wordCount(form.scopeDescription) > 500 ? 'text-red-500 font-bold' : 'text-slate-400'}`}>{wordCount(form.scopeDescription)} / 500 words</p></div>
          <div><label className={label}>Exclusions / Out of Scope</label><textarea className={input+' h-16'} placeholder="Items explicitly excluded..." /></div>
        </div>
      );
      case 4: return isResearch ? (
        <div className="space-y-4">
          <div><label className={label}>Problem Statement *</label><textarea className={input+' h-24'} value={form.problemStatement} onChange={u('problemStatement')} placeholder="Research problem or knowledge gap..." /></div>
          <div><label className={label}>Significance & Relevance to Mintek's Mandate</label><textarea className={input+' h-20'} value={form.mandateAlignment} onChange={u('mandateAlignment')} placeholder="Strategic alignment..." /></div>
          {/* V3.1 — Motivation Topics now sourced from admin-managed list (was hardcoded GOVT_PRIORITIES).
              Falls back to the legacy list if the topics endpoint hasn't been populated yet. */}
          {(() => {
            const useDynamic = motivationTopics.length > 0;
            const selected = form.govtPriorities || [];
            const toggle = (name) => setForm(prev => ({
              ...prev,
              govtPriorities: (prev.govtPriorities || []).includes(name)
                ? prev.govtPriorities.filter(x => x !== name)
                : [...(prev.govtPriorities || []), name]
            }));

            if (!useDynamic) {
              return (
                <div>
                  <div className="flex items-center justify-between"><label className={label}>Alignment to Government Priorities</label><span className="text-[9px] text-amber-600">(default list — ask admin to set up Motivation Topics)</span></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">{GOVT_PRIORITIES.map(gp => (
                    <label key={gp} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-slate-50 border ${selected.includes(gp) ? 'border-[#c8102e] bg-red-50/30' : 'border-slate-200'}`}>
                      <input type="checkbox" checked={selected.includes(gp)} onChange={() => toggle(gp)} className="rounded" />
                      <span className="text-xs text-slate-700">{gp}</span>
                    </label>
                  ))}</div>
                </div>
              );
            }

            // Group topics by category
            const grouped = motivationTopics.reduce((acc, t) => {
              const k = t.category || 'General';
              (acc[k] = acc[k] || []).push(t);
              return acc;
            }, {});
            const orderedCats = Object.keys(grouped).sort();

            return (
              <div>
                <label className={label}>Motivation Topics</label>
                <p className="text-[10px] text-slate-400 mt-0.5 mb-2">Tick all topics this project addresses. Topics are managed by the administrator.</p>
                {orderedCats.map(cat => (
                  <div key={cat} className="mb-3 last:mb-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#a30d24]">{cat}</span>
                      <div className="flex-1 h-px bg-slate-200" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {grouped[cat].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)).map(t => (
                        <label key={t.id} title={t.description || ''} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-slate-50 border ${selected.includes(t.name) ? 'border-[#c8102e] bg-red-50/30' : 'border-slate-200'}`}>
                          <input type="checkbox" checked={selected.includes(t.name)} onChange={() => toggle(t.name)} className="rounded" />
                          <span className="text-xs text-slate-700">{t.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          <div><label className={label}>Innovation & Novelty</label><textarea className={input+' h-20'} value={form.innovationNovelty} onChange={u('innovationNovelty')} placeholder="What is new or unique?" /></div>
        </div>
      ) : (
        <div className="bg-slate-50 rounded-xl p-6 text-center"><Flag size={32} className="mx-auto text-slate-300 mb-2" /><p className="text-sm text-slate-500">Milestones managed in the project detail view after creation</p></div>
      );
      case 5: return isResearch ? (
        <div className="space-y-4">
          <div><label className={label}>Research Objectives (SMART)</label><textarea className={input+' h-20'} value={form.description} onChange={u('description')} placeholder="List SMART objectives..." /></div>
          <div><label className={label}>Methodology</label><textarea className={input+' h-24'} value={form.methodology} onChange={u('methodology')} placeholder="Research approach, methods, materials..." /></div>
        </div>
      ) : (
        <div className="bg-slate-50 rounded-xl p-6 text-center"><DollarSign size={32} className="mx-auto text-slate-300 mb-2" /><p className="text-sm text-slate-500">Financial tracking managed in the Financial tab</p></div>
      );
      case 6: return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={label}>Current TRL (1–9)</label><select className={input} value={form.trlStage} onChange={u('trlStage')}><option value="">— Select —</option>{TRL_LEVELS.map(t=><option key={t} value={t}>TRL-{t}: {TRL_NAMES[t]}</option>)}</select></div>
            <div><label className={label}>Target TRL</label><select className={input} value={form.trlTarget} onChange={u('trlTarget')}><option value="">— Select —</option>{TRL_LEVELS.map(t=><option key={t} value={t}>TRL-{t}: {TRL_NAMES[t]}</option>)}</select></div>
          </div>
          <div><label className={label}>Socio-Economic Impact</label><textarea className={input+' h-20'} value={form.socioeconomicImpact} onChange={u('socioeconomicImpact')} placeholder="Potential impact..." /></div>
          <div><label className={label}>Commercialisation Potential</label><textarea className={input+' h-16'} value={form.commercialisationPotential} onChange={u('commercialisationPotential')} placeholder="Commercial pathway..." /></div>
        </div>
      );
      case 7: return isResearch ? (
        <div className="space-y-4">
          <div><label className={label}>Existing Infrastructure & Equipment</label><textarea className={input+' h-16'} value={form.existingInfrastructure} onChange={u('existingInfrastructure')} placeholder="Available facilities..." /></div>
          <div><label className={label}>Additional Resources Required</label><textarea className={input+' h-16'} value={form.additionalResources} onChange={u('additionalResources')} placeholder="Equipment to procure..." /></div>
        </div>
      ) : renderStrategyLinkage();
      case 8: return (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">{isResearch ? 'Minimum 3 register risks required before submission.' : 'Minimum 2 register risks required.'}</p>
          <ProjectRiskPicker rows={form.risks || []} onChange={(rows) => setForm(f => ({ ...f, risks: rows }))} />
        </div>
      );
      case 9: {
        const COMPLIANCE_QUESTIONS = [
          'Does this research involve human subjects?',
          'Does this research involve hazardous materials?',
          'Are there existing IP rights affected?',
          'Has IP disclosure been completed?',
          'Are there export-controlled technologies?',
          'Does the project involve foreign collaboration?',
        ];
        return isResearch ? (
        <div className="space-y-3">{COMPLIANCE_QUESTIONS.map((q,i)=>(<div key={i} className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-lg"><span className="text-xs text-slate-700 flex-1">{q}</span><select className="px-2 py-1 border border-slate-200 rounded text-xs w-20" value={(form.complianceAnswers||{})[i]||''} onChange={e => setForm(f => ({...f, complianceAnswers: {...(f.complianceAnswers||{}), [i]: e.target.value}}))}><option value="">—</option><option>Yes</option><option>No</option></select></div>))}</div>
      ) : (<div><label className={label}>Project Team</label><p className="text-[10px] text-slate-400">Team members managed on the detail view</p></div>);
      }
      case 10: return isResearch ? (
        <div><label className={label}>Key References (up to 10)</label><textarea className={input+' h-32'} value={form.keyReferences} onChange={u('keyReferences')} placeholder="Harvard or Vancouver style..." /></div>
      ) : (<div className="bg-slate-50 rounded-xl p-6 text-center"><FileText size={32} className="mx-auto text-slate-300 mb-2" /><p className="text-sm text-slate-500">Client survey settings configured after approval</p></div>);
      case 11: return renderStrategyLinkage();
      default: return null;
    }
  };

  const renderStrategyLinkage = () => (
    <div className="space-y-4">
      <div className="bg-red-50/30 rounded-xl p-4 border border-red-100">
        <p className="text-xs font-bold text-[#c8102e] uppercase mb-3 flex items-center gap-1.5"><Target size={14}/>Strategy & KPI Linkage</p>
        <p className="text-[10px] text-slate-500 mb-3">Strategic Objectives and Programmes are mandatory. KPI linkage is optional — pick the KPIs this project contributes to.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MultiSelect label="Strategic Objectives *" options={objectives} value={form.selectedSOs} onChange={v=>setForm(f=>({...f,selectedSOs:v}))} renderOption={o=>`${o.reference} — ${o.title}`} />
          <MultiSelect label="Programmes *" options={programmes} value={form.selectedProgrammes} onChange={v=>setForm(f=>({...f,selectedProgrammes:v}))} renderOption={o=>`${o.reference} — ${o.title}`} />
          <MultiSelect label="KPIs (optional)" options={kpis} value={form.selectedKpis||[]} onChange={v=>setForm(f=>({...f,selectedKpis:v}))} renderOption={o=>`${o.reference||''} — ${o.name}`} />
        </div>
      </div>
      <div className="bg-slate-50 rounded-xl p-4"><p className="rd-overview-section-title">Additional Strategy Tags <span className="normal-case font-normal text-red-500">(at least one required for submission)</span></p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className={label}>Mintek Strategic Plan Theme</label><input className={input} value={form.mintekTheme} onChange={u('mintekTheme')} placeholder="e.g., Minerals beneficiation" /></div>
          <div><label className={label}>DST&I Priority Area</label><input className={input} value={form.dstPriority} onChange={u('dstPriority')} placeholder="e.g., Advanced manufacturing" /></div>
          <div><label className={label}>MPRDA Mandate Area</label><input className={input} value={form.mprdaMandate} onChange={u('mprdaMandate')} placeholder="e.g., Mineral resources R&D" /></div>
          <div><label className={label}>NDP / Government Priority</label><input className={input} value={form.ndpPriority} onChange={u('ndpPriority')} placeholder="e.g., Economic transformation" /></div>
        </div>
      </div>
    </div>
  );

  const formSections = [
    { num:1, title:'Project Identification', icon:Hash, required:true },
    { num:2, title:isResearch?'Funding Request Summary':'Commercial Details & Scope', icon:DollarSign, required:true },
    { num:3, title:isResearch?'Executive Summary':'Project Objectives & Scope', icon:BookOpen, required:true },
    { num:4, title:isResearch?'Research Motivation':'Milestones & Workplan', icon:Microscope, required:true },
    { num:5, title:isResearch?'Objectives & Methodology':'Financial Plan', icon:Target, required:true },
    { num:6, title:'Expected Outcomes & TRL', icon:Award, required:true },
    { num:7, title:isResearch?'Team & Resources':'Strategy Linkage', icon:Users, required:true },
    { num:8, title:'Risk Assessment', icon:Shield, required:true },
    { num:9, title:isResearch?'Ethics, IP & Compliance':'Team & Resources', icon:Shield, required:isResearch },
    { num:10, title:isResearch?'Key References':'Survey Settings', icon:BookOpen, required:false },
    { num:11, title:'Strategy Linkage & Tags', icon:Globe, required:true },
  ];

  // ══════════════════════════════════════════════════════
  // EXCEL EXPORT
  // ══════════════════════════════════════════════════════

  const exportExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const data = filtered.map(p => ({ Reference:p.reference, Title:p.title, Type:p.projectType, Division:p.division?.name||'', Status:STATUS_LABELS[p.status]||p.status, TRL:p.trlStage?`TRL-${p.trlStage}`:'', 'Budget (R)':p.totalBudget||0, PI:p.principalInvestigator||'', Start:p.startDate?new Date(p.startDate).toLocaleDateString('en-ZA'):'', End:p.endDate?new Date(p.endDate).toLocaleDateString('en-ZA'):'' }));
      const ws = XLSX.utils.json_to_sheet(data); const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'R&D Projects'); XLSX.writeFile(wb, `SiloGate_RD_FY${filters.fy}.xlsx`);
    } catch {
      const csv = [['Reference','Title','Type','Status','PI','Budget'].join(','), ...filtered.map(p=>[p.reference,`"${p.title}"`,p.projectType,p.status,p.principalInvestigator,p.totalBudget].join(','))].join('\n');
      const b=new Blob([csv],{type:'text/csv'});const a=document.createElement('a');a.href=URL.createObjectURL(b);a.download='RD_Projects.csv';a.click();
    }
  };

  // ══════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════

  return (
    <>
    <LoadingOverlay show={detailLoading} message="Loading project..." />
    <style>{`
      .rd-projects-module{
        --sg-red:#97051f;--sg-red2:#b40b2a;--sg-red3:#d61033;--sg-dark:#1f242c;--sg-charcoal:#303640;--sg-mid:#7d838c;
        color:#111827;font-size:12px;line-height:1.25;letter-spacing:-.01em;
      }
      .rd-projects-module *{box-sizing:border-box;}
      .rd-projects-header h1{color:#7f1224;font-weight:900;letter-spacing:-.035em;font-size:23px!important;}
      .rd-projects-header .lucide-flask-conical{color:#7f1224!important;stroke-width:2.4;}
      .rd-projects-header select,.rd-projects-header button,.rd-project-filters select,.rd-project-filters>div{border-radius:10px!important;box-shadow:0 5px 14px rgba(20,25,35,.08);}
      .rd-projects-module button[class*="bg-[#c8102e]"],.rd-projects-module button[class*="bg-emerald-500"]{background:linear-gradient(180deg,#b40b2a 0%,#87051d 100%)!important;border:1px solid rgba(105,0,20,.35)!important;box-shadow:0 8px 18px rgba(151,5,31,.24)!important;color:#fff!important;}

      /* Project front page / portfolio dashboard */
      .rd-stat-card{background:linear-gradient(135deg,#ffffff 0%,#f4f6f9 58%,#e1e5eb 100%)!important;border:1px solid #c9d0d8!important;border-radius:15px!important;box-shadow:0 8px 18px rgba(25,30,40,.10);position:relative;overflow:hidden;min-height:74px;padding:16px!important;display:flex;align-items:flex-start;justify-content:space-between;}
      .rd-stat-card::after{content:"";position:absolute;left:0;right:0;bottom:0;height:4px;background:#97051f;box-shadow:0 -2px 9px rgba(151,5,31,.35);}
      .rd-stat-card:nth-child(5){background:linear-gradient(135deg,#e3183d 0%,#c8102e 52%,#9d071f 100%)!important;color:#fff!important;border-color:#c8102e!important;}
      .rd-stat-card:nth-child(5)::after{background:rgba(255,255,255,.4)}
      .rd-stat-card:nth-child(5) .rd-stat-label,.rd-stat-card:nth-child(5) .rd-stat-value,.rd-stat-card:nth-child(5) .rd-stat-icon{color:#fff!important;}
      .rd-stat-label{color:#5b6470!important;font-size:9px!important;font-weight:900;text-transform:uppercase;letter-spacing:.05em;}
      .rd-stat-value{font-size:23px!important;color:#7f1224!important;font-weight:950!important;margin-top:4px;}
      .rd-stat-icon{color:#7e8794!important;opacity:.55;}
      .rd-project-filters input,.rd-project-filters select{background:#fff!important;border:1px solid #cfd5dd!important;border-radius:12px!important;height:38px!important;}
      .rd-projects-module .lg\:col-span-1>div[class*="rounded-2xl"],.rd-projects-module .lg\:col-span-2>div[class*="rounded-2xl"]{border:1px solid #b8c0ca!important;border-radius:18px!important;box-shadow:0 8px 18px rgba(22,28,38,.14)!important;background:linear-gradient(135deg,#ffffff 0%,#f7f8fa 44%,#d8dde4 100%)!important;}
      .rd-projects-module .lg\:col-span-1>div[class*="ring-2"]{box-shadow:0 0 0 2px #b58cff inset,0 8px 18px rgba(80,20,130,.12)!important;}
      .rd-executive-page .rd-project-workspace>div,.rd-executive-view .bg-white{background:linear-gradient(135deg,#ffffff 0%,#f5f6f8 46%,#e0e4e9 100%)!important;border:1px solid #c7cdd5!important;border-radius:16px!important;box-shadow:0 7px 16px rgba(22,28,38,.10)!important;}
      .rd-executive-page h3,.rd-executive-view h3{color:#111827!important;font-weight:900!important;}

      /* Detail shell */
      .rd-detail-card{border-radius:22px!important;border:1px solid #aeb6c1!important;box-shadow:0 12px 28px rgba(22,28,38,.18)!important;overflow:hidden;background:#fff!important;}
      .rd-detail-card>.p-5:first-child{background:linear-gradient(135deg,#ffffff 0%,#f6f7f9 42%,#d0d5dc 100%)!important;padding:22px 24px 16px!important;}
      .rd-active-tab-milestones .rd-detail-card>.p-5:first-child,.rd-active-tab-extensions .rd-detail-card>.p-5:first-child{background:linear-gradient(145deg,#2a2e36 0%,#1d2027 45%,#11141a 100%)!important;color:#fff!important;position:relative;overflow:hidden;}
      .rd-active-tab-milestones .rd-detail-card>.p-5:first-child::after,.rd-active-tab-extensions .rd-detail-card>.p-5:first-child::after{content:"";position:absolute;right:38%;bottom:-1px;width:360px;height:130px;background:#f5f6f8;border-radius:55% 55% 0 0/100% 100% 0 0;transform:translateY(40%);box-shadow:-35px -18px 34px rgba(151,5,31,.18);}
      .rd-active-tab-milestones .rd-detail-card>.p-5:first-child>* , .rd-active-tab-extensions .rd-detail-card>.p-5:first-child>*{position:relative;z-index:1;}
      .rd-active-tab-milestones .rd-detail-card h2,.rd-active-tab-extensions .rd-detail-card h2{color:#fff!important;}
      .rd-detail-card h2{font-size:20px!important;font-weight:900!important;color:#0f172a!important;}
      .rd-detail-card .font-mono.text-\[\#c8102e\],.rd-detail-card span.font-mono{color:#8d0b20!important;font-weight:950!important;}

      /* Stage tracker */
      .rd-stage-track{display:flex;align-items:center;width:100%;gap:10px;overflow-x:auto;padding:14px 10px 4px;}
      .rd-stage-node{display:flex;flex-direction:column;align-items:center;gap:7px;min-width:86px;position:relative;z-index:2;}
      .rd-stage-dot{width:42px;height:42px;border-radius:999px;display:flex;align-items:center;justify-content:center;background:linear-gradient(145deg,#f2f3f5,#d3d7dd);color:#8a929d;border:1px solid rgba(255,255,255,.8);box-shadow:inset 2px 2px 7px rgba(255,255,255,.9),inset -3px -3px 8px rgba(72,79,90,.18),0 8px 15px rgba(22,28,38,.16);}
      .rd-stage-node.is-past .rd-stage-dot{background:linear-gradient(180deg,#c60f31,#8d071f);color:#fff;box-shadow:inset 1px 1px 4px rgba(255,255,255,.55),0 0 0 4px rgba(151,5,31,.10),0 7px 16px rgba(151,5,31,.28);}
      .rd-stage-node.is-current .rd-stage-dot{width:54px;height:54px;background:radial-gradient(circle at 38% 35%,#666d76 0%,#3f4650 52%,#262b33 100%);color:#fff;border:8px solid rgba(151,5,31,.25);box-shadow:0 0 0 4px rgba(151,5,31,.12),0 0 26px rgba(151,5,31,.42),inset 0 0 10px rgba(255,255,255,.2);}
      .rd-stage-label{font-size:10px;font-weight:800;color:#111827;text-align:center;white-space:nowrap;}
      .rd-active-tab-milestones .rd-stage-label,.rd-active-tab-extensions .rd-stage-label{color:#d9dde3;}.rd-stage-node.is-current .rd-stage-label{color:#8b0b20!important;}
      .rd-stage-line{height:4px;min-width:55px;flex:1;background:#d8dde4;border-radius:999px;box-shadow:inset 0 1px 2px rgba(0,0,0,.08);transform:translateY(-12px);}
      .rd-stage-line.is-filled{background:linear-gradient(90deg,#a00621,#c8102e 75%,#e7d0d5);box-shadow:0 0 8px rgba(151,5,31,.28);}

      /* Main project tabs */
      .rd-detail-card .rd-detail-tabs{background:linear-gradient(180deg,#ffffff 0%,#f3f4f6 100%)!important;border-top:1px solid #cfd4dc;border-bottom:1px solid #cfd4dc;padding:0 20px!important;gap:18px!important;align-items:center;min-height:58px;overflow-x:auto;}
      .rd-active-tab-costs .rd-detail-tabs,.rd-active-tab-linkage .rd-detail-tabs{background:linear-gradient(180deg,#505762 0%,#2f3540 55%,#20252d 100%)!important;border-color:#20252d!important;box-shadow:0 8px 18px rgba(0,0,0,.18);}
      .rd-active-tab-costs .rd-detail-tabs button,.rd-active-tab-linkage .rd-detail-tabs button{color:#e5e7eb!important;}
      .rd-detail-tabs button{font-size:15px!important;font-weight:600!important;padding:16px 0 14px!important;border-bottom:3px solid transparent!important;color:#202733!important;display:inline-flex!important;align-items:center!important;gap:8px!important;line-height:1!important;white-space:nowrap!important;border-radius:0!important;position:relative;background:transparent!important;box-shadow:none!important;border-left:0!important;border-right:0!important;border-top:0!important;}
      .rd-detail-tabs button svg{width:16px;height:16px;color:#6f7782;flex:0 0 auto;}
      .rd-detail-tabs .rd-tab-label{background:transparent!important;color:inherit!important;min-width:auto!important;height:auto!important;display:inline!important;margin:0!important;}
      .rd-detail-tabs .rd-tab-count{font-size:12px!important;min-width:25px;height:25px;display:inline-flex;align-items:center;justify-content:center;border-radius:999px!important;background:#d9dde3!important;color:#68707b!important;margin-left:2px!important;font-weight:800!important;}
      .rd-detail-tabs button.rd-tab-active{color:#8d0b20!important;font-weight:800!important;border-bottom-color:#97051f!important;}
      .rd-detail-tabs button.rd-tab-active svg{color:#8d0b20!important;}
      .rd-detail-tabs button.rd-tab-active .rd-tab-count{background:#d9dde3!important;color:#8d0b20!important;}
      .rd-active-tab-approval .rd-detail-tabs button.rd-tab-active,.rd-active-tab-milestones .rd-detail-tabs button.rd-tab-active,.rd-active-tab-extensions .rd-detail-tabs button.rd-tab-active,.rd-active-tab-resources .rd-detail-tabs button.rd-tab-active{background:linear-gradient(180deg,#b40b2a,#85051d)!important;color:#fff!important;border-radius:999px!important;border-bottom-color:#8f071f!important;box-shadow:0 8px 18px rgba(151,5,31,.25)!important;padding:11px 18px!important;margin:7px 0!important;}
      .rd-active-tab-approval .rd-detail-tabs button.rd-tab-active svg,.rd-active-tab-milestones .rd-detail-tabs button.rd-tab-active svg,.rd-active-tab-extensions .rd-detail-tabs button.rd-tab-active svg,.rd-active-tab-resources .rd-detail-tabs button.rd-tab-active svg{color:#fff!important;}
      .rd-active-tab-costs .rd-detail-tabs button.rd-tab-active{background:linear-gradient(180deg,#272c36,#171b22)!important;color:#fff!important;padding:20px 18px 17px!important;border-bottom-color:#97051f!important;box-shadow:inset 0 1px 0 rgba(255,255,255,.08),0 8px 18px rgba(0,0,0,.25)!important;}
      .rd-active-tab-costs .rd-detail-tabs button.rd-tab-active svg{color:#a20a24!important;}
      .rd-active-tab-linkage .rd-detail-tabs button.rd-tab-active{color:#b70d2c!important;border-bottom-color:#b70d2c!important;font-weight:900!important;}

      /* Tab content common */
      .rd-tab-panel{background:#fff;padding:24px!important;position:relative;}
      .rd-active-tab-linkage .rd-tab-panel{background:linear-gradient(145deg,#1d232b 0%,#0f151b 100%)!important;color:#e5e7eb;}
      .rd-tab-panel .bg-slate-50,.rd-tab-panel .bg-gradient-to-r{background:linear-gradient(135deg,#f7f8fa 0%,#e7ebef 100%)!important;border:1px solid #d0d5dd!important;border-radius:16px!important;box-shadow:0 5px 14px rgba(18,24,34,.08);}
      .rd-tab-panel thead tr{background:linear-gradient(180deg,#eef0f3,#dde1e7)!important;}
      .rd-tab-panel th{font-weight:800!important;color:#151922!important;text-transform:uppercase;font-size:11px!important;}
      .rd-tab-panel td{border-top:1px solid #e5e7eb;}
      .rd-tab-panel:after{content:"";position:absolute;right:22px;bottom:22px;width:58px;height:58px;background:rgba(255,255,255,.55);clip-path:polygon(50% 0,63% 35%,100% 50%,63% 65%,50% 100%,37% 65%,0 50%,37% 35%);opacity:.75;pointer-events:none;}

      /* Motivation design */
      .rd-active-tab-motivation .rd-tab-panel{padding:18px 20px 24px!important;}
      .rd-active-tab-motivation .rd-tab-panel>.space-y-2{display:grid!important;grid-template-columns:1fr 1fr!important;gap:18px 24px!important;align-items:start;}
      .rd-motivation-card{border:1px solid #8f98a4;border-radius:16px;background:#fff;box-shadow:0 6px 13px rgba(21,27,37,.15);overflow:hidden;min-height:84px;}
      .rd-motivation-card.is-open{grid-row:span 3;min-height:300px;background:linear-gradient(135deg,#f7f8fa 0%,#fff 45%,#e2e6ec 100%);}
      .rd-motivation-card-head{width:100%;display:flex;align-items:center;gap:14px;padding:18px 22px;background:linear-gradient(135deg,#fff 0%,#fafafa 55%,#edf0f4 100%)!important;border:0!important;box-shadow:none!important;color:#0b1220;text-align:left;}
      .rd-motivation-card.is-open .rd-motivation-card-head{background:linear-gradient(180deg,#e6e8ed,#c4c9d1)!important;border-bottom:1px solid #9aa2ad!important;}
      .rd-motivation-number{font-size:42px;line-height:.9;font-weight:950;color:#9f5c65;text-shadow:1px 1px 0 #5e1422,0 3px 6px rgba(0,0,0,.22);min-width:48px;}
      .rd-motivation-icon{width:34px;height:34px;border-radius:999px;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle,#47515b 0%,#2f3740 70%);color:#fff;border:2px solid #8a1024;box-shadow:0 3px 8px rgba(0,0,0,.22);}
      .rd-motivation-title{font-size:27px;font-weight:950;letter-spacing:-.035em;flex:1;color:#0b1220;}
      .rd-motivation-chevron{color:#7b828c;margin-left:auto;}
      .rd-motivation-body{padding:24px 28px 28px;font-size:18px;line-height:1.4;color:#111827;white-space:normal;}
      .rd-motivation-body p{font-size:18px!important;color:#111827!important;line-height:1.35!important;}
      .rd-required-chip{font-size:10px;font-weight:900;color:#97051f;background:#f3d5db;border-radius:999px;padding:4px 8px;}

      /* Approval history */
      .rd-active-tab-approval .rd-tab-panel>.space-y-3>p{font-size:14px!important;color:#6b7280!important;font-weight:900!important;letter-spacing:.02em;margin:4px 0 18px;}
      .rd-active-tab-approval .rd-tab-panel .space-y-2{position:relative;}
      .rd-active-tab-approval .rd-tab-panel .space-y-2>div{gap:18px!important;margin-bottom:12px;}
      .rd-active-tab-approval .rd-tab-panel .space-y-2>div>div:first-child{width:40px!important;height:40px!important;background:linear-gradient(180deg,#b40b2a,#87051d)!important;color:#fff!important;box-shadow:0 7px 16px rgba(151,5,31,.24);}
      .rd-active-tab-approval .rd-tab-panel .space-y-2>div>div:nth-child(2){background:linear-gradient(135deg,#f5f6f8 0%,#e9edf2 100%)!important;border-radius:17px!important;padding:16px!important;border:0!important;min-height:66px;box-shadow:none!important;}
      .rd-active-tab-approval .rd-tab-panel .space-y-2>div>div:nth-child(2) p:first-child{font-size:14px!important;font-weight:900!important;color:#111827!important;}
      .rd-active-tab-approval .rd-tab-panel .space-y-2>div>div:nth-child(2) p:nth-child(2){font-size:12px!important;color:#4b5563!important;}

      /* Milestones */
      .rd-active-tab-milestones .rd-tab-panel{background:#f7f8fa!important;padding:28px!important;}
      .rd-active-tab-milestones .rd-tab-panel .space-y-3>div:first-child p{font-size:14px!important;font-weight:900!important;color:#4b5563!important;}
      .rd-active-tab-milestones .rd-tab-panel .space-y-3>div:not(:first-child){background:linear-gradient(135deg,#e9ebef 0%,#c6cbd3 100%)!important;border:1px solid #8f98a4!important;border-radius:14px!important;min-height:78px;box-shadow:0 7px 14px rgba(18,24,34,.18), inset 0 1px 0 rgba(255,255,255,.65)!important;align-items:center!important;position:relative;overflow:hidden;}
      .rd-active-tab-milestones .rd-tab-panel .space-y-3>div:not(:first-child)::after{content:"";position:absolute;left:0;bottom:0;height:2px;width:20%;background:#e5e7eb;box-shadow:0 0 6px rgba(255,255,255,.8);}
      .rd-active-tab-milestones .rd-tab-panel .space-y-3>div:not(:first-child) .font-mono{font-size:16px!important;color:#97051f!important;background:transparent!important;}
      .rd-active-tab-milestones .rd-tab-panel .space-y-3>div:not(:first-child) span.text-sm{font-size:18px!important;font-weight:900!important;color:#111827!important;}

      /* Resources */
      .rd-active-tab-resources .rd-tab-panel{background:#fff!important;padding:28px!important;}
      .rd-active-tab-resources .rd-tab-panel h3,.rd-active-tab-resources .rd-tab-panel p.uppercase{font-size:20px!important;color:#111827!important;font-weight:500!important;}
      .rd-active-tab-resources .rd-tab-panel table{border-collapse:collapse!important;}
      .rd-active-tab-resources .rd-tab-panel thead tr{background:linear-gradient(180deg,#fff,#f7f7f9)!important;}
      .rd-active-tab-resources .rd-tab-panel th,.rd-active-tab-resources .rd-tab-panel td{font-size:14px!important;padding:14px 16px!important;}
      .rd-active-tab-resources .rd-tab-panel tbody tr{background:#fff!important;}
      .rd-active-tab-resources .rd-tab-panel .rounded-2xl{border-radius:18px!important;box-shadow:0 7px 16px rgba(18,24,34,.13)!important;overflow:hidden;}

      /* Costs */
      .rd-active-tab-costs .rd-tab-panel{padding:28px!important;background:#fff!important;}
      .rd-sub-tabs button{font-size:15px!important;font-weight:700!important;border-radius:8px 8px 0 0!important;padding:12px 20px!important;background:#eef0f4!important;color:#111827!important;box-shadow:0 6px 14px rgba(18,24,34,.12)!important;}
      .rd-sub-tabs button.active{background:linear-gradient(180deg,#eef1f5,#c7ccd4)!important;color:#8a1024!important;border-bottom:3px solid #97051f!important;}
      .rd-active-tab-costs .rd-tab-panel .grid.grid-cols-1.md\:grid-cols-3>div{background:linear-gradient(135deg,#cdd2da 0%,#a5abb5 100%)!important;border:1px solid #7d2738!important;border-radius:14px!important;box-shadow:0 7px 16px rgba(18,24,34,.18)!important;min-height:76px;text-align:center;}
      .rd-active-tab-costs .rd-tab-panel .rounded-2xl{border-radius:18px!important;box-shadow:0 8px 18px rgba(18,24,34,.16)!important;}
      .rd-active-tab-costs .rd-tab-panel .rounded-t-2xl{background:linear-gradient(180deg,#9ca3af,#7e858f)!important;color:#111827!important;}

      /* Attachments */
      .rd-active-tab-attachments .rd-tab-panel{background:linear-gradient(145deg,#dfe3e8 0%,#c4c9d1 100%)!important;padding:28px!important;border-radius:0 0 22px 22px;}
      .rd-active-tab-attachments .rd-sub-tabs button.active{background:linear-gradient(180deg,#555b65,#2f3540)!important;color:#fff!important;border-bottom:0!important;border-radius:999px!important;}
      .rd-active-tab-attachments .rd-tab-panel .space-y-4>div:not(:first-child){background:linear-gradient(180deg,#f9fafb,#e6e9ee)!important;border:1px solid #606975!important;border-radius:16px!important;box-shadow:0 8px 18px rgba(18,24,34,.22)!important;overflow:hidden;}
      .rd-active-tab-attachments .rd-tab-panel .space-y-4>div:not(:first-child)>div:first-child{background:linear-gradient(90deg,#444a54,#303640)!important;color:#e5e7eb!important;padding:14px 20px!important;font-size:20px!important;}

      /* Extensions */
      .rd-active-tab-extensions .rd-tab-panel{background:#fff!important;padding:28px!important;}
      .rd-active-tab-extensions .rd-tab-panel .space-y-3>div:not(:first-child){background:linear-gradient(135deg,#d9dde3 0%,#aeb4be 100%)!important;border:1px solid #606975!important;border-left:5px solid #97051f!important;border-radius:18px!important;box-shadow:0 8px 20px rgba(18,24,34,.20)!important;min-height:135px;}
      .rd-active-tab-extensions .rd-tab-panel h3,.rd-active-tab-extensions .rd-tab-panel p.uppercase{font-weight:900!important;color:#111827!important;}

      /* Strategy */
      .rd-active-tab-linkage .rd-tab-panel .space-y-5>div{background:linear-gradient(135deg,#555c66 0%,#2b313a 100%)!important;border:2px solid #8a929d!important;border-radius:18px!important;box-shadow:0 8px 18px rgba(0,0,0,.35)!important;padding:18px!important;}
      .rd-active-tab-linkage .rd-tab-panel .space-y-5>div:first-child{background:linear-gradient(135deg,#b80d2d 0%,#8b061d 100%)!important;border-color:#9299a3!important;}
      .rd-active-tab-linkage .rd-tab-panel h4{color:#e5e7eb!important;font-size:15px!important;font-weight:900!important;}
      .rd-active-tab-linkage .rd-tab-panel .space-y-1\.5>div,.rd-active-tab-linkage .rd-tab-panel .bg-white{background:linear-gradient(180deg,#f4f6f8,#e3e7ec)!important;color:#111827!important;border-radius:9px!important;box-shadow:inset 0 1px 0 rgba(255,255,255,.8);}
      .rd-active-tab-linkage .rd-tab-panel p{color:#fff!important;}



      /* Additive focused fix: project left list + Overview tab only */
      .rd-projects-module .rd-project-filters{margin-top:2px!important;margin-bottom:10px!important;}
      .rd-projects-module .rd-project-filters>div,
      .rd-projects-module .rd-project-filters select{height:34px!important;border-radius:10px!important;background:linear-gradient(180deg,#3a414b,#252b34)!important;color:#fff!important;border:1px solid rgba(255,255,255,.18)!important;box-shadow:0 6px 12px rgba(16,24,40,.14)!important;font-size:12px!important;}
      .rd-projects-module .rd-project-filters input{color:#fff!important;}
      .rd-projects-module .rd-project-filters input::placeholder{color:#d3d8df!important;}
      .rd-project-list-panel{max-height:calc(100vh - 245px)!important;overflow-y:auto!important;padding-right:8px!important;display:flex!important;flex-direction:column!important;gap:10px!important;}
      .rd-project-list-card{min-height:112px!important;border-radius:13px!important;padding:14px 16px!important;background:linear-gradient(135deg,#f6f7f9 0%,#d6dae1 48%,#bfc5ce 100%)!important;border:1px solid #9fa7b2!important;box-shadow:0 6px 12px rgba(20,28,40,.16), inset 0 1px 0 rgba(255,255,255,.75)!important;position:relative!important;overflow:hidden!important;transition:all .18s ease!important;}
      .rd-project-list-card:hover{transform:translateY(-1px);box-shadow:0 9px 18px rgba(20,28,40,.20), inset 0 1px 0 rgba(255,255,255,.8)!important;}
      .rd-project-list-card.is-selected{border-color:#a855f7!important;box-shadow:0 0 0 2px rgba(168,85,247,.68),0 9px 20px rgba(20,28,40,.22)!important;}
      .rd-project-list-card .rd-list-top{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-bottom:6px;}
      .rd-project-list-card .rd-list-ref{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:10px;font-weight:950;color:#97051f!important;letter-spacing:-.02em;}
      .rd-project-list-card .rd-list-status,
      .rd-project-list-card .rd-list-type,
      .rd-project-list-card .rd-list-trl{height:18px!important;padding:2px 7px!important;border-radius:999px!important;font-size:9px!important;font-weight:900!important;line-height:14px!important;box-shadow:inset 0 1px 0 rgba(255,255,255,.6)!important;display:inline-flex;align-items:center;gap:3px;}
      .rd-project-list-card .rd-list-status{background:#ecdfe3!important;color:#8d0b20!important;}
      .rd-project-list-card .rd-list-type{background:#9b0a23!important;color:#fff!important;}
      .rd-project-list-card .rd-list-trl{background:#4c5561!important;color:#fff!important;border:0!important;}
      .rd-project-list-card .rd-list-title{font-size:14px!important;line-height:1.15!important;color:#111827!important;font-weight:900!important;margin:4px 0 5px!important;}
      .rd-project-list-card .rd-list-so-row{display:flex;gap:6px;flex-wrap:wrap;margin:4px 0 5px;}
      .rd-project-list-card .rd-list-so{background:#f7dce2!important;color:#97051f!important;border:0!important;border-radius:5px!important;font-size:9px!important;font-weight:900!important;padding:2px 6px!important;}
      .rd-project-list-card .rd-list-meta{display:flex;align-items:center;gap:12px;font-size:10px!important;color:#6b7280!important;margin-top:4px;}
      .rd-project-list-card .rd-list-meta span{display:inline-flex;align-items:center;gap:4px;}
      .rd-project-list-card .rd-list-progress-text{font-size:10px!important;color:#6b7280!important;}
      .rd-project-list-card .rd-list-progress-value{font-size:10px!important;font-weight:950!important;color:#97051f!important;}
      .rd-project-list-card .rd-list-progress-bar{height:5px!important;border-radius:999px!important;background:#e5e7eb!important;box-shadow:inset 0 1px 2px rgba(0,0,0,.08)!important;overflow:hidden!important;}
      .rd-project-list-card .rd-list-progress-fill{height:100%!important;border-radius:999px!important;background:linear-gradient(90deg,#7b0b1d,#c8102e)!important;}

      .rd-project-list-card span[title^="TRL-"]{height:18px!important;padding:2px 7px!important;border-radius:999px!important;font-size:9px!important;font-weight:900!important;line-height:14px!important;background:#4c5561!important;color:#fff!important;border:0!important;box-shadow:inset 0 1px 0 rgba(255,255,255,.35)!important;}

      .rd-active-tab-overview .rd-detail-card{background:#fff!important;border-radius:16px!important;overflow:hidden!important;}
      .rd-active-tab-overview .rd-tab-panel{padding:18px 16px 20px!important;background:linear-gradient(135deg,#f7f8fa 0%,#ffffff 44%,#eef1f5 100%)!important;}
      .rd-overview-content{display:flex!important;flex-direction:column!important;gap:14px!important;}
      .rd-overview-summary{background:linear-gradient(135deg,#f4f5f8 0%,#e9edf2 100%)!important;border:1px solid #e2e6ec!important;border-radius:15px!important;min-height:48px!important;padding:14px 16px!important;font-size:12px!important;color:#303743!important;box-shadow:inset 0 1px 0 rgba(255,255,255,.8)!important;}
      .rd-overview-metric-grid{display:grid!important;grid-template-columns:repeat(6,minmax(0,1fr))!important;gap:10px!important;}
      .rd-overview-metric{background:linear-gradient(135deg,#2f3540 0%,#1f252d 100%)!important;color:#fff!important;border:1px solid #343b45!important;border-radius:9px!important;min-height:58px!important;padding:11px 13px!important;box-shadow:0 7px 14px rgba(10,14,20,.18),inset 0 1px 0 rgba(255,255,255,.08)!important;}
      .rd-overview-metric:nth-child(2){background:linear-gradient(135deg,#262d36 0%,#d6d8dd 48%,#9b0a23 100%)!important;}
      .rd-overview-metric-label{font-size:9px!important;text-transform:uppercase!important;color:#d9dde4!important;font-weight:900!important;letter-spacing:.035em!important;margin:0 0 4px!important;}
      .rd-overview-metric-value{font-size:15px!important;font-weight:950!important;line-height:1.1!important;color:#fff!important;margin:0!important;}
      .rd-overview-meta-grid{display:grid!important;grid-template-columns:1fr 1fr!important;gap:24px!important;font-size:12px!important;padding:0 2px!important;}
      .rd-overview-meta-label{font-size:9px!important;text-transform:uppercase!important;color:#606977!important;font-weight:900!important;letter-spacing:.03em!important;margin-bottom:3px!important;}
      .rd-overview-meta-value{font-size:13px!important;color:#111827!important;font-weight:700!important;}
      .rd-overview-year-panel,.rd-overview-trl-panel{background:linear-gradient(135deg,#2f3540,#20262e)!important;border-radius:9px!important;padding:14px!important;border:1px solid #303740!important;box-shadow:0 7px 14px rgba(10,14,20,.18)!important;}
      .rd-overview-section-title{color:#fff!important;font-size:11px!important;font-weight:900!important;text-transform:uppercase!important;margin-bottom:11px!important;display:flex!important;align-items:center!important;gap:6px!important;}
      .rd-overview-years-grid{display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr))!important;gap:8px!important;}
      .rd-overview-year-card{background:linear-gradient(135deg,#fff,#e7ebf0)!important;border:1px solid #cfd5dd!important;border-radius:8px!important;padding:10px!important;color:#111827!important;box-shadow:inset 0 1px 0 rgba(255,255,255,.85)!important;}
      .rd-overview-year-head{display:flex;justify-content:space-between;gap:6px;align-items:center;margin-bottom:4px;}
      .rd-overview-year-name{font-size:11px!important;font-weight:950!important;color:#111827!important;}
      .rd-overview-year-status{font-size:9px!important;color:#374151!important;display:inline-flex;gap:3px;align-items:center;}
      .rd-overview-year-fy,.rd-overview-year-budget{font-size:9px!important;color:#4b5563!important;}
      .rd-overview-trl-grid{display:grid!important;grid-template-columns:repeat(9,minmax(0,1fr))!important;gap:8px!important;}
      .rd-overview-trl{height:25px!important;border-radius:6px!important;background:linear-gradient(180deg,#fff,#e4e7ec)!important;border:1px solid #cfd4dc!important;color:#424b57!important;font-size:10px!important;font-weight:900!important;display:flex;align-items:center;justify-content:center;}
      .rd-overview-trl.is-current{background:linear-gradient(180deg,#c8102e,#90071f)!important;color:#fff!important;box-shadow:0 6px 14px rgba(151,5,31,.32)!important;transform:scale(1.04);}
      .rd-overview-progress{margin-top:2px!important;}
      .rd-overview-progress-head{display:flex;justify-content:space-between;align-items:center;font-size:10px;margin-bottom:5px;}
      .rd-overview-progress-track{height:8px!important;border-radius:999px!important;background:#e5e7eb!important;box-shadow:inset 0 1px 2px rgba(0,0,0,.10)!important;overflow:hidden!important;}
      .rd-overview-progress-fill{height:100%!important;border-radius:999px!important;background:linear-gradient(90deg,#6b7280,#3f4650)!important;}
      .rd-overview-scoring{display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr))!important;gap:10px!important;}
      .rd-overview-score{background:linear-gradient(135deg,#fff,#e7ebf0)!important;border:1px solid #cfd5dd!important;border-radius:10px!important;padding:10px;text-align:center;box-shadow:0 5px 11px rgba(10,14,20,.10)!important;}
      .rd-overview-score-label{font-size:9px!important;color:#6b7280!important;text-transform:uppercase!important;}
      .rd-overview-score-value{font-size:15px!important;color:#111827!important;font-weight:950!important;}


      /* FINAL PATCH: readable native dropdowns */
      .rd-projects-module select,
      .rd-projects-module select option,
      .rd-projects-module input,
      .rd-projects-module textarea{
        color:#111827!important;
        -webkit-text-fill-color:#111827!important;
      }
      .rd-projects-module select option{
        background:#ffffff!important;
        color:#111827!important;
      }
      .rd-projects-module .rd-project-filters select{
        background:linear-gradient(180deg,#3a414b,#252b34)!important;
        color:#ffffff!important;
        -webkit-text-fill-color:#ffffff!important;
      }
      .rd-projects-module .rd-project-filters select option{
        background:#ffffff!important;
        color:#111827!important;
        -webkit-text-fill-color:#111827!important;
      }

      /* FINAL PATCH: Overview TRL progression matches supplied design */
      .rd-overview-trl-panel{
        background:linear-gradient(135deg,#2f3540 0%,#20262e 100%)!important;
        border-radius:10px!important;
        padding:14px 16px!important;
        border:1px solid #2d343e!important;
        box-shadow:0 10px 18px rgba(10,14,20,.18)!important;
      }
      .rd-overview-trl-panel>p{
        color:#ffffff!important;
        font-size:11px!important;
        font-weight:950!important;
        margin-bottom:12px!important;
      }
      .rd-overview-trl-panel .flex{
        display:grid!important;
        grid-template-columns:repeat(9,minmax(0,1fr))!important;
        gap:10px!important;
        align-items:center!important;
      }
      .rd-overview-trl{
        height:32px!important;
        min-width:0!important;
        border-radius:8px!important;
        background:linear-gradient(180deg,#ffffff 0%,#e4e8ee 100%)!important;
        border:1px solid #cfd4dc!important;
        color:#2f3742!important;
        font-size:15px!important;
        font-weight:950!important;
        display:flex!important;
        align-items:center!important;
        justify-content:center!important;
        box-shadow:inset 0 1px 0 rgba(255,255,255,.9),0 4px 8px rgba(0,0,0,.15)!important;
      }
      .rd-overview-trl.is-current{
        background:linear-gradient(180deg,#c8102e 0%,#87051d 100%)!important;
        color:#ffffff!important;
        box-shadow:0 8px 18px rgba(151,5,31,.45),inset 0 1px 0 rgba(255,255,255,.35)!important;
        transform:translateY(-2px) scale(1.06)!important;
      }

      /* FINAL PATCH: Scope Extensions tab design */
      .rd-active-tab-extensions .rd-tab-panel{
        background:#ffffff!important;
        padding:24px!important;
      }
      .rd-active-tab-extensions .rd-tab-panel>div{
        gap:20px!important;
      }
      .rd-active-tab-extensions .rd-tab-panel>div>div:first-child{
        display:flex!important;
        align-items:flex-start!important;
        justify-content:space-between!important;
        padding:8px 0 10px!important;
        background:#fff!important;
        border:0!important;
        box-shadow:none!important;
      }
      .rd-active-tab-extensions .rd-tab-panel>div>div:first-child p:first-child{
        font-size:13px!important;
        font-weight:950!important;
        color:#111827!important;
      }
      .rd-active-tab-extensions .rd-tab-panel>div>div:first-child p:last-child{
        font-size:12px!important;
        color:#111827!important;
        max-width:980px!important;
      }
      .rd-active-tab-extensions .rd-tab-panel>div>div:first-child button{
        border-radius:13px!important;
        padding:13px 20px!important;
        font-size:14px!important;
        font-weight:850!important;
        box-shadow:0 8px 16px rgba(151,5,31,.30)!important;
      }
      .rd-active-tab-extensions .rd-tab-panel>div>div:not(:first-child){
        background:linear-gradient(135deg,#d9dde3 0%,#b8bec8 55%,#a4abb6 100%)!important;
        border:1px solid #5d6673!important;
        border-left:6px solid #97051f!important;
        border-radius:20px!important;
        box-shadow:inset 0 1px 0 rgba(255,255,255,.55),0 10px 20px rgba(18,24,34,.24)!important;
        min-height:145px!important;
        padding:24px!important;
        position:relative!important;
        overflow:hidden!important;
      }
      .rd-active-tab-extensions .rd-tab-panel>div>div:not(:first-child)::after{
        content:"";
        position:absolute;
        right:12px;
        bottom:8px;
        width:60px;
        height:60px;
        background:rgba(255,255,255,.55);
        clip-path:polygon(50% 0%,62% 36%,100% 50%,62% 64%,50% 100%,38% 64%,0% 50%,38% 36%);
      }
      .rd-active-tab-extensions .rd-tab-panel .font-mono{
        color:#111827!important;
        background:rgba(255,255,255,.35)!important;
        border-radius:8px!important;
        padding:7px 11px!important;
        font-weight:950!important;
      }
      .rd-active-tab-extensions .rd-tab-panel .rounded-full{
        background:rgba(255,255,255,.72)!important;
        color:#111827!important;
      }
      .rd-active-tab-extensions .rd-tab-panel div:not(:first-child) p,
      .rd-active-tab-extensions .rd-tab-panel div:not(:first-child) span{
        color:#111827!important;
      }
      .rd-active-tab-extensions .rd-tab-panel div:not(:first-child) .text-slate-400,
      .rd-active-tab-extensions .rd-tab-panel div:not(:first-child) .text-slate-500{
        color:#606875!important;
      }
      .rd-active-tab-extensions .rd-tab-panel div:not(:first-child) .text-slate-800,
      .rd-active-tab-extensions .rd-tab-panel div:not(:first-child) .font-bold{
        color:#7f1224!important;
      }

      /* FINAL PATCH: Executive page dashboard + TRL curve */
      .rd-executive-page .rd-projects-header h1{color:#7f1224!important;}
      .rd-executive-view .rd-project-workspace{gap:18px!important;}
      .rd-executive-view .rd-project-workspace>.bg-white{
        min-height:150px!important;
        padding:18px!important;
        border-radius:14px!important;
        background:linear-gradient(135deg,#f7f8fa 0%,#d7dce3 100%)!important;
        border:1px solid #c5ccd5!important;
        box-shadow:0 8px 16px rgba(22,28,38,.10)!important;
      }
      .rd-executive-view h3{
        font-size:12px!important;
        color:#111827!important;
        font-weight:950!important;
        letter-spacing:.02em!important;
      }
      .rd-exec-type-icon{
        width:25px!important;height:25px!important;border-radius:8px!important;
        background:linear-gradient(180deg,#a30b24,#76051a)!important;color:#fff!important;
        display:flex!important;align-items:center!important;justify-content:center!important;
      }
      .rd-exec-bar-track{height:8px!important;border-radius:999px!important;background:#cfd4dc!important;overflow:hidden!important;box-shadow:inset 0 1px 2px rgba(0,0,0,.12)!important;}
      .rd-exec-bar-fill{height:100%!important;border-radius:999px!important;background:linear-gradient(90deg,#78051a,#a70825)!important;}
      .rd-exec-trl-chart{padding:4px 6px 0!important;}
      .rd-exec-trl-numbers{display:grid!important;grid-template-columns:repeat(9,minmax(0,1fr))!important;gap:16px!important;align-items:center!important;margin-bottom:8px!important;}
      .rd-exec-trl-number{text-align:center!important;}
      .rd-exec-trl-count{display:inline-flex!important;align-items:center!important;justify-content:center!important;min-width:25px;height:25px;border-radius:8px;background:#eef0f4;color:#7f1224;font-size:11px;font-weight:950;}
      .rd-exec-trl-count.is-hot{background:linear-gradient(180deg,#a70825,#76051a)!important;color:#fff!important;box-shadow:0 6px 12px rgba(151,5,31,.25)!important;}
      .rd-exec-trl-level{font-size:10px!important;color:#7f1224!important;font-weight:900!important;margin-top:5px!important;}
      .rd-exec-trl-svg{width:100%;height:66px;display:block;overflow:visible;}
      .rd-exec-trl-line{fill:none;stroke:#78051a;stroke-width:4;stroke-linecap:round;stroke-linejoin:round;filter:drop-shadow(0 8px 8px rgba(151,5,31,.14));}
      .rd-exec-trl-area{fill:url(#rdTrlFade);}

      @media(max-width:1450px){.rd-overview-metric-grid{grid-template-columns:repeat(6,minmax(0,1fr))!important}.rd-project-list-card{min-height:100px!important;padding:12px 14px!important}.rd-overview-metric{min-height:52px!important;padding:9px 11px!important}.rd-overview-metric-value{font-size:13px!important}.rd-overview-year-panel,.rd-overview-trl-panel{padding:12px!important}}
      @media(max-width:1450px){
        .rd-projects-module{font-size:11px}.rd-projects-header h1{font-size:20px!important}.rd-stat-card{min-height:66px;padding:13px!important}.rd-stat-value{font-size:20px!important}.rd-detail-tabs{gap:10px!important}.rd-detail-tabs button{font-size:13px!important;gap:5px!important}.rd-detail-tabs .rd-tab-count{min-width:21px;height:21px;font-size:10px!important}.rd-stage-node{min-width:74px}.rd-stage-dot{width:36px;height:36px}.rd-stage-node.is-current .rd-stage-dot{width:46px;height:46px}.rd-stage-line{min-width:42px}.rd-tab-panel{padding:18px!important}.rd-motivation-title{font-size:21px}.rd-motivation-number{font-size:36px}.rd-motivation-body p{font-size:15px!important}
      }
    `}</style>
    <div className={`space-y-5 rd-projects-module ${mainView==='executive' ? 'rd-executive-page' : ''} ${detail ? `rd-active-tab-${tab}` : ''}`}>
      {/* Toast Notification */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* ═══ HEADER ═══ */}
      <div className="rd-projects-header flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <FlaskConical size={28} className="text-[#c8102e]" />
          <div>
            <h1 className="font-bold text-2xl text-slate-800">Projects Management</h1>
            <p className="text-slate-500 text-sm mt-0.5">FY {filters.fy-1}/{filters.fy%100} · {stats.total} projects · {stats.active} active</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select className={input+' max-w-[110px]'} value={filters.fy} onChange={e=>{setFilters(f=>({...f,fy:+e.target.value}));setDetail(null);}}>{[2025,2026,2027,2028].map(y=><option key={y} value={y}>FY {y-1}/{y%100}</option>)}</select>
          <div className="flex bg-slate-100 rounded-xl p-0.5">
            <button onClick={()=>{setMainView('list');setDetail(null);}} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${mainView==='list'?'bg-white text-[#c8102e] shadow-sm':'text-slate-500'}`}>Projects</button>
            <button onClick={()=>{setMainView('executive');setDetail(null);}} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${mainView==='executive'?'bg-white text-[#c8102e] shadow-sm':'text-slate-500'}`}>Executive</button>
          </div>
          <button onClick={exportExcel} className={btnS}><FileSpreadsheet size={14}/>Export</button>
          <button onClick={openNewWithDraftRestore} className={btnP}><Plus size={14}/>New Project</button>
        </div>
      </div>

      {/* ═══ STATS ═══ */}
      <div className="rd-project-stat-grid grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Total" value={stats.total} icon={Layers} accent="border-l-blue-500" />
        <StatCard label="Active" value={stats.active} icon={Zap} accent="border-l-emerald-500" />
        <StatCard label="Under Review" value={stats.underReview} icon={Clock} accent="border-l-indigo-500" />
        <StatCard label="Budget" value={fmtR(stats.totalBudget)} icon={DollarSign} accent="border-l-[#c8102e]" />
        <StatCard label="Utilisation" value={stats.totalBudget>0?`${((stats.totalSpend/stats.totalBudget)*100).toFixed(0)}%`:'0%'} icon={BarChart3} accent="border-l-amber-500" />
      </div>

      {/* ═══ EXECUTIVE VIEW ═══ */}
      {mainView==='executive'&&(<div className="space-y-6 rd-executive-view">
        <div className="rd-project-workspace grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border p-5"><h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Portfolio by Type</h3>
            {TYPES.map(t=>{const Icon=TYPE_ICONS[t];const c=projects.filter(p=>p.projectType===t).length;return(<div key={t} className="flex items-center gap-3 mb-3"><div className="rd-exec-type-icon"><Icon size={14}/></div><span className="text-xs text-slate-700 font-semibold w-20">{t}</span><div className="rd-exec-bar-track flex-1"><div className="rd-exec-bar-fill" style={{width:`${stats.total?((c/stats.total)*100):0}%`}}/></div><span className="text-sm font-bold text-[#7f1224] w-6 text-right">{c}</span></div>);})}</div>
          <div className="bg-white rounded-2xl shadow-sm border p-5"><h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">By Status</h3>
            {['Draft','Active','Approved','InProgress','Completed','Declined'].map(s=>{const c=projects.filter(p=>p.status===s).length;return c>0?(<div key={s} className="flex items-center justify-between mb-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[s]||'bg-slate-100 text-slate-600'}`}>{STATUS_LABELS[s]||s}</span><span className="font-bold text-slate-800 text-sm">{c}</span></div>):null;})}</div>
          <div className="bg-white rounded-2xl shadow-sm border p-5"><h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">TRL Distribution</h3>
            {(()=>{const counts=TRL_LEVELS.map(t=>projects.filter(p=>p.trlStage===t).length);const max=Math.max(1,...counts);const pts=counts.map((c,i)=>`${12+i*47},${58-(c/max)*42}`).join(' ');const area=`12,62 ${pts} ${12+(counts.length-1)*47},62`;return(<div className="rd-exec-trl-chart"><div className="rd-exec-trl-numbers">{TRL_LEVELS.map((t,i)=>(<div key={t} className="rd-exec-trl-number"><span className={`rd-exec-trl-count ${counts[i]>1?'is-hot':''}`}>{counts[i]}</span><p className="rd-exec-trl-level">{t}</p></div>))}</div><svg className="rd-exec-trl-svg" viewBox="0 0 400 70" preserveAspectRatio="none"><defs><linearGradient id="rdTrlFade" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#97051f" stopOpacity="0.22"/><stop offset="100%" stopColor="#97051f" stopOpacity="0"/></linearGradient></defs><polygon className="rd-exec-trl-area" points={area}/><polyline className="rd-exec-trl-line" points={pts}/></svg></div>);})()}</div>
        </div>
        {/* Strategy Alignment */}
        <div className="bg-white rounded-2xl shadow-sm border p-5"><h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Strategy → Programme → Project Alignment</h3>
          {objectives.map(so=>{const linked=projects.filter(p=>(p.strategicObjectives||[]).some(x=>x.strategicObjectiveId===so.id));return(<div key={so.id} className="mb-4 border rounded-xl p-4"><div className="flex items-center gap-2 mb-2"><Target size={14} className="text-[#c8102e]"/><span className="rd-list-ref">{so.reference}</span><span className="text-sm font-semibold text-slate-800 flex-1">{so.title}</span><span className="text-xs text-slate-400">{linked.length} projects</span></div>
            <div className="ml-6 space-y-1">{linked.map(p=>(<div key={p.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 rounded-lg px-2 py-1" onClick={()=>{setMainView('list');loadDetail(p.id);}}><span className="font-mono text-[9px] text-slate-400">{p.reference}</span><span className="text-slate-700 flex-1 truncate">{p.title}</span><span className={`px-1.5 py-0.5 rounded-full text-[8px] font-semibold ${STATUS_COLORS[p.status]||'bg-slate-100 text-slate-600'}`}>{STATUS_LABELS[p.status]||p.status}</span></div>))}</div>
            {linked.length===0&&<p className="text-[10px] text-slate-300 ml-6">No projects linked</p>}
          </div>);})}
          {(()=>{const unlinked=projects.filter(p=>!(p.strategicObjectives||[]).length);return unlinked.length>0&&(<div className="border border-dashed border-amber-300 rounded-xl p-4 bg-amber-50/30"><div className="flex items-center gap-2 mb-2"><AlertTriangle size={14} className="text-amber-500"/><span className="text-sm font-semibold text-amber-800">Unlinked ({unlinked.length})</span></div><div className="ml-6 space-y-1">{unlinked.map(p=>(<div key={p.id} className="flex items-center gap-2 text-xs"><span className="font-mono text-[9px] text-slate-400">{p.reference}</span><span className="text-slate-700 flex-1 truncate">{p.title}</span><span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${TYPE_COLORS[p.projectType]}`}>{p.projectType}</span></div>))}</div></div>);})()}
        </div>
      </div>)}

      {/* ═══ LIST VIEW ═══ */}
      {mainView==='list'&&(<>
        <div className="rd-project-filters flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-1.5"><Search size={14} className="text-slate-400"/><input className="bg-transparent text-sm w-40 focus:outline-none" placeholder="Search..." value={filters.search} onChange={e=>setFilters(f=>({...f,search:e.target.value}))}/></div>
          <select className={input+' max-w-[140px]'} value={filters.status} onChange={e=>setFilters(f=>({...f,status:e.target.value}))}><option value="">All Statuses</option>{STATUSES.map(s=><option key={s} value={s}>{STATUS_LABELS[s]}</option>)}</select>
          <select className={input+' max-w-[130px]'} value={filters.type} onChange={e=>setFilters(f=>({...f,type:e.target.value}))}><option value="">All Types</option>{TYPES.map(t=><option key={t}>{t}</option>)}</select>
          <select className={input+' max-w-[140px]'} value={filters.division} onChange={e=>setFilters(f=>({...f,division:e.target.value}))}><option value="">All Divisions</option>{divisions.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}</select>
          {/* V3.3 — tester-requested filters: Cluster, Research Theme, Funding Source */}
          <select className={input+' max-w-[140px]'} value={filters.clusterId} onChange={e=>setFilters(f=>({...f,clusterId:e.target.value}))}><option value="">All Clusters</option>{clusters.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select>
          <select className={input+' max-w-[160px]'} value={filters.researchThemeId} onChange={e=>setFilters(f=>({...f,researchThemeId:e.target.value}))}><option value="">All Research Themes</option>{researchThemes.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}</select>
          <select className={input+' max-w-[160px]'} value={filters.fundingSource} onChange={e=>setFilters(f=>({...f,fundingSource:e.target.value}))}><option value="">All Funding Sources</option>{fundingSources.map(s=><option key={s} value={s}>{s}</option>)}</select>
          {/* V3.3 — Group By selector */}
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-2 py-1.5">
            <Layers size={12} className="text-slate-400"/>
            <select className="bg-transparent text-xs focus:outline-none" value={filters.groupBy} onChange={e=>setFilters(f=>({...f,groupBy:e.target.value}))}>
              <option value="">No grouping</option>
              <option value="cluster">Group by Cluster</option>
              <option value="researchTheme">Group by Research Theme</option>
              <option value="fundingSource">Group by Funding Source</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Project List */}
          <div className="lg:col-span-1 rd-project-list-panel">
            {loading&&<div className="text-center py-8 text-slate-400 text-sm">Loading...</div>}
            {!loading&&filtered.length===0&&(<div className="bg-white rounded-2xl border p-8 text-center text-slate-400 text-sm"><FlaskConical size={32} className="mx-auto mb-2 text-slate-300"/>No projects found</div>)}
            {/* V3.3 — render either grouped sections or flat list depending on filters.groupBy */}
            {!loading && filtered.length > 0 && (() => {
              // Card render reused in both flat and grouped modes
              const renderCard = (p) => {
                const TypeIcon = TYPE_ICONS[p.projectType] || Beaker;
                return (
                  <div key={p.id} className={`rd-project-list-card ${detail?.id===p.id?'is-selected':''}`} onClick={()=>loadDetail(p.id)}>
                    <div className="rd-list-top">
                      <span className="rd-list-ref">{p.reference}</span>
                      <span className="rd-list-status">{STATUS_LABELS[p.status]||p.status}</span>
                      <span className="rd-list-type"><TypeIcon size={9}/>{p.projectType}</span>
                      <TrlBadge level={p.trlStage} />
                    </div>
                    <p className="rd-list-title line-clamp-2">{p.title}</p>
                    {(p.strategicObjectives||[]).length>0&&<div className="rd-list-so-row">{(p.strategicObjectives||[]).slice(0,3).map(x=>(<span key={x.strategicObjectiveId} className="rd-list-so">{x.strategicObjective?.reference}</span>))}</div>}
                    <div className="rd-list-meta">
                      <span className="inline-flex items-center gap-1"><DollarSign size={10}/>{fmtR(p.totalBudget)}</span>
                      <span className="inline-flex items-center gap-1"><User size={10}/>{p.principalInvestigator||'—'}</span>
                    </div>
                    <div className="mt-2"><div className="flex justify-between mb-0.5"><span className="rd-list-progress-text">Progress</span><span className="rd-list-progress-value">{p.progressPercentage||0}%</span></div><div className="rd-list-progress-bar"><div className="rd-list-progress-fill" style={{width:`${p.progressPercentage||0}%`}}/></div></div>
                  </div>
                );
              };

              // Flat list
              if (!grouped) return filtered.map(renderCard);

              // Grouped sections
              return grouped.map(g => (
                <div key={g.key} className="mb-3">
                  <div className="flex items-center gap-2 px-2 py-1.5 mb-1.5 bg-slate-100 rounded-lg border border-slate-200">
                    <Layers size={11} className="text-[#8f1024]"/>
                    <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wide flex-1 truncate" title={g.label}>{g.label}</span>
                    <span className="text-[10px] font-bold text-slate-500 bg-white px-1.5 py-0.5 rounded">{g.items.length}</span>
                  </div>
                  {g.items.map(renderCard)}
                </div>
              ));
            })()}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-2">{detail?(<div className="bg-white rounded-2xl shadow-sm border rd-detail-card">
            {/* Detail Header */}
            <div className="p-5 border-b">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-xs font-bold text-[#c8102e]">{detail.reference}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[detail.status]||'bg-slate-100 text-slate-600'}`}>{STATUS_LABELS[detail.status]||detail.status}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${TYPE_COLORS[detail.projectType]} inline-flex items-center gap-0.5`}>{React.createElement(TYPE_ICONS[detail.projectType]||Beaker,{size:10})}{detail.projectType}</span>
                    <TrlBadge level={detail.trlStage} size="md" />
                  </div>
                  <h2 className="font-bold text-lg text-slate-800">{detail.title}</h2>
                  <p className="text-xs text-slate-400 mt-1">{detail.division?.name||'—'} · {detail.principalInvestigator||'—'} · {detail.fundingSource||'—'}</p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0 ml-3">
                  {canSubmit&&<button onClick={submitProject} className={btnG+' !py-1.5 !px-3'}><Send size={12}/>Submit</button>}
                  {isEditable&&<button onClick={()=>openEdit(detail)} className={btnS+' !py-1.5 !px-3'}><Edit3 size={12}/>Edit</button>}
                  {isEditable&&<button onClick={()=>deleteProject(detail.id)} className={btnD+' !py-1.5 !px-3'}><Trash2 size={12}/></button>}
                </div>
              </div>
              <StatusTimeline status={detail.status} projectType={detail.projectType} />
            </div>

            {/* Review Panel */}
            {isReviewable&&(<div className="px-5 py-4 bg-indigo-50/50 border-b"><p className="text-xs font-bold text-indigo-700 uppercase mb-2 flex items-center gap-1.5"><Shield size={14}/>Review Actions</p>
              <textarea className={input+' h-16 mb-3 text-xs'} placeholder="Review comments (mandatory for Return / Decline)..." value={reviewComment} onChange={e=>setReviewComment(e.target.value)} />
              <div className="flex gap-2">
                <button onClick={approveProject} className={btnG+' !text-[11px]'}><CheckCircle2 size={14}/>Approve</button>
                <button onClick={returnProject} className="px-3 py-1.5 bg-amber-500 text-white rounded-xl text-[11px] font-semibold hover:bg-amber-600 inline-flex items-center gap-1"><RotateCcw size={14}/>Return for Revisions</button>
                <button onClick={declineProject} className="px-3 py-1.5 bg-red-500 text-white rounded-xl text-[11px] font-semibold hover:bg-red-600 inline-flex items-center gap-1"><XCircle size={14}/>Decline</button>
              </div>
            </div>)}

            {/* Tabs */}
            <div className="rd-detail-tabs px-5 border-b flex gap-0.5 overflow-x-auto">
              {DETAIL_TABS.map(t => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`relative transition-all ${tab===t.key ? 'rd-tab-active' : ''}`}
                  >
                    <Icon />
                    <span className="rd-tab-label">{t.label}</span>
                    {t.count != null && <span className="rd-tab-count">{t.count}</span>}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            <div className="rd-tab-panel p-5">
              {/* Overview */}
              {tab==='overview'&&(<div className="rd-overview-content">
                {/* Show description from section data or direct field */}
                {(detail._sectionData?.executiveSummary || detail._sectionData?.scopeDescription || detail.description) &&
                  <p className="rd-overview-summary">{detail._sectionData?.executiveSummary || detail._sectionData?.scopeDescription || detail.description}</p>
                }
                <div className="rd-overview-metric-grid">
                  <div className="rd-overview-metric"><p className="rd-overview-metric-label">Budget</p><p className="rd-overview-metric-value">{fmtR(detail.totalBudget)}</p></div>
                  <div className="rd-overview-metric"><p className="rd-overview-metric-label">Spent</p><p className="rd-overview-metric-value">{fmtR(detail.actualSpend)}</p></div>
                  <div className="rd-overview-metric"><p className="rd-overview-metric-label">Duration</p><p className="rd-overview-metric-value">{detail.totalYears || 1} {(detail.totalYears||1) === 1 ? 'Year' : 'Years'}</p></div>
                  <div className="rd-overview-metric"><p className="rd-overview-metric-label">Financial Year</p><p className="rd-overview-metric-value">FY {(detail.financialYear||2026)-1}/{(detail.financialYear||2026)%100}</p></div>
                  <div className="rd-overview-metric"><p className="rd-overview-metric-label">Start</p><p className="rd-overview-metric-value">{fmtDate(detail.startDate)}</p></div>
                  <div className="rd-overview-metric"><p className="rd-overview-metric-label">End</p><p className="rd-overview-metric-value">{fmtDate(detail.endDate)}</p></div>
                </div>
                <div className="rd-overview-meta-grid">
                  <div><p className="rd-overview-meta-label">Principal Investigator</p><p className="rd-overview-meta-value">{detail.principalInvestigator||'—'}</p></div>
                  <div><p className="rd-overview-meta-label">Research Group</p><p className="rd-overview-meta-value">{detail.researchTheme||'—'}</p></div>
                </div>
                {/* Multi-Year Panel — shows year status breakdown */}
                {(detail.totalYears > 1 || (detail.years && detail.years.length > 0)) && (
                  <div className="rd-overview-year-panel">
                    <p className="rd-overview-section-title"><Calendar size={14}/>Project Years ({detail.totalYears || (detail.years||[]).length || 1})</p>
                    <div className="rd-overview-years-grid">
                      {(detail.years && detail.years.length > 0 ? detail.years : Array.from({length: detail.totalYears||1}, (_, i) => ({
                        yearNumber: i+1, financialYear: (detail.financialYear||2026)+i, status: i===0 ? (detail.status === 'Active' ? 'Active' : 'Pending') : 'Pending'
                      }))).sort((a,b) => a.yearNumber - b.yearNumber).map(yr => {
                        const statusColors = { Active:'bg-emerald-100 text-emerald-700 border-emerald-200', Closed:'bg-slate-100 text-slate-600 border-slate-200', ClosedEarly:'bg-blue-100 text-blue-700 border-blue-200', Pending:'bg-amber-50 text-amber-600 border-amber-200' };
                        const statusIcons = { Active: Zap, Closed: CheckCircle2, ClosedEarly: CheckCircle2, Pending: Clock };
                        const YrIcon = statusIcons[yr.status] || Clock;
                        return (
                          <div key={yr.yearNumber} className="rd-overview-year-card">
                            <div className="rd-overview-year-head">
                              <span className="rd-overview-year-name">Year {yr.yearNumber}</span>
                              <span className="rd-overview-year-status"><YrIcon size={11}/>{yr.status === 'ClosedEarly' ? 'Closed Early' : yr.status}</span>
                            </div>
                            <p className="rd-overview-year-fy">FY {(yr.financialYear||2026)-1}/{(yr.financialYear||2026)%100}</p>
                            {(yr.plannedBudget || yr.actualSpend) ? (
                              <div className="rd-overview-year-budget">
                                <span>Budget: {fmtR(yr.plannedBudget)}</span>
                                <span>Spend: {fmtR(yr.actualSpend)}</span>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* TRL Progress */}
                {detail.trlStage&&(<div className="rd-overview-trl-panel"><p className="text-xs font-bold text-slate-500 uppercase mb-3">TRL Progression</p>
                  <div className="flex items-center gap-2">{TRL_LEVELS.map(t=>(<div key={t} className={`rd-overview-trl ${t===detail.trlStage?'is-current':''}`}>{t}</div>))}</div>
                </div>)}
                {/* Progress bar */}
                <div className="rd-overview-progress"><div className="rd-overview-progress-head"><span className="text-slate-500 font-semibold flex items-center gap-1"><TrendingUp size={12}/>Progress (milestones)</span><span className="font-bold text-[#97051f]">{detail.progressPercentage||0}%</span></div><div className="rd-overview-progress-track"><div className="rd-overview-progress-fill" style={{width:`${detail.progressPercentage||0}%`}}/></div></div>
                {/* Scoring — from section data */}
                {(()=>{const sd=detail._sectionData||{};return(sd.impactScore||sd.feasibilityScore||sd.alignmentScore)?(<div><p className="rd-overview-meta-label">Scoring</p><div className="rd-overview-scoring">{[{l:'Impact',v:sd.impactScore},{l:'Feasibility',v:sd.feasibilityScore},{l:'Alignment',v:sd.alignmentScore}].map(s=>s.v!=null&&s.v!==''&&(<div key={s.l} className="rd-overview-score"><p className="rd-overview-score-label">{s.l}</p><p className="rd-overview-score-value">{s.v}<span className="text-xs text-slate-400">/10</span></p></div>))}</div></div>):null;})()}
              </div>)}

              {/* Motivation Form (read-only) */}
              {tab==='motivation'&&(<div className="space-y-2">
                {(()=>{
                  const sd = detail._sectionData || {};
                  const hasData = sd.executiveSummary || sd.scopeDescription || sd.problemStatement || sd.methodology
                    || sd.socioeconomicImpact || sd.commercialisationPotential || sd.existingInfrastructure
                    || sd.additionalResources || sd.keyReferences || detail.trlStage;
                  if (!hasData) return (
                    <div className="text-center py-8 text-slate-400"><ClipboardList size={32} className="mx-auto mb-2 text-slate-300"/><p className="text-sm">No form data captured yet</p>{isEditable&&<button onClick={()=>openEdit(detail)} className={btnP+' mt-3'}><Edit3 size={14}/>Edit</button>}</div>
                  );
                  return (<>
                    {/* Section 3: Executive Summary / Scope */}
                    {sd.executiveSummary && <FormSection number={3} title="Executive Summary" icon={BookOpen} defaultOpen={true}><p className="text-sm text-slate-700 whitespace-pre-wrap">{sd.executiveSummary}</p></FormSection>}
                    {sd.scopeDescription && <FormSection number={3} title="Project Scope" icon={BookOpen} defaultOpen={true}><p className="text-sm text-slate-700 whitespace-pre-wrap">{sd.scopeDescription}</p></FormSection>}
                    {/* Section 4: Research Motivation */}
                    {sd.problemStatement && <FormSection number={4} title="Problem Statement" icon={Microscope}><p className="text-sm text-slate-700 whitespace-pre-wrap">{sd.problemStatement}</p></FormSection>}
                    {sd.mandateAlignment && <FormSection number={4} title="Mandate Alignment" icon={Target}><p className="text-sm text-slate-700 whitespace-pre-wrap">{sd.mandateAlignment}</p></FormSection>}
                    {sd.innovationNovelty && <FormSection number={4} title="Innovation & Novelty" icon={Zap}><p className="text-sm text-slate-700 whitespace-pre-wrap">{sd.innovationNovelty}</p></FormSection>}
                    {/* Section 5: Objectives & Methodology */}
                    {(sd.objectives || sd.description) && <FormSection number={5} title="Objectives" icon={Target}><p className="text-sm text-slate-700 whitespace-pre-wrap">{sd.objectives || sd.description}</p></FormSection>}
                    {sd.methodology && <FormSection number={5} title="Methodology" icon={Beaker}><p className="text-sm text-slate-700 whitespace-pre-wrap">{sd.methodology}</p></FormSection>}
                    {/* Section 6: Expected Outcomes & TRL */}
                    {(detail.trlStage || sd.socioeconomicImpact || sd.commercialisationPotential) && <FormSection number={6} title="Expected Outcomes & TRL" icon={Award}>
                      {detail.trlStage && <div className="flex items-center gap-4 mb-3"><div><span className="text-[10px] text-slate-400 uppercase font-semibold">Current TRL</span><TrlBadge level={detail.trlStage} size="md" /></div>{detail.trlTarget && <div><span className="text-[10px] text-slate-400 uppercase font-semibold">Target TRL</span><TrlBadge level={detail.trlTarget} size="md" /></div>}</div>}
                      {sd.socioeconomicImpact && <div className="mb-2"><p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Socio-Economic Impact</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{sd.socioeconomicImpact}</p></div>}
                      {sd.commercialisationPotential && <div><p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Commercialisation Potential</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{sd.commercialisationPotential}</p></div>}
                    </FormSection>}
                    {/* Section 7: Team & Resources */}
                    {(sd.existingInfrastructure || sd.additionalResources || sd.divisionalManager) && <FormSection number={7} title="Team & Resources" icon={Users}>
                      {sd.divisionalManager && <div className="mb-2"><p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Divisional Manager</p><p className="text-sm text-slate-700">{sd.divisionalManager}</p></div>}
                      {sd.existingInfrastructure && <div className="mb-2"><p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Existing Infrastructure & Equipment</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{sd.existingInfrastructure}</p></div>}
                      {sd.additionalResources && <div><p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Additional Resources Required</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{sd.additionalResources}</p></div>}
                    </FormSection>}
                    {/* Section 8: Risk Assessment */}
                    {(sd.risks && sd.risks.length > 0) ? <FormSection number={8} title="Risk Assessment" icon={Shield}>
                      <div className="overflow-x-auto"><table className="w-full text-xs"><thead><tr className="bg-slate-50"><th className="px-2 py-2 text-left text-slate-500 font-semibold">Risk</th><th className="px-2 py-2 text-center text-slate-500 font-semibold w-20">Likelihood</th><th className="px-2 py-2 text-center text-slate-500 font-semibold w-20">Impact</th><th className="px-2 py-2 text-left text-slate-500 font-semibold">Mitigation</th></tr></thead>
                        <tbody>{sd.risks.filter(r=>r.description).map((r,i)=>(<tr key={i} className="border-t border-slate-200"><td className="px-2 py-2 text-slate-700">{r.description}</td><td className="px-2 py-2 text-center"><span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${r.likelihood==='High'?'bg-red-100 text-red-700':r.likelihood==='Low'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}`}>{r.likelihood}</span></td><td className="px-2 py-2 text-center"><span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${r.impact==='High'?'bg-red-100 text-red-700':r.impact==='Low'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}`}>{r.impact}</span></td><td className="px-2 py-2 text-slate-600">{r.mitigation||'—'}</td></tr>))}</tbody>
                      </table></div>
                    </FormSection> : <FormSection number={8} title="Risk Assessment" icon={Shield}><p className="text-xs text-slate-400">No risks captured yet</p></FormSection>}
                    {/* Section 9: Ethics, IP & Compliance */}
                    {detail.projectType === 'Research' && (()=>{
                      const COMPLIANCE_QUESTIONS = ['Does this research involve human subjects?','Does this research involve hazardous materials?','Are there existing IP rights affected?','Has IP disclosure been completed?','Are there export-controlled technologies?','Does the project involve foreign collaboration?'];
                      const answers = sd.complianceAnswers || {};
                      const hasAnswers = Object.values(answers).some(v => v);
                      return <FormSection number={9} title="Ethics, IP & Compliance" icon={Shield}>
                        {hasAnswers ? <div className="space-y-1">{COMPLIANCE_QUESTIONS.map((q,i)=>{const a=answers[i];return a?(<div key={i} className="flex items-center justify-between px-3 py-1.5 bg-slate-50 rounded-lg"><span className="text-xs text-slate-700">{q}</span><span className={`text-xs font-semibold ${a==='Yes'?'text-amber-600':'text-emerald-600'}`}>{a}</span></div>):null;})}</div> : <p className="text-xs text-slate-400">No compliance answers captured yet</p>}
                      </FormSection>;
                    })()}
                    {/* Section 10: Key References */}
                    {sd.keyReferences && <FormSection number={10} title="Key References" icon={BookOpen}><p className="text-sm text-slate-700 whitespace-pre-wrap">{sd.keyReferences}</p></FormSection>}
                  </>);
                })()}
              </div>)}

              {/* Approval History — uses approvalEvents (RdApprovalEvent) */}
              {tab==='approval'&&(<div className="space-y-3"><p className="text-xs font-semibold text-slate-500 uppercase">Audit Trail</p>
                {(detail.approvalEvents||[]).length>0?(<div className="space-y-2">{(detail.approvalEvents||[]).map((evt,i)=>(<div key={i} className="flex items-start gap-3"><div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${evt.decision==='Approved'?'bg-emerald-500 text-white':evt.decision==='Declined'?'bg-red-500 text-white':evt.decision==='ReturnedForRevision'?'bg-amber-500 text-white':'bg-blue-500 text-white'}`}>{evt.decision==='Approved'?<CheckCircle2 size={14}/>:evt.decision==='Declined'?<XCircle size={14}/>:evt.decision==='ReturnedForRevision'?<RotateCcw size={14}/>:<Send size={14}/>}</div><div className="flex-1 bg-slate-50 rounded-xl p-3"><p className="text-xs font-semibold text-slate-800">{evt.stage||evt.decision||'Event'} — <span className={evt.decision==='Approved'?'text-emerald-600':evt.decision==='Declined'?'text-red-600':'text-amber-600'}>{evt.decision}</span></p><p className="text-[10px] text-slate-400">{evt.decidedByName||'—'} · {fmtDate(evt.decidedAt)}</p>{evt.comments&&<p className="text-xs text-slate-600 mt-1">{evt.comments}</p>}</div></div>))}</div>):(<div className="text-center py-8 text-slate-400 text-sm"><Shield size={32} className="mx-auto mb-2 text-slate-300"/>No approval events yet</div>)}
              </div>)}

              {/* Execution */}
              {tab==='execution'&&(<div className="space-y-4">
                <div className="flex items-center gap-2"><p className="text-xs font-semibold text-slate-500 uppercase mr-2">Year:</p>{Array.from({length:detail.totalYears||1},(_,i)=>(<button key={i} onClick={()=>setActiveYear(i+1)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${activeYear===i+1?'bg-[#c8102e] text-white':'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>Year {i+1}</button>))}</div>
                <div className="bg-slate-50 rounded-xl p-6 text-center"><Zap size={32} className="mx-auto text-slate-300 mb-2"/><p className="text-sm text-slate-500">Year {activeYear} Execution Plan</p><p className="text-[10px] text-slate-400">Unlocks after project approval</p></div>
              </div>)}

              {/* Milestones — m.assignedTo aliased from m.responsiblePerson by API */}
              {tab==='milestones'&&(<div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase">Milestones ({(detail.milestones||[]).length})</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Edit individual milestones; the progress slider on each row auto-completes the milestone at 100%.</p>
                  </div>
                  <button onClick={()=>{setMsForm({...emptyMilestone,yearNumber:activeYear});setModal('milestone');}} className={btnP+' !py-1.5 !text-[10px]'}><Plus size={12}/>Add</button>
                </div>
                {(detail.milestones||[]).sort((a,b)=>(a.sortOrder||0)-(b.sortOrder||0)).map(ms=>{
                  const isDone = ms.status==='Completed';
                  const prog = ms.progressPercentage ?? (isDone ? 100 : 0);
                  const progColor = prog >= 100 ? '#10b981' : prog >= 50 ? '#3b82f6' : prog > 0 ? '#f59e0b' : '#cbd5e1';
                  return (
                    <div key={ms.id} className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        {isDone ? <CheckCircle2 size={18} className="text-emerald-500 mt-0.5 shrink-0"/>
                          : ms.status==='InProgress' ? <Clock size={18} className="text-blue-500 mt-0.5 shrink-0"/>
                          : ms.status==='Delayed' ? <AlertTriangle size={18} className="text-amber-500 mt-0.5 shrink-0"/>
                          : <Circle size={18} className="text-slate-300 mt-0.5 shrink-0"/>}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-sm font-semibold ${isDone?'text-slate-400 line-through':'text-slate-800'}`}>{ms.title}</span>
                            <span className="font-mono text-[10px] text-slate-400 bg-white px-1.5 py-0.5 rounded" title="Weight contribution to overall project progress">{ms.weightPercentage}% weight</span>
                            {ms.yearNumber && <span className="text-[10px] text-slate-400 bg-white px-1.5 py-0.5 rounded">Yr {ms.yearNumber}</span>}
                          </div>
                          {ms.description && <p className="text-xs text-slate-500 mt-0.5">{ms.description}</p>}
                          <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400 flex-wrap">
                            <span className="inline-flex items-center gap-1"><Calendar size={10}/>{fmtDate(ms.dueDate)}</span>
                            <span className="inline-flex items-center gap-1"><User size={10}/>{ms.responsiblePerson||ms.assignedTo||'—'}</span>
                            {ms.completedDate && <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 size={10}/>Completed {fmtDate(ms.completedDate)}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={()=>openMilestoneEdit(ms)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500 hover:text-blue-700" title="Edit milestone"><Edit3 size={13}/></button>
                          <button onClick={()=>deleteMilestone(ms.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600" title="Delete"><Trash2 size={13}/></button>
                        </div>
                      </div>

                      {/* V3.3 — Progress slider + Mark Complete button */}
                      <div className="mt-3 ml-7">
                        <div className="flex items-center gap-3">
                          <input
                            type="range"
                            min="0" max="100" step="5"
                            value={prog}
                            onChange={(e)=>updateMilestoneProgress(ms, e.target.value)}
                            className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
                            style={{background:`linear-gradient(to right, ${progColor} 0%, ${progColor} ${prog}%, #e2e8f0 ${prog}%, #e2e8f0 100%)`}}
                          />
                          <span className="text-xs font-bold w-10 text-right" style={{color: progColor}}>{prog}%</span>
                          {!isDone && (
                            <button onClick={()=>updateMilestoneProgress(ms, 100)}
                                    className="px-2.5 py-1 text-[10px] font-semibold rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 inline-flex items-center gap-1">
                              <CheckCircle2 size={11}/>Mark Complete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(detail.milestones||[]).length===0&&<div className="text-center text-slate-400 text-sm py-8"><Flag size={32} className="mx-auto mb-2 text-slate-300"/>No milestones — add milestones to track progress</div>}
              </div>)}

              {/* ═══ RESOURCES TAB (Phase A) ═══ */}
              {tab==='resources'&&(<div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase">Resources</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Role rates are managed in Admin Setup. Labour cost = hours × rate snapshot.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-white" value={resourceYear||''} onChange={e=>setResourceYear(e.target.value?parseInt(e.target.value):null)}>
                      <option value="">All years</option>
                      {(detail.years||[]).sort((a,b)=>a.yearNumber-b.yearNumber).map(y=>(
                        <option key={y.id} value={y.yearNumber}>Year {y.yearNumber}{y.financialYear?` (FY ${y.financialYear})`:''}</option>
                      ))}
                    </select>
                    <button onClick={()=>openResourceAdd(resourceYear||activeYear||1)} className={btnP+' !py-1.5 !text-[10px]'}><Plus size={12}/>Add Resource</button>
                  </div>
                </div>

                {roleRates.length===0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0"/>
                    <div>
                      <p className="text-xs font-bold text-amber-700">No active role rates</p>
                      <p className="text-[10px] text-amber-600">Set up role rates in Admin Setup → Role Rates before adding resources.</p>
                    </div>
                  </div>
                )}

                {(detail.resources||[]).length===0 && roleRates.length>0 && (
                  <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                    <Users size={28} className="mx-auto mb-2 text-slate-300"/>
                    <p className="text-sm text-slate-500">No resources assigned yet.</p>
                    <p className="text-[10px] text-slate-400 mt-1">Assign resources to track planned vs actual labour cost per year.</p>
                  </div>
                )}

                {/* Grouped by year */}
                {(detail.years||[]).sort((a,b)=>a.yearNumber-b.yearNumber).filter(y=>!resourceYear||y.yearNumber===resourceYear).map(year => {
                  const rows = (detail.resources||[]).filter(r => r.yearNumber === year.yearNumber && !r.isDeleted);
                  if (rows.length === 0 && resourceYear !== year.yearNumber) return null;
                  const locked = year.status === 'Active' || year.status === 'Closed' || year.status === 'ClosedEarly';
                  const plannedTotal = rows.reduce((s,r)=>s+((r.plannedHours||0)*(r.hourlyRateSnapshot||0)),0);
                  const actualTotal = rows.reduce((s,r)=>s+((r.actualHours||0)*(r.hourlyRateSnapshot||0)),0);
                  const unplannedCount = rows.filter(r=>r.isUnplanned).length;
                  return (
                    <div key={year.id} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
                      <div className="px-4 py-2.5 bg-slate-50 border-b flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-slate-700">Year {year.yearNumber}{year.financialYear?` (FY ${year.financialYear})`:''}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${yearStatusBadge(year.status)}`}>{year.status}</span>
                          {locked && <span className="flex items-center gap-1 text-[10px] text-slate-500"><Lock size={10}/>Planned locked</span>}
                          {unplannedCount>0 && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold uppercase text-[9px]">{unplannedCount} unplanned</span>}
                        </div>
                        <div className="text-[10px] text-slate-500 font-mono">{rows.length} resource{rows.length===1?'':'s'} · Plan {fmtR(plannedTotal)} · Actual {fmtR(actualTotal)}</div>
                      </div>
                      {rows.length === 0 ? (
                        <div className="p-5 text-center text-xs text-slate-400">No resources for this year yet.</div>
                      ) : (
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-100">
                              <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase">Role</th>
                              <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase">Assignee</th>
                              <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Rate</th>
                              <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Plan Hrs</th>
                              <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Act Hrs</th>
                              <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Plan Cost</th>
                              <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Act Cost</th>
                              <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase w-20">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map(r => {
                              const plannedCost = (r.plannedHours||0)*(r.hourlyRateSnapshot||0);
                              const actualCost = (r.actualHours||0)*(r.hourlyRateSnapshot||0);
                              return (
                                <tr key={r.id} className="border-t border-slate-50 hover:bg-slate-50/30">
                                  <td className="px-3 py-2">
                                    <span className="font-mono text-[10px] font-bold text-[#c8102e]">{r.roleRate?.code||'—'}</span>
                                    {r.roleRate?.name && <span className="ml-1.5 text-slate-600">{r.roleRate.name}</span>}
                                    {r.isUnplanned && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold uppercase text-[9px]">Unplanned</span>}
                                  </td>
                                  <td className="px-3 py-2 text-slate-700">{r.assigneeName}</td>
                                  <td className="px-3 py-2 text-right font-mono text-slate-500">{fmtR(r.hourlyRateSnapshot)}</td>
                                  <td className="px-3 py-2 text-right font-mono text-slate-700">{r.plannedHours||0}</td>
                                  <td className="px-3 py-2 text-right font-mono text-slate-700">{r.actualHours||0}</td>
                                  <td className="px-3 py-2 text-right font-mono font-bold text-slate-800">{fmtR(plannedCost)}</td>
                                  <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{fmtR(actualCost)}</td>
                                  <td className="px-3 py-2 text-right whitespace-nowrap">
                                    <button onClick={()=>openResourceEdit(r)} title={locked?'Edit actuals only':'Edit'} className="text-slate-400 hover:text-[#c8102e] p-1"><Edit3 size={12}/></button>
                                    <button onClick={()=>deleteResource(r.id)} title="Delete" className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={12}/></button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>)}

              {/* ═══ PROJECT COSTS TAB (Phase B) ═══ */}
              {tab==='costs'&&(<div className="space-y-4">
                {/* Sub-tab toggle + year filter */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
                    <button onClick={()=>setCostsSubTab('labour')} className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${costsSubTab==='labour'?'bg-white text-[#c8102e] shadow-sm':'text-slate-500 hover:text-slate-700'}`}>
                      <Users size={12} className="inline mr-1"/>Labour Cost
                    </button>
                    <button onClick={()=>setCostsSubTab('running')} className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${costsSubTab==='running'?'bg-white text-[#c8102e] shadow-sm':'text-slate-500 hover:text-slate-700'}`}>
                      <DollarSign size={12} className="inline mr-1"/>Running Cost
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <select className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-white" value={costsYear||''} onChange={e=>setCostsYear(e.target.value?parseInt(e.target.value):null)}>
                      <option value="">All years</option>
                      {(detail.years||[]).sort((a,b)=>a.yearNumber-b.yearNumber).map(y=>(
                        <option key={y.id} value={y.yearNumber}>Year {y.yearNumber}{y.financialYear?` (FY ${y.financialYear})`:''}</option>
                      ))}
                    </select>
                    {costsSubTab==='running' && (
                      <button onClick={()=>openRunningAdd(costsYear||activeYear||1)} className={btnP+' !py-1.5 !text-[10px]'}><Plus size={12}/>Add Running Cost</button>
                    )}
                    {costsSubTab==='labour' && (
                      <button onClick={()=>setTab('resources')} className="px-3 py-1.5 text-[10px] font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">Manage in Resources tab →</button>
                    )}
                  </div>
                </div>

                {/* ── LABOUR SUB-TAB (read-only rollup from Resources) ── */}
                {costsSubTab==='labour' && (<>
                  <p className="text-[10px] text-slate-400">Labour cost is derived from Resources (role × hours × rate). Edit resources in the Resources tab.</p>

                  {/* Totals */}
                  {(()=>{
                    const rs = (detail.resources||[]).filter(r => !r.isDeleted && (!costsYear || r.yearNumber === costsYear));
                    const plannedLabour = rs.reduce((s,r)=>s+((r.plannedHours||0)*(r.hourlyRateSnapshot||0)),0);
                    const actualLabour = rs.reduce((s,r)=>s+((r.actualHours||0)*(r.hourlyRateSnapshot||0)),0);
                    const variance = plannedLabour - actualLabour;
                    return (
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-slate-50 rounded-xl p-3 text-center"><p className="text-[10px] text-slate-500 uppercase font-semibold">Planned Labour</p><p className="rd-overview-metric-value">{fmtR(plannedLabour)}</p></div>
                        <div className="bg-emerald-50 rounded-xl p-3 text-center"><p className="text-[10px] text-emerald-600 uppercase font-semibold">Actual Labour</p><p className="font-black text-lg text-emerald-800">{fmtR(actualLabour)}</p></div>
                        <div className="bg-blue-50 rounded-xl p-3 text-center"><p className="text-[10px] text-blue-600 uppercase font-semibold">Variance</p><p className={`font-black text-lg ${variance>=0?'text-blue-800':'text-red-700'}`}>{variance>=0?'+':''}{fmtR(variance)}</p></div>
                      </div>
                    );
                  })()}

                  {/* Grouped rollup by year → role */}
                  {(()=>{
                    const rs = (detail.resources||[]).filter(r => !r.isDeleted && (!costsYear || r.yearNumber === costsYear));
                    if (rs.length===0) {
                      return (<div className="bg-slate-50 rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                        <Users size={28} className="mx-auto mb-2 text-slate-300"/>
                        <p className="text-sm text-slate-500">No labour cost yet.</p>
                        <p className="text-[10px] text-slate-400 mt-1">Add resources in the Resources tab to start tracking labour cost.</p>
                      </div>);
                    }
                    const yearsShown = (detail.years||[]).sort((a,b)=>a.yearNumber-b.yearNumber).filter(y => !costsYear || y.yearNumber===costsYear);
                    return yearsShown.map(year => {
                      const forYear = rs.filter(r => r.yearNumber === year.yearNumber);
                      if (forYear.length===0) return null;
                      // Group by role code
                      const byRole = {};
                      forYear.forEach(r => {
                        const key = r.roleRate?.code || r.roleRateId;
                        if (!byRole[key]) byRole[key] = { role: r.roleRate, rows: [], plannedHrs: 0, actualHrs: 0, plannedCost: 0, actualCost: 0 };
                        byRole[key].rows.push(r);
                        byRole[key].plannedHrs += r.plannedHours || 0;
                        byRole[key].actualHrs += r.actualHours || 0;
                        byRole[key].plannedCost += (r.plannedHours||0)*(r.hourlyRateSnapshot||0);
                        byRole[key].actualCost += (r.actualHours||0)*(r.hourlyRateSnapshot||0);
                      });
                      return (
                        <div key={year.id} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
                          <div className="px-4 py-2.5 bg-slate-50 border-b flex items-center gap-2">
                            <span className="font-bold text-sm text-slate-700">Year {year.yearNumber}{year.financialYear?` (FY ${year.financialYear})`:''}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${yearStatusBadge(year.status)}`}>{year.status}</span>
                          </div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-slate-50/50 border-b border-slate-100">
                                <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase">Role</th>
                                <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">People</th>
                                <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Plan Hrs</th>
                                <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Act Hrs</th>
                                <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Plan Cost</th>
                                <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Act Cost</th>
                                <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Variance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(byRole).map(([key, g]) => (
                                <tr key={key} className="border-t border-slate-50">
                                  <td className="px-3 py-2">
                                    <span className="font-mono text-[10px] font-bold text-[#c8102e]">{g.role?.code||key}</span>
                                    {g.role?.name && <span className="ml-2 text-slate-600">{g.role.name}</span>}
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono text-slate-600">{g.rows.length}</td>
                                  <td className="px-3 py-2 text-right font-mono">{g.plannedHrs}</td>
                                  <td className="px-3 py-2 text-right font-mono">{g.actualHrs}</td>
                                  <td className="px-3 py-2 text-right font-mono font-bold text-slate-800">{fmtR(g.plannedCost)}</td>
                                  <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{fmtR(g.actualCost)}</td>
                                  <td className={`px-3 py-2 text-right font-mono font-bold ${(g.plannedCost-g.actualCost)>=0?'text-blue-700':'text-red-600'}`}>{fmtR(g.plannedCost-g.actualCost)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    });
                  })()}
                </>)}

                {/* ── RUNNING SUB-TAB (CRUD on RdBudgetLines, Personnel filtered out) ── */}
                {costsSubTab==='running' && (<>
                  {/* Totals */}
                  {(()=>{
                    const lines = (detail.budgetLines||[]).filter(b => !b.isDeleted && b.category !== 'Personnel' && (!costsYear || b.yearNumber === costsYear));
                    const planned = lines.reduce((s,b)=>s+(b.plannedAmount||b.budgetAmount||0),0);
                    const actual = lines.reduce((s,b)=>s+(b.actualAmount||0),0);
                    const variance = planned - actual;
                    return (
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-slate-50 rounded-xl p-3 text-center"><p className="text-[10px] text-slate-500 uppercase font-semibold">Planned Running</p><p className="font-black text-lg text-slate-800">{fmtR(planned)}</p></div>
                        <div className="bg-emerald-50 rounded-xl p-3 text-center"><p className="text-[10px] text-emerald-600 uppercase font-semibold">Actual Running</p><p className="font-black text-lg text-emerald-800">{fmtR(actual)}</p></div>
                        <div className="bg-blue-50 rounded-xl p-3 text-center"><p className="text-[10px] text-blue-600 uppercase font-semibold">Variance</p><p className={`font-black text-lg ${variance>=0?'text-blue-800':'text-red-700'}`}>{variance>=0?'+':''}{fmtR(variance)}</p></div>
                      </div>
                    );
                  })()}

                  {/* Empty state */}
                  {(detail.budgetLines||[]).filter(b => !b.isDeleted && b.category !== 'Personnel').length===0 && (
                    <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                      <DollarSign size={28} className="mx-auto mb-2 text-slate-300"/>
                      <p className="text-sm text-slate-500">No running cost lines yet.</p>
                      <p className="text-[10px] text-slate-400 mt-1">Travel, consumables, materials, subcontracting — add them here as they're planned or incurred.</p>
                    </div>
                  )}

                  {/* Grouped by year */}
                  {(detail.years||[]).sort((a,b)=>a.yearNumber-b.yearNumber).filter(y=>!costsYear||y.yearNumber===costsYear).map(year => {
                    const rows = (detail.budgetLines||[]).filter(b => !b.isDeleted && b.category !== 'Personnel' && b.yearNumber === year.yearNumber);
                    if (rows.length === 0 && costsYear !== year.yearNumber) return null;
                    const planned = rows.reduce((s,b)=>s+(b.plannedAmount||b.budgetAmount||0),0);
                    const actual = rows.reduce((s,b)=>s+(b.actualAmount||0),0);
                    return (
                      <div key={year.id} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
                        <div className="px-4 py-2.5 bg-slate-50 border-b flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-sm text-slate-700">Year {year.yearNumber}{year.financialYear?` (FY ${year.financialYear})`:''}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${yearStatusBadge(year.status)}`}>{year.status}</span>
                          </div>
                          <div className="text-[10px] text-slate-500 font-mono">{rows.length} line{rows.length===1?'':'s'} · Plan {fmtR(planned)} · Actual {fmtR(actual)}</div>
                        </div>
                        {rows.length === 0 ? (
                          <div className="p-5 text-center text-xs text-slate-400">No running cost lines for this year yet. <button onClick={()=>openRunningAdd(year.yearNumber)} className="text-[#c8102e] hover:underline">Add one</button></div>
                        ) : (
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-slate-50/50 border-b border-slate-100">
                                <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase">Category</th>
                                <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase">Description</th>
                                <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase">Date</th>
                                <th className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase">Ref</th>
                                <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Planned</th>
                                <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Actual</th>
                                <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase">Var</th>
                                <th className="px-3 py-2 text-right text-[9px] font-bold text-slate-500 uppercase w-20">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map(bl => {
                                const bAmt = bl.plannedAmount||bl.budgetAmount||0;
                                const aAmt = bl.actualAmount||0;
                                return (
                                  <tr key={bl.id} className="border-t border-slate-50 hover:bg-slate-50/30">
                                    <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold">{rcLabel(bl)}</span></td>
                                    <td className="px-3 py-2 text-slate-700">{bl.description||'—'}</td>
                                    <td className="px-3 py-2 text-slate-500 text-[11px]">{fmtDate(bl.incurredDate)}</td>
                                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{bl.reference||'—'}</td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtR(bAmt)}</td>
                                    <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{fmtR(aAmt)}</td>
                                    <td className={`px-3 py-2 text-right font-mono font-bold ${(bAmt-aAmt)>=0?'text-blue-700':'text-red-600'}`}>{fmtR(bAmt-aAmt)}</td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                      <button onClick={()=>openRunningEdit(bl)} title="Edit" className="text-slate-400 hover:text-[#c8102e] p-1"><Edit3 size={12}/></button>
                                      <button onClick={()=>deleteRunningLine(bl.id)} title="Delete" className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={12}/></button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    );
                  })}

                  {/* Legacy Personnel budget lines notice (only shown if they exist) */}
                  {(detail.budgetLines||[]).filter(b => !b.isDeleted && b.category === 'Personnel').length > 0 && (
                    <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                      <Info size={14} className="text-amber-600 mt-0.5 flex-shrink-0"/>
                      <div>
                        <p className="text-xs font-bold text-amber-700">Legacy Personnel budget lines hidden</p>
                        <p className="text-[10px] text-amber-600">This project has {(detail.budgetLines||[]).filter(b => !b.isDeleted && b.category === 'Personnel').length} budget line(s) with Category = Personnel. Labour is now tracked in the Resources tab. Those legacy rows stay in the database but no longer appear here.</p>
                      </div>
                    </div>
                  )}
                </>)}
              </div>)}

              {/* ═══ ATTACHMENTS TAB (Phase C) ═══ */}
              {tab==='attachments'&&(<div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
                    <button onClick={()=>setAttachmentsSubTab('documents')} className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${attachmentsSubTab==='documents'?'bg-white text-[#c8102e] shadow-sm':'text-slate-500 hover:text-slate-700'}`}>
                      <FileText size={12} className="inline mr-1"/>Documents <span className="ml-1 text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{(detail.documents||[]).length}</span>
                    </button>
                    <button onClick={()=>setAttachmentsSubTab('deliverables')} className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${attachmentsSubTab==='deliverables'?'bg-white text-[#c8102e] shadow-sm':'text-slate-500 hover:text-slate-700'}`}>
                      <FileCheck size={12} className="inline mr-1"/>Deliverables <span className="ml-1 text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{(detail.deliverables||[]).length}</span>
                    </button>
                  </div>
                  <button onClick={()=>attachmentsSubTab==='documents'?openDocumentAdd():openDeliverableAdd()} className={btnP+' !py-1.5 !text-[10px]'}><Plus size={12}/>{attachmentsSubTab==='documents'?'Upload Document':'Add Deliverable'}</button>
                </div>

                {/* DOCUMENTS sub-tab */}
                {attachmentsSubTab==='documents' && (<>
                  {(detail.documents||[]).length===0 && (
                    <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                      <FileText size={28} className="mx-auto mb-2 text-slate-300"/>
                      <p className="text-sm text-slate-500">No documents uploaded yet.</p>
                      <p className="text-[10px] text-slate-400 mt-1">Proposals, POs, NDAs, contracts, specs, CVs — anything relevant to the project.</p>
                    </div>
                  )}
                  {(detail.documents||[]).length>0 && (()=>{
                    // Group by documentType
                    const grouped = {};
                    (detail.documents||[]).forEach(d => { const t = d.documentType || 'Other'; (grouped[t] = grouped[t] || []).push(d); });
                    return Object.entries(grouped).map(([type, docs]) => (
                      <div key={type} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
                        <div className="px-4 py-2.5 bg-slate-50 border-b flex items-center justify-between">
                          <span className="font-bold text-sm text-slate-700">{type}</span>
                          <span className="text-[10px] text-slate-500 font-mono">{docs.length} file{docs.length===1?'':'s'}</span>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {docs.map(d => (
                            <div key={d.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50/30">
                              <FileText size={16} className="text-slate-400 flex-shrink-0"/>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-semibold text-slate-800 truncate">{d.fileName || d.description || 'Untitled'}</span>
                                  <span className="font-mono text-[10px] text-slate-500">v{d.version || '1.0'}</span>
                                  {d.isConfidential && <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold uppercase text-[9px] flex items-center gap-0.5"><Lock size={9}/>Confidential</span>}
                                  {d.isSuperseded && <span className="px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600 font-bold uppercase text-[9px]">Superseded</span>}
                                </div>
                                {d.description && <p className="text-[11px] text-slate-500 mt-0.5">{d.description}</p>}
                                <p className="text-[10px] text-slate-400 mt-0.5">Uploaded {fmtDate(d.createdAt)} by {d.createdBy||'—'}</p>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={()=>downloadDocument(d)} title="Download" className="text-slate-400 hover:text-[#c8102e] p-1"><Download size={14}/></button>
                                <button onClick={()=>openDocumentEdit(d)} title="Edit" className="text-slate-400 hover:text-[#c8102e] p-1"><Edit3 size={12}/></button>
                                <button onClick={()=>deleteDocument(d.id)} title="Delete" className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={12}/></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </>)}

                {/* DELIVERABLES sub-tab */}
                {attachmentsSubTab==='deliverables' && (<>
                  {(detail.deliverables||[]).length===0 && (
                    <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                      <FileCheck size={28} className="mx-auto mb-2 text-slate-300"/>
                      <p className="text-sm text-slate-500">No deliverables yet.</p>
                      <p className="text-[10px] text-slate-400 mt-1">Reports, data packs, presentations — work products shared with the client.</p>
                    </div>
                  )}
                  {(detail.deliverables||[]).map(d => {
                    const ms = (detail.milestones||[]).find(m => m.id === d.milestoneId);
                    return (
                      <div key={d.id} className="bg-white rounded-2xl shadow-sm border p-4">
                        <div className="flex items-start justify-between flex-wrap gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <FileCheck size={14} className="text-slate-400"/>
                              <h4 className="font-bold text-sm text-slate-800">{d.title}</h4>
                              <span className="font-mono text-[10px] text-slate-500">v{d.version || '1.0'}</span>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${extStatusBadge(d.status)}`}>{d.status || 'Draft'}</span>
                              {d.isForClient && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold uppercase text-[9px]">Client-Facing</span>}
                            </div>
                            {ms && <p className="text-[11px] text-slate-500 mt-1">Linked to milestone: <span className="font-semibold">{ms.title}</span></p>}
                            {d.description && <p className="text-[11px] text-slate-600 mt-1">{d.description}</p>}
                            <p className="text-[10px] text-slate-400 mt-1">Type: {d.deliverableType||'—'} · Created {fmtDate(d.createdAt)}</p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0 flex-wrap">
                            <button onClick={()=>downloadDeliverable(d)} title="Download" className="text-slate-400 hover:text-[#c8102e] p-1"><Download size={14}/></button>
                            <button onClick={()=>openDeliverableEdit(d)} title="Edit" className="text-slate-400 hover:text-[#c8102e] p-1"><Edit3 size={12}/></button>
                            {/* Quick status transitions */}
                            {d.status==='Draft' && <button onClick={()=>updateDeliverableStatus(d,'Submitted')} className="px-2 py-1 text-[10px] font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600">Submit</button>}
                            {d.status==='Submitted' && d.isForClient && <button onClick={()=>updateDeliverableStatus(d,'SubmittedToClient')} className="px-2 py-1 text-[10px] font-semibold rounded-lg bg-blue-500 text-white hover:bg-blue-600">Send to Client</button>}
                            {d.status==='SubmittedToClient' && <>
                              <button onClick={()=>updateDeliverableStatus(d,'ClientAccepted')} className="px-2 py-1 text-[10px] font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">Accepted</button>
                              <button onClick={()=>updateDeliverableStatus(d,'ClientRejected')} className="px-2 py-1 text-[10px] font-semibold rounded-lg bg-red-500 text-white hover:bg-red-600">Rejected</button>
                            </>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>)}
              </div>)}

              {/* ═══ EXTENSIONS TAB (Phase C) ═══ */}
              {tab==='extensions'&&(<div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase">Scope Extensions</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Formal request for additional scope, budget, or time. Each extension gets a reference (SCE-…) and requires approval before it changes the project plan.</p>
                  </div>
                  <button onClick={openExtensionAdd} className={btnP+' !py-1.5 !text-[10px]'}><Plus size={12}/>Request Extension</button>
                </div>

                {(detail.scopeExtensions||[]).length===0 && (
                  <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                    <ArrowRight size={28} className="mx-auto mb-2 text-slate-300"/>
                    <p className="text-sm text-slate-500">No extensions requested yet.</p>
                    <p className="text-[10px] text-slate-400 mt-1">Raise one when scope, budget, or duration needs to change mid-project.</p>
                  </div>
                )}

                {(detail.scopeExtensions||[]).map(ext => (
                  <div key={ext.id} className={`bg-white rounded-2xl shadow-sm border border-l-4 p-4 ${ext.status==='Approved'?'border-l-emerald-500':ext.status==='Declined'?'border-l-red-500':ext.status==='Submitted'||ext.status==='UnderReview'?'border-l-amber-500':'border-l-slate-300'}`}>
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[10px] font-bold text-[#c8102e] bg-red-50 px-1.5 py-0.5 rounded">{ext.extensionRefNo}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${extStatusBadge(ext.status)}`}>{ext.status}</span>
                          {ext.reason && <span className="text-[10px] text-slate-500">Reason: <span className="font-semibold">{ext.reason === 'Other' ? (ext.reasonOther || 'Other') : ext.reason}</span></span>}
                        </div>
                        <p className="text-sm text-slate-700 mt-2">{ext.description||'—'}</p>
                        <div className="flex items-center gap-4 flex-wrap mt-2 text-[11px]">
                          {ext.additionalBudget > 0 && <span className="text-slate-600"><DollarSign size={10} className="inline"/> Additional Budget: <span className="font-bold text-slate-800">{fmtR(ext.additionalBudget)}</span></span>}
                          {ext.revisedEndDate && <span className="text-slate-600"><Calendar size={10} className="inline"/> Revised End: <span className="font-bold text-slate-800">{fmtDate(ext.revisedEndDate)}</span></span>}
                        </div>
                        {ext.impactOnExistingWork && <p className="text-[11px] text-slate-500 mt-2 italic">Impact: {ext.impactOnExistingWork}</p>}
                        <p className="text-[10px] text-slate-400 mt-2">Created {fmtDate(ext.createdAt)} by {ext.createdBy||'—'}
                          {ext.submittedAt && <> · Submitted {fmtDate(ext.submittedAt)}</>}
                          {ext.approvedAt && <> · Approved {fmtDate(ext.approvedAt)}</>}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 flex-wrap">
                        {ext.status==='Draft' && <>
                          <button onClick={()=>openExtensionEdit(ext)} title="Edit" className="text-slate-400 hover:text-[#c8102e] p-1"><Edit3 size={14}/></button>
                          <button onClick={()=>submitExtension(ext.id)} className="px-2.5 py-1 text-[10px] font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 flex items-center gap-1"><Send size={10}/>Submit</button>
                          <button onClick={()=>deleteExtension(ext.id)} title="Delete" className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={12}/></button>
                        </>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>)}

              {/* ═══ BILLING TAB (Phase C — Commercial / Contract only) ═══ */}
              {tab==='billing'&&(<div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase">Billing Milestones</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Invoice schedule. Amounts are VAT-exclusive. Invoice references are entered manually from the accounting system.</p>
                  </div>
                  <button onClick={openBillingAdd} className={btnP+' !py-1.5 !text-[10px]'}><Plus size={12}/>Add Billing Milestone</button>
                </div>

                {/* Totals */}
                {(detail.billingMilestones||[]).length>0 && (()=>{
                  const total = (detail.billingMilestones||[]).reduce((s,b)=>s+(b.amount||0),0);
                  const invoiced = (detail.billingMilestones||[]).filter(b=>b.invoiceStatus==='Invoiced'||b.invoiceStatus==='Paid').reduce((s,b)=>s+(b.amount||0),0);
                  const paid = (detail.billingMilestones||[]).filter(b=>b.invoiceStatus==='Paid').reduce((s,b)=>s+(b.amount||0),0);
                  const outstanding = invoiced - paid;
                  return (
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-slate-50 rounded-xl p-3 text-center"><p className="text-[10px] text-slate-500 uppercase font-semibold">Total Scheduled</p><p className="font-black text-lg text-slate-800">{fmtR(total)}</p></div>
                      <div className="bg-amber-50 rounded-xl p-3 text-center"><p className="text-[10px] text-amber-600 uppercase font-semibold">Invoiced</p><p className="font-black text-lg text-amber-800">{fmtR(invoiced)}</p></div>
                      <div className="bg-emerald-50 rounded-xl p-3 text-center"><p className="text-[10px] text-emerald-600 uppercase font-semibold">Paid</p><p className="font-black text-lg text-emerald-800">{fmtR(paid)}</p></div>
                      <div className="bg-blue-50 rounded-xl p-3 text-center"><p className="text-[10px] text-blue-600 uppercase font-semibold">Outstanding</p><p className="font-black text-lg text-blue-800">{fmtR(outstanding)}</p></div>
                    </div>
                  );
                })()}

                {(detail.billingMilestones||[]).length===0 && (
                  <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                    <Award size={28} className="mx-auto mb-2 text-slate-300"/>
                    <p className="text-sm text-slate-500">No billing milestones yet.</p>
                    <p className="text-[10px] text-slate-400 mt-1">Set up the invoice schedule — mobilisation, progress, final — with or without delivery milestone links.</p>
                  </div>
                )}

                {(detail.billingMilestones||[]).sort((a,b)=>a.sequenceNumber-b.sequenceNumber).map(b => {
                  const linkedMs = (detail.milestones||[]).find(m => m.id === b.milestoneId);
                  return (
                    <div key={b.id} className={`bg-white rounded-2xl shadow-sm border border-l-4 p-4 ${
                      b.invoiceStatus==='Paid'?'border-l-emerald-500':
                      b.invoiceStatus==='Invoiced'?'border-l-amber-500':
                      b.invoiceStatus==='WrittenOff'?'border-l-red-500':
                      b.invoiceStatus==='Cancelled'?'border-l-slate-400':'border-l-slate-300'
                    }`}>
                      <div className="flex items-start justify-between flex-wrap gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">#{b.sequenceNumber}</span>
                            <h4 className="font-bold text-sm text-slate-800">{b.title}</h4>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${billingStatusBadge(b.invoiceStatus)}`}>{b.invoiceStatus}</span>
                          </div>
                          {b.description && <p className="text-[11px] text-slate-600 mt-1">{b.description}</p>}
                          {linkedMs && <p className="text-[11px] text-slate-500 mt-1"><Flag size={10} className="inline"/> Linked to milestone: <span className="font-semibold">{linkedMs.title}</span></p>}
                          <div className="flex items-center gap-4 flex-wrap mt-2 text-[11px]">
                            <span className="text-slate-600">Amount: <span className="font-bold text-slate-800 font-mono">{fmtR(b.amount)}</span>{b.percentage != null && <span className="text-slate-400"> ({b.percentage}%)</span>}</span>
                            {b.targetDate && <span className="text-slate-600"><Calendar size={10} className="inline"/> Target: <span className="font-semibold">{fmtDate(b.targetDate)}</span></span>}
                            {b.invoiceReference && <span className="text-slate-600">Ref: <span className="font-mono font-bold text-slate-800">{b.invoiceReference}</span></span>}
                            {b.invoicedDate && <span className="text-amber-700">Invoiced: <span className="font-semibold">{fmtDate(b.invoicedDate)}</span></span>}
                            {b.paidDate && <span className="text-emerald-700">Paid: <span className="font-semibold">{fmtDate(b.paidDate)}</span></span>}
                          </div>
                          {b.notes && <p className="text-[11px] text-slate-500 italic mt-2 whitespace-pre-wrap">{b.notes}</p>}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 flex-wrap">
                          {b.invoiceStatus==='Draft' && <>
                            <button onClick={()=>openBillingEdit(b)} title="Edit" className="text-slate-400 hover:text-[#c8102e] p-1"><Edit3 size={14}/></button>
                            <button onClick={()=>openMarkInvoiced(b)} className="px-2.5 py-1 text-[10px] font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 flex items-center gap-1"><Send size={10}/>Mark Invoiced</button>
                            <button onClick={()=>writeOffBilling(b.id, true)} title="Cancel" className="text-slate-400 hover:text-slate-600 p-1"><X size={12}/></button>
                            <button onClick={()=>deleteBilling(b.id)} title="Delete" className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={12}/></button>
                          </>}
                          {b.invoiceStatus==='Invoiced' && <>
                            <button onClick={()=>openBillingEdit(b)} title="Edit (amount locked)" className="text-slate-400 hover:text-[#c8102e] p-1"><Edit3 size={14}/></button>
                            <button onClick={()=>openMarkPaid(b)} className="px-2.5 py-1 text-[10px] font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1"><CheckCircle2 size={10}/>Mark Paid</button>
                            <button onClick={()=>writeOffBilling(b.id, false)} title="Write Off" className="text-slate-400 hover:text-red-600 p-1"><XCircle size={12}/></button>
                          </>}
                          {b.invoiceStatus==='Paid' && <button onClick={()=>openBillingEdit(b)} title="View / edit metadata" className="text-slate-400 hover:text-[#c8102e] p-1"><Edit3 size={14}/></button>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>)}

              {/* Strategy Linkage */}
              {tab==='linkage'&&(<div className="space-y-5">
                <div className="bg-red-50/50 rounded-xl p-4"><h4 className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1.5"><Target size={14} className="text-[#c8102e]"/>Strategic Objectives ({(detail.strategicObjectives||[]).length})</h4>
                  {(detail.strategicObjectives||[]).length>0?<div className="space-y-1.5">{(detail.strategicObjectives||[]).map(x=>(<div key={x.strategicObjectiveId} className="flex items-center gap-2 bg-white rounded-lg p-2"><span className="font-mono text-[10px] font-bold text-[#c8102e] bg-red-50 px-1.5 py-0.5 rounded">{x.strategicObjective?.reference}</span><span className="text-xs text-slate-700 flex-1">{x.strategicObjective?.title}</span></div>))}</div>:<p className="text-xs text-slate-400">No SOs linked — edit project to add</p>}</div>
                <div className="bg-purple-50/50 rounded-xl p-4"><h4 className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1.5"><GitBranch size={14} className="text-purple-600"/>Programmes ({(detail.programmes||[]).length})</h4>
                  {(detail.programmes||[]).length>0?<div className="space-y-1.5">{(detail.programmes||[]).map(x=>(<div key={x.initiativeId} className="flex items-center gap-2 bg-white rounded-lg p-2"><span className="font-mono text-[10px] font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">{x.initiative?.reference}</span><span className="text-xs text-slate-700 flex-1">{x.initiative?.title}</span></div>))}</div>:<p className="text-xs text-slate-400">No programmes linked</p>}</div>
                <div className="bg-emerald-50/50 rounded-xl p-4"><h4 className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1.5"><GaugeCircle size={14} className="text-emerald-600"/>KPIs ({(detail.kpiLinks||[]).length})</h4>
                  {(detail.kpiLinks||[]).length>0?<div className="space-y-1.5">{(detail.kpiLinks||[]).map(x=>(<div key={x.kpiDefinitionId} className="flex items-center gap-2 bg-white rounded-lg p-2"><span className="font-mono text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">{x.kpiDefinition?.reference||'KPI'}</span><span className="text-xs text-slate-700 flex-1">{x.kpiDefinition?.name}</span></div>))}</div>:<p className="text-xs text-slate-400">No KPIs linked — edit project to add</p>}</div>
                <div className="bg-blue-50/50 rounded-xl p-4"><h4 className="text-xs font-bold text-slate-500 uppercase mb-2"><Layers size={14} className="text-blue-600 inline mr-1.5"/>Division</h4><p className="text-sm text-slate-700">{detail.division?.name||'Not assigned'}</p></div>
              </div>)}

              {/* ═══ V3.3: GRANTS TAB ═══ */}
              {tab==='grants'&&(<div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase">Grants Linked to this Project ({projectGrants.length})</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Grants are managed in the <a href="/grants" className="text-[#c8102e] hover:underline">Grant Management</a> module. Link existing grants here to attribute funding to this project.</p>
                  </div>
                  <button onClick={openGrantLink} className={btnP+' !py-1.5 !text-[10px]'}><Link2 size={12}/>Link a Grant</button>
                </div>

                {projectGrants.length === 0 ? (
                  <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                    <Award size={36} className="text-slate-300 mx-auto mb-2"/>
                    <p className="text-sm text-slate-500 font-semibold">No grants linked to this project yet</p>
                    <p className="text-[10px] text-slate-400 mt-1">Click <b>Link a Grant</b> above to associate one or more existing grants from the register.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {projectGrants.map(link => {
                      const g = link.grant;
                      const statusColor = {
                        Applied:   'bg-blue-100 text-blue-700',
                        Awarded:   'bg-violet-100 text-violet-700',
                        Active:    'bg-emerald-100 text-emerald-700',
                        Reporting: 'bg-amber-100 text-amber-700',
                        Closed:    'bg-slate-100 text-slate-600',
                        Rejected:  'bg-red-100 text-red-700'
                      }[g.status] || 'bg-slate-100 text-slate-600';
                      const ccy = g.currency || 'ZAR';
                      const fmtMoney = (v) => v != null ? `${ccy==='ZAR'?'R':ccy+' '}${Number(v).toLocaleString('en-ZA', {maximumFractionDigits:0})}` : '—';
                      return (
                        <div key={link.linkId} className="bg-white border border-slate-200 rounded-2xl p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="font-mono text-[10px] font-bold text-[#8f1024] bg-[#fff3f5] px-2 py-0.5 rounded">{g.referenceNo}</span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColor}`}>{g.status}</span>
                              </div>
                              <p className="text-sm font-semibold text-slate-800">{g.title}</p>
                              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500 flex-wrap">
                                <span className="inline-flex items-center gap-1"><Building2 size={10}/>{g.funder}{g.programme?` · ${g.programme}`:''}</span>
                                <span className="inline-flex items-center gap-1"><DollarSign size={10}/>Total: {fmtMoney(g.totalAwarded)}</span>
                                {link.allocatedAmount != null && <span className="inline-flex items-center gap-1 text-emerald-700"><DollarSign size={10}/>Allocated: {fmtMoney(link.allocatedAmount)}</span>}
                                {link.allocatedPercentage != null && <span className="text-slate-600">({link.allocatedPercentage}%)</span>}
                                {g.startDate && <span className="inline-flex items-center gap-1"><Calendar size={10}/>{fmtDate(g.startDate)} → {fmtDate(g.endDate)}</span>}
                              </div>
                              {link.notes && <p className="text-[11px] italic text-slate-500 mt-1.5">"{link.notes}"</p>}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <a href="/grants" className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500 hover:text-blue-700" title="Open in Grant Management"><ExternalLink size={13}/></a>
                              <button onClick={()=>unlinkGrantFromProject(g.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600" title="Unlink grant"><X size={13}/></button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>)}
            </div>
          </div>):(<div className="bg-white rounded-2xl shadow-sm border p-12 text-center text-slate-400"><FlaskConical size={40} className="mx-auto mb-3 text-slate-300"/><p className="text-sm">Select a project</p></div>)}</div>
        </div>
      </>)}

      {/* ═══ MODALS ═══ */}

      {/* New / Edit Project */}
      <Modal open={modal==='project'} onClose={()=>{setModal(null);setFormStep(0);}} title={editingId?`Edit ${form.projectType} Project`:'New R&D Project'} extraWide>
        {formStep===0&&!editingId?(
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Select the type of R&D project to create.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {TYPES.map(type=>{const Icon=TYPE_ICONS[type];const desc={Research:'Parliamentary Grant funded research. Internal + external committee approval.',Commercial:'Client-funded project under a purchase order. Revenue-generating.',Contract:'Government or SOE contracted research. Public sector requirements.'};return(
                <button key={type} onClick={()=>{setForm(f=>({...f,projectType:type}));setFormStep(1);}} className="text-left p-5 rounded-2xl border-2 border-slate-200 hover:border-[#c8102e] hover:shadow-lg transition-all group">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${TYPE_COLORS[type]}`}><Icon size={24}/></div>
                  <h3 className="font-bold text-slate-800 mb-1 group-hover:text-[#c8102e] transition-colors">{type}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{desc[type]}</p>
                </button>
              );})}
            </div>
          </div>
        ):(
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${TYPE_COLORS[form.projectType]} inline-flex items-center gap-1.5`}>{React.createElement(TYPE_ICONS[form.projectType],{size:14})}{form.projectType} Project</span>
              {!editingId&&<button onClick={()=>setFormStep(0)} className="text-xs text-slate-400 hover:text-slate-600 underline">Change type</button>}
              <span className="ml-auto text-[10px] text-emerald-500 font-semibold flex items-center gap-1"><CheckCircle2 size={10}/>Draft auto-saved</span>
            </div>
            <div className="space-y-2">
              {formSections.map(s=>(<FormSection key={s.num} number={s.num} title={s.title} icon={s.icon} required={s.required} defaultOpen={s.num===1} complete={s.num===1&&form.title&&form.principalInvestigator}>{renderFormSection(s.num)}</FormSection>))}
            </div>
            {/* Scoring — saved into section data, not RdProject fields */}
            <div className="bg-slate-50 rounded-xl p-4"><p className="text-xs font-semibold text-slate-500 uppercase mb-3">Impact & Scoring (0–10)</p><div className="grid grid-cols-3 gap-3"><div><label className={label}>Impact</label><input className={input} type="number" min="0" max="10" step="0.5" value={form.impactScore} onChange={u('impactScore')}/></div><div><label className={label}>Feasibility</label><input className={input} type="number" min="0" max="10" step="0.5" value={form.feasibilityScore} onChange={u('feasibilityScore')}/></div><div><label className={label}>Alignment</label><input className={input} type="number" min="0" max="10" step="0.5" value={form.alignmentScore} onChange={u('alignmentScore')}/></div></div></div>
            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <p className="text-[10px] text-slate-400">{isResearch?'Follows Mintek Research Grant Funding Motivation Form (FORM RFA-001).':'Follows SPEC-RD-003: Commercial R&D Projects.'}</p>
              <div className="flex gap-2">
                <button onClick={()=>{setModal(null);setFormStep(0);}} className={btnS}>Cancel</button>
                <button onClick={saveProject} disabled={saving} className={btnP}>{saving?'Saving...':editingId?'Update Project':'Create Project'}</button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Milestone Modal — V3.3: handles BOTH add and edit modes via msForm.id */}
      <Modal open={modal==='milestone'} onClose={()=>{setModal(null); setMsForm(emptyMilestone);}} title={msForm.id ? 'Edit Milestone' : 'Add Milestone'}>
        <div className="space-y-3">
          <div><label className={label}>Title *</label><input className={input} value={msForm.title} onChange={e=>setMsForm(f=>({...f,title:e.target.value}))}/></div>
          <div><label className={label}>Description</label><textarea className={input+' h-12'} value={msForm.description} onChange={e=>setMsForm(f=>({...f,description:e.target.value}))}/></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={label}>Start Date</label><input className={input} type="date" value={msForm.startDate||''} onChange={e=>setMsForm(f=>({...f,startDate:e.target.value}))}/></div>
            <div><label className={label}>Due Date</label><input className={input} type="date" value={msForm.dueDate} onChange={e=>setMsForm(f=>({...f,dueDate:e.target.value}))}/></div>
            <div><label className={label}>Year</label><input className={input} type="number" min="1" value={msForm.yearNumber} onChange={e=>setMsForm(f=>({...f,yearNumber:e.target.value}))}/></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Weight % <span className="text-slate-400 normal-case font-normal">(contribution to project)</span></label><input className={input} type="number" min="0" max="100" value={msForm.weightPercentage} onChange={e=>setMsForm(f=>({...f,weightPercentage:e.target.value}))}/></div>
            <div><label className={label}>Status</label>
              <select className={input} value={msForm.status||'NotStarted'} onChange={e=>setMsForm(f=>({...f,status:e.target.value}))}>
                <option value="NotStarted">Not Started</option>
                <option value="InProgress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Delayed">Delayed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          {/* V3.3 — Progress % field with inline slider */}
          <div>
            <label className={label}>Progress % <span className="text-slate-400 normal-case font-normal">(0–100; sets Completed at 100%)</span></label>
            <div className="flex items-center gap-3">
              <input type="range" min="0" max="100" step="5" value={msForm.progressPercentage||0}
                     onChange={e=>setMsForm(f=>({...f,progressPercentage:e.target.value}))}
                     className="flex-1 h-1.5 cursor-pointer"/>
              <input type="number" min="0" max="100" className={input+' max-w-[80px] text-right'}
                     value={msForm.progressPercentage||0}
                     onChange={e=>setMsForm(f=>({...f,progressPercentage:e.target.value}))}/>
            </div>
          </div>
          <div><label className={label}>Responsible Person</label><input className={input} value={msForm.responsiblePerson} onChange={e=>setMsForm(f=>({...f,responsiblePerson:e.target.value}))}/></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={()=>{setModal(null); setMsForm(emptyMilestone);}} className={btnS}>Cancel</button>
          <button onClick={saveMilestone} disabled={saving} className={btnP}>{saving?'Saving...':(msForm.id?'Save Changes':'Add')}</button>
        </div>
      </Modal>

      {/* V3.3 — Grant Link Modal (used from the Grants tab) */}
      <Modal open={grantLinkModal} onClose={()=>setGrantLinkModal(false)} title="Link a Grant to this Project">
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Pick a grant from the register. To create a new grant, use the <a href="/grants" className="text-[#c8102e] hover:underline">Grant Management</a> page first.</p>
          <div>
            <label className={label}>Grant *</label>
            <select className={input} value={grantLinkForm.grantId} onChange={e=>setGrantLinkForm(f=>({...f,grantId:e.target.value}))}>
              <option value="">— Choose a grant —</option>
              {allGrants
                .filter(g => !projectGrants.some(l => l.grant.id === g.id))
                .map(g => (
                  <option key={g.id} value={g.id}>
                    {g.referenceNo} — {g.title} ({g.funder} · {g.status})
                  </option>
                ))}
            </select>
            {allGrants.length > 0 && allGrants.every(g => projectGrants.some(l => l.grant.id === g.id)) && (
              <p className="text-[11px] text-amber-700 mt-1">All grants are already linked to this project.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Allocated Amount</label>
              <input type="number" min="0" step="0.01" className={input} value={grantLinkForm.allocatedAmount} onChange={e=>setGrantLinkForm(f=>({...f,allocatedAmount:e.target.value}))} placeholder="Optional"/>
            </div>
            <div>
              <label className={label}>Allocated %</label>
              <input type="number" min="0" max="100" step="0.1" className={input} value={grantLinkForm.allocatedPercentage} onChange={e=>setGrantLinkForm(f=>({...f,allocatedPercentage:e.target.value}))} placeholder="Optional"/>
            </div>
          </div>
          <div>
            <label className={label}>Notes (optional)</label>
            <input className={input} value={grantLinkForm.notes} onChange={e=>setGrantLinkForm(f=>({...f,notes:e.target.value}))} placeholder="e.g. 'Funds Year 1 only'"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={()=>setGrantLinkModal(false)} className={btnS}>Cancel</button>
          <button onClick={linkGrantToProject} disabled={!grantLinkForm.grantId} className={btnP}><Link2 size={12}/>Link Grant</button>
        </div>
      </Modal>

      {/* Budget Modal — Phase B: add/edit Running Cost line */}
      <Modal open={modal==='budget'} onClose={()=>{setModal(null);setEditingBudgetId(null);}} title={editingBudgetId?'Edit Running Cost':'Add Running Cost'}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Year *</label>
              <select className={input} value={budgetForm.yearNumber} onChange={e=>setBudgetForm(f=>({...f,yearNumber:parseInt(e.target.value)||1}))}>
                {(detail?.years||[]).sort((a,b)=>a.yearNumber-b.yearNumber).map(y=>(
                  <option key={y.id} value={y.yearNumber}>Year {y.yearNumber}{y.financialYear?` (FY ${y.financialYear})`:''} — {y.status}</option>
                ))}
                {(!detail?.years || detail.years.length===0) && <option value={1}>Year 1</option>}
              </select>
            </div>
            <div>
              <label className={label}>Category *</label>
              <select className={input} value={budgetForm.category} onChange={e=>setBudgetForm(f=>({...f,category:e.target.value}))}>
                {BUDGET_CATEGORIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          {budgetForm.category === 'Other' && (
            <div>
              <label className={label}>Custom Category * <span className="text-slate-400 font-normal">(required when Other is selected)</span></label>
              <input className={input} value={budgetForm.customCategory} onChange={e=>setBudgetForm(f=>({...f,customCategory:e.target.value}))} placeholder="e.g. Laboratory certification fee"/>
            </div>
          )}
          <div><label className={label}>Description</label><input className={input} value={budgetForm.description} onChange={e=>setBudgetForm(f=>({...f,description:e.target.value}))} placeholder="What the cost covers"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Planned (R)</label><input className={input} type="number" step="0.01" value={budgetForm.budgetAmount} onChange={e=>setBudgetForm(f=>({...f,budgetAmount:e.target.value}))}/></div>
            <div><label className={label}>Actual (R)</label><input className={input} type="number" step="0.01" value={budgetForm.actualAmount} onChange={e=>setBudgetForm(f=>({...f,actualAmount:e.target.value}))}/></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Incurred Date</label><input className={input} type="date" value={budgetForm.incurredDate} onChange={e=>setBudgetForm(f=>({...f,incurredDate:e.target.value}))}/></div>
            <div><label className={label}>Reference</label><input className={input} value={budgetForm.reference} onChange={e=>setBudgetForm(f=>({...f,reference:e.target.value}))} placeholder="PO no., invoice no., GL code"/></div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><button onClick={()=>{setModal(null);setEditingBudgetId(null);}} className={btnS}>Cancel</button><button onClick={saveBudgetLine} disabled={saving} className={btnP}>{saving?'Saving...':editingBudgetId?'Save':'Add'}</button></div>
      </Modal>

      {/* Output Modal */}
      <Modal open={modal==='output'} onClose={()=>setModal(null)} title="Add Planned Output">
        <div className="space-y-3">
          <div><label className={label}>Output Type</label><select className={input} value={outputForm.outputType} onChange={e=>setOutputForm(f=>({...f,outputType:e.target.value}))}>{OUTPUT_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
          <div><label className={label}>Description *</label><textarea className={input+' h-16'} value={outputForm.description} onChange={e=>setOutputForm(f=>({...f,description:e.target.value}))} placeholder="Expected output..."/></div>
          <div><label className={label}>Target Date</label><input className={input} type="date" value={outputForm.targetDate} onChange={e=>setOutputForm(f=>({...f,targetDate:e.target.value}))}/></div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><button onClick={()=>setModal(null)} className={btnS}>Cancel</button><button onClick={saveOutput} disabled={saving} className={btnP}>{saving?'Saving...':'Add'}</button></div>
      </Modal>

      {/* ═══ PHASE A: Resource modal ═══ */}
      <Modal open={modal==='resource'} onClose={()=>setModal(null)} title={resourceForm.id?'Edit Resource':'Add Resource'}>
        {(()=>{
          const locked = resourceForm.id && isYearLocked(resourceForm.yearNumber);
          const unplannedWarning = !resourceForm.id && isYearLocked(resourceForm.yearNumber);
          return (
            <div className="space-y-3">
              {locked && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <Lock size={14} className="text-amber-600 mt-0.5 flex-shrink-0"/>
                  <div>
                    <p className="text-xs font-bold text-amber-700">Year is Active — planned fields locked</p>
                    <p className="text-[10px] text-amber-600">Only Actual Hours and Notes are editable. To change the role or planned hours, remove and re-add (will be flagged Unplanned).</p>
                  </div>
                </div>
              )}
              {unplannedWarning && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0"/>
                  <div>
                    <p className="text-xs font-bold text-amber-700">Adding resource to an Active year</p>
                    <p className="text-[10px] text-amber-600">This resource will be flagged "Unplanned" because Year {resourceForm.yearNumber} is already Active.</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Year *</label>
                  <select className={input+(locked?' bg-slate-100 cursor-not-allowed':'')} disabled={locked} value={resourceForm.yearNumber} onChange={e=>setResourceForm(f=>({...f,yearNumber:parseInt(e.target.value)||1}))}>
                    {(detail?.years||[]).sort((a,b)=>a.yearNumber-b.yearNumber).map(y=>(
                      <option key={y.id} value={y.yearNumber}>Year {y.yearNumber}{y.financialYear?` (FY ${y.financialYear})`:''} — {y.status}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={label}>Role *</label>
                  <select className={input+(locked?' bg-slate-100 cursor-not-allowed':'')} disabled={locked} value={resourceForm.roleRateId} onChange={e=>setResourceForm(f=>({...f,roleRateId:e.target.value}))}>
                    <option value="">— Select a role —</option>
                    {roleRates.map(rr => (
                      <option key={rr.id} value={rr.id}>{rr.code} · {rr.name} — {fmtR(rr.hourlyRate)}/hr</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className={label}>Assignee Name *</label>
                <input className={input+(locked?' bg-slate-100 cursor-not-allowed':'')} disabled={locked} value={resourceForm.assigneeName} onChange={e=>setResourceForm(f=>({...f,assigneeName:e.target.value}))} placeholder="e.g. John Smith"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Planned Hours</label>
                  <input type="number" step="0.5" min="0" className={input+(locked?' bg-slate-100 cursor-not-allowed':'')} disabled={locked} value={resourceForm.plannedHours} onChange={e=>setResourceForm(f=>({...f,plannedHours:e.target.value}))}/>
                </div>
                <div>
                  <label className={label}>Actual Hours</label>
                  <input type="number" step="0.5" min="0" className={input} value={resourceForm.actualHours} onChange={e=>setResourceForm(f=>({...f,actualHours:e.target.value}))}/>
                </div>
              </div>
              {resourceForm.roleRateId && (()=>{
                const rate = roleRates.find(r => r.id === resourceForm.roleRateId);
                if (!rate) return null;
                const plannedCost = (parseFloat(resourceForm.plannedHours)||0) * rate.hourlyRate;
                const actualCost = (parseFloat(resourceForm.actualHours)||0) * rate.hourlyRate;
                return (
                  <div className="bg-slate-50 rounded-xl p-3 grid grid-cols-3 gap-3">
                    <div><p className="text-[9px] text-slate-400 uppercase font-semibold">Rate (snapshot)</p><p className="font-mono font-bold text-slate-700 text-sm">{fmtR(rate.hourlyRate)}/hr</p></div>
                    <div><p className="text-[9px] text-slate-400 uppercase font-semibold">Planned Cost</p><p className="font-mono font-bold text-slate-800 text-sm">{fmtR(plannedCost)}</p></div>
                    <div><p className="text-[9px] text-slate-400 uppercase font-semibold">Actual Cost</p><p className="font-mono font-bold text-emerald-700 text-sm">{fmtR(actualCost)}</p></div>
                  </div>
                );
              })()}
              <div>
                <label className={label}>Notes</label>
                <input className={input} value={resourceForm.notes} onChange={e=>setResourceForm(f=>({...f,notes:e.target.value}))}/>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={()=>setModal(null)} className={btnS}>Cancel</button>
                <button onClick={saveResource} disabled={saving} className={btnP}>{saving?'Saving...':resourceForm.id?'Save':'Add Resource'}</button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* ═══ PHASE C MODALS ═══ */}

      {/* Document upload / edit */}
      <Modal open={modal==='document'} onClose={()=>setModal(null)} title={documentForm.id?'Edit Document':'Upload Document'}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Type *</label>
              <select className={input} value={documentForm.documentType} onChange={e=>setDocumentForm(f=>({...f,documentType:e.target.value}))}>
                {['ClientProposal','PurchaseOrder','LetterOfAward','SignedContract','NDA','EthicsApproval','ClientSpec','CV','BudgetJustification','IPDisclosure','CollaborationAgreement','LetterOfSupport','PreliminaryData','Other'].map(t=>(
                  <option key={t} value={t}>{t.replace(/([A-Z])/g,' $1').trim()}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>Version</label>
              <input type="number" min="1" step="1" className={input} value={documentForm.version} onChange={e=>setDocumentForm(f=>({...f,version:e.target.value}))} placeholder="1"/>
            </div>
          </div>
          <div>
            <label className={label}>Description</label>
            <input className={input} value={documentForm.description} onChange={e=>setDocumentForm(f=>({...f,description:e.target.value}))} placeholder="What's in this document"/>
          </div>
          {!documentForm.id && (
            <div>
              <label className={label}>File *</label>
              <input type="file" className={input} onChange={e=>onDocumentFileChange(e.target.files?.[0])}/>
              {documentForm.fileName && <p className="text-[10px] text-emerald-600 mt-1">✓ {documentForm.fileName} loaded</p>}
              <p className="text-[10px] text-slate-400 mt-0.5">Or paste a URL instead:</p>
              <input className={input+' mt-1'} value={documentForm.fileUrl} onChange={e=>setDocumentForm(f=>({...f,fileUrl:e.target.value}))} placeholder="https://..."/>
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={documentForm.isConfidential} onChange={e=>setDocumentForm(f=>({...f,isConfidential:e.target.checked}))}/>
            <span className="flex items-center gap-1"><Lock size={12}/> Mark as confidential</span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModal(null)} className={btnS}>Cancel</button>
            <button onClick={saveDocument} disabled={saving} className={btnP}>{saving?'Saving...':documentForm.id?'Save':'Upload'}</button>
          </div>
        </div>
      </Modal>

      {/* Deliverable add / edit */}
      <Modal open={modal==='deliverable'} onClose={()=>setModal(null)} title={deliverableForm.id?'Edit Deliverable':'Add Deliverable'}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Type</label>
              <select className={input} value={deliverableForm.deliverableType} onChange={e=>setDeliverableForm(f=>({...f,deliverableType:e.target.value}))}>
                {['Report','DataPack','Presentation','Specification','TestResults','SoftwareArtifact','Other'].map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Version</label>
              <input type="number" min="1" step="1" className={input} value={deliverableForm.version} onChange={e=>setDeliverableForm(f=>({...f,version:e.target.value}))} placeholder="1"/>
            </div>
          </div>
          <div>
            <label className={label}>Title *</label>
            <input className={input} value={deliverableForm.title} onChange={e=>setDeliverableForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Q1 Progress Report"/>
          </div>
          <div>
            <label className={label}>Linked Milestone (optional)</label>
            <select className={input} value={deliverableForm.milestoneId} onChange={e=>setDeliverableForm(f=>({...f,milestoneId:e.target.value}))}>
              <option value="">— Not linked —</option>
              {(detail?.milestones||[]).map(m=><option key={m.id} value={m.id}>{m.title}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Description</label>
            <textarea className={input+' h-16'} value={deliverableForm.description} onChange={e=>setDeliverableForm(f=>({...f,description:e.target.value}))}/>
          </div>
          <div>
            <label className={label}>File (optional at draft stage)</label>
            <input type="file" className={input} onChange={e=>onDeliverableFileChange(e.target.files?.[0])}/>
            {deliverableForm.fileName && <p className="text-[10px] text-emerald-600 mt-1">✓ {deliverableForm.fileName} loaded</p>}
            <p className="text-[10px] text-slate-400 mt-0.5">Or paste a URL:</p>
            <input className={input+' mt-1'} value={deliverableForm.fileUrl} onChange={e=>setDeliverableForm(f=>({...f,fileUrl:e.target.value}))} placeholder="https://..."/>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={deliverableForm.isForClient} onChange={e=>setDeliverableForm(f=>({...f,isForClient:e.target.checked}))}/>
            <span>Client-facing (will be shared with client once submitted)</span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModal(null)} className={btnS}>Cancel</button>
            <button onClick={saveDeliverable} disabled={saving} className={btnP}>{saving?'Saving...':deliverableForm.id?'Save':'Add'}</button>
          </div>
        </div>
      </Modal>

      {/* Scope Extension draft / edit */}
      <Modal open={modal==='extension'} onClose={()=>setModal(null)} title={extensionForm.id?'Edit Extension':'Request Scope Extension'}>
        <div className="space-y-3">
          {!extensionForm.id && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
              <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0"/>
              <div className="text-[11px] text-blue-700">
                Create as Draft first — you can edit freely. When ready, click <b>Submit</b> on the list to send it for approval. Once submitted it becomes read-only.
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Reason *</label>
              <select className={input} value={extensionForm.reason} onChange={e=>setExtensionForm(f=>({...f,reason:e.target.value}))}>
                <option value="ClientRequest">Client Request</option>
                <option value="UnforeseenComplexity">Unforeseen Complexity</option>
                <option value="AdditionalAnalysis">Additional Analysis</option>
                <option value="RegulatoryRequirement">Regulatory Requirement</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className={label}>Additional Budget (R)</label>
              <input type="number" step="0.01" min="0" className={input} value={extensionForm.additionalBudget} onChange={e=>setExtensionForm(f=>({...f,additionalBudget:e.target.value}))}/>
            </div>
          </div>
          {extensionForm.reason === 'Other' && (
            <div>
              <label className={label}>Specify Reason *</label>
              <input className={input} value={extensionForm.reasonOther} onChange={e=>setExtensionForm(f=>({...f,reasonOther:e.target.value}))} placeholder="e.g. Equipment breakdown"/>
            </div>
          )}
          <div>
            <label className={label}>Description *</label>
            <textarea className={input+' h-20'} value={extensionForm.description} onChange={e=>setExtensionForm(f=>({...f,description:e.target.value}))} placeholder="What additional work or time is needed and why"/>
          </div>
          <div>
            <label className={label}>Revised End Date</label>
            <input type="date" className={input} value={extensionForm.revisedEndDate} onChange={e=>setExtensionForm(f=>({...f,revisedEndDate:e.target.value}))}/>
          </div>
          <div>
            <label className={label}>Impact on Existing Work</label>
            <textarea className={input+' h-16'} value={extensionForm.impactOnExistingWork} onChange={e=>setExtensionForm(f=>({...f,impactOnExistingWork:e.target.value}))} placeholder="How this affects the current plan"/>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModal(null)} className={btnS}>Cancel</button>
            <button onClick={saveExtension} disabled={saving} className={btnP}>{saving?'Saving...':extensionForm.id?'Save':'Create Draft'}</button>
          </div>
        </div>
      </Modal>

      {/* Billing Milestone add / edit */}
      <Modal open={modal==='billing'} onClose={()=>setModal(null)} title={billingForm.id?'Edit Billing Milestone':'Add Billing Milestone'}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Sequence #</label>
              <input type="number" min="1" className={input} value={billingForm.sequenceNumber} onChange={e=>setBillingForm(f=>({...f,sequenceNumber:e.target.value}))}/>
            </div>
            <div>
              <label className={label}>Target Date</label>
              <input type="date" className={input} value={billingForm.targetDate} onChange={e=>setBillingForm(f=>({...f,targetDate:e.target.value}))}/>
            </div>
          </div>
          <div>
            <label className={label}>Title *</label>
            <input className={input} value={billingForm.title} onChange={e=>setBillingForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Mobilisation 20%"/>
          </div>
          <div>
            <label className={label}>Description</label>
            <input className={input} value={billingForm.description} onChange={e=>setBillingForm(f=>({...f,description:e.target.value}))}/>
          </div>
          <div>
            <label className={label}>Linked Delivery Milestone (optional)</label>
            <select className={input} value={billingForm.milestoneId} onChange={e=>setBillingForm(f=>({...f,milestoneId:e.target.value}))}>
              <option value="">— Standalone (mobilisation, retention, etc.) —</option>
              {(detail?.milestones||[]).map(m=><option key={m.id} value={m.id}>{m.title}</option>)}
            </select>
            <p className="text-[10px] text-slate-400 mt-0.5">Link to a delivery milestone when the invoice is triggered by completion of work. Leave blank for upfront mobilisation, retention releases, etc.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Percentage (%)</label>
              <input type="number" step="0.01" min="0" max="100" className={input} value={billingForm.percentage} onChange={e=>setBillingForm(f=>({...f,percentage:e.target.value}))} placeholder="e.g. 20"/>
            </div>
            <div>
              <label className={label}>Amount (R, VAT excl.) *{billingForm.id?<span className="text-slate-400 font-normal"> — locked once invoiced</span>:''}</label>
              <input type="number" step="0.01" min="0" className={input} value={billingForm.amount} onChange={e=>setBillingForm(f=>({...f,amount:e.target.value}))}/>
            </div>
          </div>
          <div>
            <label className={label}>Notes</label>
            <textarea className={input+' h-14'} value={billingForm.notes} onChange={e=>setBillingForm(f=>({...f,notes:e.target.value}))}/>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModal(null)} className={btnS}>Cancel</button>
            <button onClick={saveBilling} disabled={saving} className={btnP}>{saving?'Saving...':billingForm.id?'Save':'Add'}</button>
          </div>
        </div>
      </Modal>

      {/* Mark Invoiced */}
      <Modal open={modal==='invoice'} onClose={()=>setModal(null)} title="Mark as Invoiced">
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <Info size={14} className="text-amber-600 mt-0.5 flex-shrink-0"/>
            <p className="text-[11px] text-amber-700">Enter the invoice number from your accounting system. This records what Finance raised — SiloGate doesn't generate invoice numbers.</p>
          </div>
          <div>
            <label className={label}>Invoice Reference *</label>
            <input className={input} value={invoiceForm.invoiceReference} onChange={e=>setInvoiceForm(f=>({...f,invoiceReference:e.target.value}))} placeholder="e.g. INV-2026-0123"/>
          </div>
          <div>
            <label className={label}>Invoiced Date</label>
            <input type="date" className={input} value={invoiceForm.invoicedDate} onChange={e=>setInvoiceForm(f=>({...f,invoicedDate:e.target.value}))}/>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModal(null)} className={btnS}>Cancel</button>
            <button onClick={submitMarkInvoiced} disabled={saving} className={btnP}>{saving?'Saving...':'Confirm Invoiced'}</button>
          </div>
        </div>
      </Modal>

      {/* Mark Paid */}
      <Modal open={modal==='paid'} onClose={()=>setModal(null)} title="Mark as Paid">
        <div className="space-y-3">
          <div>
            <label className={label}>Paid Date</label>
            <input type="date" className={input} value={paidForm.paidDate} onChange={e=>setPaidForm(f=>({...f,paidDate:e.target.value}))}/>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModal(null)} className={btnS}>Cancel</button>
            <button onClick={submitMarkPaid} disabled={saving} className={btnP}>{saving?'Saving...':'Confirm Paid'}</button>
          </div>
        </div>
      </Modal>
    </div>
    </>
  );
}
